import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  faArrowRight,
  faMinus,
  faPencil,
  faPlus,
  faTrashCan,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// Common components
import AppBar from "../../../common/AppBar";
import Loader from "../../../common/Loader";
import Snackbar from "../../../common/Snackbar";
import { SaveModal } from "../../../common/Modals";
import {
  VersionHistoryPopup,
  getVersionHistoryAuditPreviewPath,
} from "../../../common/VersionHistoryPopup";
import { useVersionHistoryDataFromNodes } from "../../../common/VersionHistoryPopup/useVersionHistoryDataFromNodes.js";
import { Button, PrimaryButton, DangerButton } from "../../../common/Buttons";
import Popup from "../../../common/Popup/Popup";
import AppTable from "../../../common/AppTable/AppTable";
import FilterDropdown from "../../../common/FilterDropdown";
import ExportCsvBtn from "../../../common/AppSpecificConpnents/ExportCsvBtn";
import WebcamPopup from "./webcam/WebcamPopup.jsx";
import ShortDescriptionEditor from "../../../common/RichTextEditor/ShortDescriptionEditor";

// Redux actions
import { getRevAllNodes } from "../../../redux/procedureRevisionSlice";
import { createCanvas, updateCanvas } from "../../../redux/stepSlice";
import {
  fetchAllCanvasPageTemplates,
  getCanvasPageTemplateById,
  setLimit,
  setOffset,
} from "../../../redux/canvasTemplatesSlice";
import {
  setProcRevId,
  setStepTreeNodeId,
  getPartById,
} from "../../../redux/partSlice";
import { openPreview } from "../../../redux/previewSlice";

// Utilities
import { generateSHA256 } from "../../../util/generateSHA256";
import { getAuditTrailData } from "../../../util/getAuditTrailData";
import { useUnsavedChangesBlocker } from "../../../hooks/useUnsavedChangesBlocker";
import useDebounce from "../../../hooks/useDebounce";
import { v4 as uuidv4 } from "uuid";
import { setTaskReference } from "../../Tasks/Uitlities";
import {
  openCanvasDB,
  loadFromIndexedDB,
  saveToIndexedDB,
  getCurrentIndexedDBVersion,
} from "../../../util/indexedDBUtils";
import { DB_VERSION } from "../../../util/canvas/canvasDbConfig";
import { generateSnapshotFromPageDataHTML } from "../../../util/canvas/canvasHtmlSnapshot";
import { processCanvasJsonWithPartDetails } from "../../../util/canvas/processCanvasJsonWithPartDetails";
import { refreshCanvasPagesWithLatestParts } from "../../../util/canvas/refreshCanvasPartsWithSearch";
import {
  normalizeShapeType,
  isMarkerId,
  isClientTempLineLikeId,
} from "../../../util/shapeTypeMapper";
import { sanitizeId } from "../utils/textDataUtils";
import { convertTextToPayload } from "../utils/textProcessingUtils";
import { convertApiShapesToMyCanvasFormat } from "../utils/templateConversionUtils";
import { canvasTextsRepresentSameEntity } from "../utils/canvasTextEntityUtils";
import { buildPermissionContext } from "../../../util/permissions";
import { cleanBaselineHtml } from "../../../util/lexical";
import { pingTask } from "../../../util/apiService";
import { hasCanvasArraysChanged } from "../utils/arrayComparisonUtils";
import { hasCanvasArraysChangedSimple } from "../utils/arrayComparisonUtilsSimple";
import {
  detectCanvasChanges,
  parseCanvasStateFromSnapshot,
  countCanvasElements,
} from "../utils/changeDetectionUtils";
import { mergeCanvasPagesForReturn } from "../utils/navigationBoundsUtils";

import { BASE_URL } from "../../../config";

// Custom hooks
import useCanvasData from "../hooks/useCanvasData";
import useCanvasPersistence from "../hooks/useCanvasPersistence";

// Canvas preview: CanvasPageRenderer with processed data (current_canvas_data_json from procedure-nodes)
import CanvasPageRenderer from "./CanvasPageRenderer";
import "./CanvasForm.css";

/** Global "Canvas N" index — same traversal as ViewChildNode / ChildrenRenderer (`canva`). */
function computeGlobalMediaOrdinalForCanvas({
  allRevisionNodes,
  section_id_ref,
  step_id_ref,
  child_node_id,
}) {
  const nodeType = "canva";
  if (
    !child_node_id ||
    !Array.isArray(allRevisionNodes) ||
    !section_id_ref ||
    !step_id_ref
  ) {
    return null;
  }

  const compareSiblingOrders = (a, b) => {
    const intA = parseInt(a, 10);
    const intB = parseInt(b, 10);
    return intA - intB;
  };

  const sortedSections = [...allRevisionNodes].sort((a, b) => {
    const orderA = a.sibling_order || a.procedure_tree?.sibling_order;
    const orderB = b.sibling_order || b.procedure_tree?.sibling_order;
    const orderCompare = compareSiblingOrders(orderA, orderB);
    if (orderCompare !== 0) return orderCompare;
    const dateA = new Date(a.created_at);
    const dateB = new Date(b.created_at);
    return dateB - dateA;
  });

  let counter = 0;
  const matchesId = (c) =>
    c.tree_node_id === child_node_id ||
    c.detail?.canvas_tree_node_id === child_node_id ||
    c.detail?.attachment_tree_node_id === child_node_id;

  for (let i = 0; i < sortedSections.length; i++) {
    const section = sortedSections[i];
    for (let j = 0; j < (section.steps?.length || 0); j++) {
      const step = section.steps[j];
      const stepChildren =
        step.children?.filter((c) => c.type === nodeType) || [];

      const currentStepChild = stepChildren.find(matchesId);
      if (currentStepChild) {
        const sortedStepChildren = [...stepChildren].sort((a, b) => {
          const orderA = a.sibling_order || 0;
          const orderB = b.sibling_order || 0;
          return orderA - orderB;
        });
        const currentChildIndex = sortedStepChildren.findIndex(matchesId);
        counter += currentChildIndex;
        return counter + 1;
      }
      counter += stepChildren.length;

      for (let k = 0; k < (step.substeps?.length || 0); k++) {
        const substep = step.substeps[k];
        const substepChildren =
          substep.children?.filter((c) => c.type === nodeType) || [];
        const currentSubstepChild = substepChildren.find(matchesId);
        if (currentSubstepChild) {
          const sortedSubstepChildren = [...substepChildren].sort((a, b) => {
            const orderA = a.sibling_order || 0;
            const orderB = b.sibling_order || 0;
            return orderA - orderB;
          });
          const currentChildIndex =
            sortedSubstepChildren.findIndex(matchesId);
          counter += currentChildIndex;
          return counter + 1;
        }
        counter += substepChildren.length;
      }
    }
  }
  return counter + 1;
}

/**
 * Konva preview must use the real card content width. A viewport-based width (e.g. 50vw)
 * is often larger than the ~55% preview card; CanvasPageRenderer then clips the right edge
 * (overflow: hidden) when constrainToContainer is set.
 */
function CanvasPagePreviewMeasured({ canvasData }) {
  const wrapRef = useRef(null);
  const [measuredWidth, setMeasuredWidth] = useState(null);
  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const update = () => {
      const w = el.getBoundingClientRect().width + 30;
      setMeasuredWidth((prev) => {
        const next = Math.max(1, Math.floor(w));
        return prev === next ? prev : next;
      });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const fallbackWidth = Math.max(
    320,
    typeof window !== "undefined" ? window.innerWidth * 0.5 - 80 : 320
  );
  return (
    <div ref={wrapRef} style={{ width: "100%" }}>
      <CanvasPageRenderer
        canvasData={canvasData}
        width={measuredWidth ?? fallbackWidth}
        showSnapshots={false}
        constrainToContainer
      />
    </div>
  );
}

// Constants
export const columnNames = [
  {
    headName: "Canvas Page Name",
    field: "canvas_page_template_name",
    sort: true,
    filter: true,
    width: "150px",
    required: false,
  },
  {
    headName: "Description",
    field: "canvas_page_template_description",
    sort: true,
    filter: true,
    required: false,
  },
  {
    headName: "Category",
    field: "category_name",
    sort: true,
    filter: true,
    width: "200px",
  },
  { headName: "Action", field: "action", width: "100px" },
];

const reduxSlice = "canvasTemplates";

const resolveCanvasHeightValue = (...candidates) => {
  for (const c of candidates) {
    const n = typeof c === "number" ? c : parseInt(c, 10);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return 100;
};

/**
 * CanvasForm - Optimized canvas form component with complete UI
 *
 * This component provides the same UI as CanvasFormFull.js but with:
 * - Better code organization with custom hooks
 * - Improved error handling
 * - Cleaner component structure
 * - Reduced location state dependencies
 */
const CanvasForm = ({
  mode, // "add" or "edit"
  fromVersionHistory,
  versionHistoryData,
  data,
  // URL parameters
  partnumber,
  revision,
  partId,
  revisionId,
  section_id_ref,
  step_id_ref,
  substep_id_ref,
  node_id_ref,
  id,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const authUser = useSelector((state) => state.auth.user);
  const permissionCtx = buildPermissionContext(authUser);
  const canEdit = permissionCtx.permissions?.Edit === true;
  const location = useLocation();

  // Ping API call every minute to keep lock alive
  useEffect(() => {
    if (!revisionId || !authUser) return;

    const pingInterval = setInterval(async () => {
      try {
        await pingTask({
          task_id: revisionId,
          user_id: authUser.id,
        });
      } catch (error) {
        console.error("Ping failed in CanvasForm:", error);
      }
    }, 60000); // 60 seconds = 1 minute

    return () => {
      clearInterval(pingInterval);
    };
  }, [revisionId, authUser]);

  // Redux state
  const { loading: reduxLoading } = useSelector((state) => state.step);
  const { templates, template, loading, pagination } = useSelector(
    (state) => state.canvasTemplates,
  );

  // CRITICAL: Clear old canvas data IMMEDIATELY on mount for add mode ONLY
  // This MUST happen before any hooks run to prevent loading stale data
  // Track if we've done initial cleanup
  const hasInitializedRef = useRef(false);

  // CRITICAL: Enable the unsaved changes blocker when there are unsaved changes
  // This will show a confirmation dialog when user tries to navigate back
  // The blocker is enabled/disabled based on hasUnsavedChanges state
  // NOTE: This will be called after useCanvasPersistence initializes hasUnsavedChanges

  // CRITICAL: Check if coming from MyCanvas BEFORE clearing flags
  const isComingFromMyCanvasRef = useRef(
    localStorage.getItem("fromMyCanvas") === "true",
  );

  // CRITICAL: Store initial API pages data for simple change detection in edit mode
  // This will be populated when canvasData is first loaded
  const initialApiPagesRef = useRef(null);

  // Track if back button was clicked and we detected changes
  // This is used to trigger history.back() after state updates
  const backButtonClickedWithChangesRef = useRef(false);
  // CLEANUP: Clear stale flags on mount - but AFTER initialHasUnsavedChanges has been computed
  useEffect(() => {
    if (!hasInitializedRef.current) {
      // Clear old flags that were causing false positives
      // NOTE: initialHasUnsavedChanges has already been computed from these flags
      // so it's safe to clear them now
      localStorage.removeItem("fromMyCanvas");
      localStorage.removeItem("canvasChangesDetected");
      sessionStorage.removeItem(`fromMyCanvas_processed_${id}`);

      // Clear global tracking
      if (window.fromMyCanvasProcessed) {
        delete window.fromMyCanvasProcessed[id];
      }

      hasInitializedRef.current = true;
    }
  }, [id]);

  // Custom hooks for data management
  const {
    loading: dataLoading,
    canvasData,
    versionId,
    procedureId,
    lastSavedText,
    pageTitle,
    breadcrumbText,
    siblingOrder,
    allNodesData,
    sectionId,
    stepId,
    substepId,
    currentStatus,
    item_code_type,
  } = useCanvasData({
    mode,
    id,
    partnumber,
    revision,
    partId,
    revisionId,
    section_id_ref,
    step_id_ref,
    substep_id_ref,
    node_id_ref,
  });

  // Check if Canvas Index Number (pageTitle) and Tree Path (breadcrumbText) are loaded
  // breadcrumbText is the most reliable indicator that all calculations are complete
  // It includes the full tree path which requires all node data to be processed
  const isInitialDataLoaded =
    !dataLoading && pageTitle && breadcrumbText && breadcrumbText.length > 0;

  // SIMPLIFIED: Use location.state.hasChanges OR localStorage.fromMyCanvas as source of truth
  // NOTE: This is NOT memoized because we need to read localStorage on every render
  // to ensure we get the latest value when returning from MyCanvas
  let initialHasUnsavedChanges = false;

  // Priority 1: location.state.hasChanges (from navigate with state)
  if (location.state?.hasChanges === true) {
    initialHasUnsavedChanges = true;
  } else {
    // If React Router "back" doesn't preserve location.state, fall back to
    // the persistent localStorage flag set by `navigateBack()` from MyCanvas.
    // This should only be trusted for the specific "returned from MyCanvas" flow.
    const fromMyCanvas =
      localStorage.getItem("fromMyCanvas") === "true" ||
      localStorage.getItem("preventApiCall") === "true";
    if (fromMyCanvas) {
      const canvasId = localStorage.getItem("currentCanvasId") || id;
      const persistentFlagKeysToTry = [
        `canvasHasUnsavedChanges_${canvasId}`,
        `canvasHasUnsavedChanges_${id}`,
      ];

      let hasPersistentChanges = persistentFlagKeysToTry.some(
        (key) => localStorage.getItem(key) === "true"
      );

      // Fallback: sometimes `currentCanvasId` wasn't present when `navigateBack()`
      // created the persistent flag key (it can fall back to a page id).
      // In that case, scan for any true flag left behind.
      if (!hasPersistentChanges) {
        for (let i = 0; i < localStorage.length; i++) {
          const k = localStorage.key(i);
          if (!k || !k.startsWith("canvasHasUnsavedChanges_")) continue;
          if (localStorage.getItem(k) === "true") {
            hasPersistentChanges = true;
            break;
          }
        }
      }

      if (hasPersistentChanges) {
        initialHasUnsavedChanges = true;
      }
    }

    // If we still don't know, default to false and rely on later checks.

    // Priority 2: Check for newly added pages in localStorage (persists across navigation)
    // This ensures newly added pages are detected even after visiting MyCanvas
    let hasNewlyAddedPages = false;
    try {
      const savedPagesRaw = localStorage.getItem("canvasPages");
      if (savedPagesRaw && savedPagesRaw !== "undefined") {
        const savedPages = JSON.parse(savedPagesRaw);
        if (Array.isArray(savedPages)) {
          hasNewlyAddedPages = savedPages.some(
            (page) => page?.newlyAdded === true,
          );
        }
      }
    } catch (error) {
      console.error("Error checking for newly added pages:", error);
    }

    if (hasNewlyAddedPages) {
      initialHasUnsavedChanges = true;
    } else {
      // Default to false (no changes) - conservative approach
      // We will do proper comparison when back button is clicked
      initialHasUnsavedChanges = false;
    }
  }

  const {
    formData,
    setFormData,
    canvasPages,
    setCanvasPages,
    hasUnsavedChanges,
    setHasUnsavedChanges,
    saveForPreview,
    clearStoredData,
  } = useCanvasPersistence({
    mode,
    id,
    canvasData,
    initialHasUnsavedChanges, // Use the simplified flag from location.state
  });

  // Add mode: returning from MyCanvas must restore the full page list. Router state / localStorage
  // can still list only pages from an older visit; merge navigation state with current state.
  useEffect(() => {
    if (mode !== "add") return;
    if (!location.state?.fromMyCanvas) return;
    const incoming = location.state?.canvasPages;
    if (!Array.isArray(incoming) || incoming.length === 0) return;

    setCanvasPages((prev) => {
      const prevArr = Array.isArray(prev) ? prev : [];
      return mergeCanvasPagesForReturn(incoming, JSON.stringify(prevArr));
    });
  }, [mode, location.key, location.state?.fromMyCanvas]);

  // Undo-based dirty tracking for the Lexical short-description editor.
  // Must be declared BEFORE useUnsavedChangesBlocker which reads it.
  // CAN_UNDO_COMMAND fires `true` on real user edits and `false` when they undo
  // everything back, bypassing the fragile HTML-string comparison that caused
  // false-positive "unsaved changes" alerts on editor initialisation.
  const [isShortDescriptionDirty, setIsShortDescriptionDirty] = useState(false);
  const handleShortDescriptionDirtyChange = useCallback(
    (dirty) => setIsShortDescriptionDirty(dirty),
    []
  );

  // CRITICAL: Enable the unsaved changes blocker when there are unsaved changes
  // This will show a confirmation dialog when user tries to navigate back
  // The blocker is enabled/disabled based on hasUnsavedChanges state
  // NOTE: This is called AFTER useCanvasPersistence initializes hasUnsavedChanges.
  // isShortDescriptionDirty is the undo-based signal from the Lexical editor so
  // that the editor's own programmatic init does NOT produce a false positive.
  useUnsavedChangesBlocker(hasUnsavedChanges || isShortDescriptionDirty);

  // Effect to call history.back() after state updates when back button detected changes
  useEffect(() => {
    if (backButtonClickedWithChangesRef.current && hasUnsavedChanges) {
      // Don't reset the ref here - keep it true so subsequent back clicks also trigger the blocker
      window.history.back();
    } else {
    }
  }, [hasUnsavedChanges]);

  // Local state
  const [isLoading, setIsLoading] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState(false);

  // Popup: merged bulk-fetch + Redux fallback; Node History aligned with ViewChildNode.
  const canvasRouteChildId = canvasData?.tree_node_id || id || null;

  const canvasVersionHistoryMatchIds = useMemo(() => {
    const fromAttachments = Array.isArray(canvasData?.detail?.attachments)
      ? canvasData.detail.attachments.flatMap((a) =>
          [a?.attachment_tree_node_ref, a?.attachment_id].filter(Boolean),
        )
      : [];
    return [
      id,
      canvasData?.tree_node_id,
      canvasData?.detail?.procedure_tree_ref,
      canvasData?.attachment_id_ref,
      canvasData?.attachment_tree_node_id,
      canvasData?.detail?.attachment_id,
      canvasData?.detail?.attachment_tree_node_id,
      canvasData?.detail?.video_tree_node_id,
      canvasData?.detail?.rich_text_node_id,
      canvasData?.detail?.rich_text_tree_node_id,
      canvasData?.detail?.canvas_tree_node_id,
      canvasData?.detail?.canva_tree_node_id,
      canvasData?.detail?.form_tree_node_id,
      canvasData?.detail?.element_id,
      ...fromAttachments,
    ].filter(Boolean);
  }, [
    id,
    canvasData?.tree_node_id,
    canvasData?.detail?.procedure_tree_ref,
    canvasData?.attachment_id_ref,
    canvasData?.attachment_tree_node_id,
    canvasData?.detail?.attachment_id,
    canvasData?.detail?.attachment_tree_node_id,
    canvasData?.detail?.video_tree_node_id,
    canvasData?.detail?.rich_text_node_id,
    canvasData?.detail?.rich_text_tree_node_id,
    canvasData?.detail?.canvas_tree_node_id,
    canvasData?.detail?.canva_tree_node_id,
    canvasData?.detail?.form_tree_node_id,
    canvasData?.detail?.element_id,
    canvasData?.detail?.attachments,
  ]);

  const canvasMediaOrdinalForNodeHistory = useMemo(
    () =>
      computeGlobalMediaOrdinalForCanvas({
        allRevisionNodes: allNodesData,
        section_id_ref,
        step_id_ref,
        child_node_id: canvasRouteChildId,
      }),
    [
      allNodesData,
      section_id_ref,
      step_id_ref,
      canvasRouteChildId,
    ],
  );

  const {
    mergedVersionHistories,
    allVersionHistories,
    versionHistoryTableLoading,
  } = useVersionHistoryDataFromNodes({
    isOpen: showVersionHistory && mode === "edit",
    revisionId,
    partId,
    allRevisionNodes: allNodesData,
    fallbackVersionId:
      canvasData?.detail?.version_id_ref || canvasData?.version_id_ref || null,
    nodesReady:
      mode === "edit" &&
      !dataLoading &&
      Array.isArray(allNodesData) &&
      allNodesData.length > 0,
  });

  const versionHistories = mergedVersionHistories;
  const versionHistoryLoading = versionHistoryTableLoading;

  const [saveProgress, setSaveProgress] = useState({ step: "", percentage: 0 });
  const [shortDescription, setShortDescription] = useState(() => {
    const saved = localStorage.getItem("short_description");
    if (saved && saved !== "undefined") {
      // Process short description to handle both plain text and HTML content
      let processedSaved = saved;
      if (processedSaved && !processedSaved.includes('<div class="editor-shell">') && !processedSaved.includes('<div class=\\"editor-shell\\"')) {
        // If it's plain text (no editor-shell wrapper), convert it to HTML format
        processedSaved = `<p>${processedSaved}</p>`;
      }
      return cleanBaselineHtml(processedSaved);
    }
    return "";
  });
  const [comment, setComment] = useState("");
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [showWebcamPopup, setShowWebcamPopup] = useState(false);
  const [templateProcessing, setTemplateProcessing] = useState(false);
  const [pendingNewPageId, setPendingNewPageId] = useState(null);

  // Canvas node from procedure-nodes API (getRevAllNodes) so we have current_canvas_data_json for preview
  const [canvasNodeFromProcedureNodes, setCanvasNodeFromProcedureNodes] =
    useState(null);
  // Processed canvas pages (fetch + part details merge) so we can show CanvasPageRenderer for every page card
  const [processedCanvasPagesFromApi, setProcessedCanvasPagesFromApi] =
    useState(null);
  const [processedCanvasPagesLoading, setProcessedCanvasPagesLoading] =
    useState(false);
  const collectPartDetailsFromCtmData = useCallback((sourceData) => {
    if (!sourceData || typeof sourceData !== "object") return [];
    if (Array.isArray(sourceData.part_details)) return sourceData.part_details;
    const ctmData = Array.isArray(sourceData.ctm_data) ? sourceData.ctm_data : [];
    const collected = ctmData.flatMap((item) =>
      Array.isArray(item?.part_details) ? item.part_details : [],
    );
    const seenPartRefs = new Set();
    return collected.filter((part) => {
      const ref = part?.part_ref;
      if (!ref) return true;
      if (seenPartRefs.has(ref)) return false;
      seenPartRefs.add(ref);
      return true;
    });
  }, []);

  // CRITICAL: Add a minimum time threshold before allowing blocker to show
  // This prevents false positives from immediate navigation
  const [blockerReadyTime, setBlockerReadyTime] = useState(null);

  // Unsaved changes blocker - only activate after user interaction
  const [userHasInteracted, setUserHasInteracted] = useState(false);

  // Removed pages tracking (for edit mode)
  const [removedCanvasPages, setRemovedCanvasPages] = useState(() => {
    // Restore removedCanvasPages from localStorage on component mount
    try {
      const saved = localStorage.getItem("removedCanvasPages");
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      return [];
    }
  });

  // Persist removedCanvasPages whenever it changes
  useEffect(() => {
    if (mode === "edit") {
      localStorage.setItem(
        "removedCanvasPages",
        JSON.stringify(removedCanvasPages),
      );
    }
  }, [removedCanvasPages, mode]);

  // In edit mode, ensure we have current_canvas_data_json from procedure-nodes API for CanvasNode (latest part details)
  useEffect(() => {
    if (mode !== "edit" || !id || !partId || !revisionId) {
      setCanvasNodeFromProcedureNodes(null);
      return;
    }

    let cancelled = false;

    const fetchProcedureNodesAndSetCanvasNode = async () => {
      try {
        const payload = await dispatch(
          getRevAllNodes({ partId, revisionId }),
        ).unwrap();

        if (cancelled || !payload || !Array.isArray(payload)) return;

        const findCanvasById = (nodes, nodeId) => {
          for (const section of nodes) {
            if (section.tree_node_id === nodeId) return section;
            if (section.steps && Array.isArray(section.steps)) {
              for (const step of section.steps) {
                if (step.tree_node_id === nodeId) return step;
                if (step.children && Array.isArray(step.children)) {
                  for (const child of step.children) {
                    if (
                      (child.type === "canva" &&
                        (child.tree_node_id === nodeId ||
                          child.detail?.canvas_tree_node_id === nodeId)) ||
                      child.canvas_tree_node_id === nodeId
                    ) {
                      return child;
                    }
                  }
                }
                if (step.substeps && Array.isArray(step.substeps)) {
                  for (const substep of step.substeps) {
                    if (substep.children && Array.isArray(substep.children)) {
                      for (const child of substep.children) {
                        if (
                          child.type === "canva" &&
                          (child.tree_node_id === nodeId ||
                            child.detail?.canvas_tree_node_id === nodeId ||
                            child.canvas_tree_node_id === nodeId)
                        ) {
                          return child;
                        }
                      }
                    }
                  }
                }
              }
            }
          }
          return null;
        };

        const canvasNode = findCanvasById(payload, id);
        if (!cancelled && canvasNode) {
          const sourceData = canvasNode?.detail?.current_canvas_data_json;
          const extractedPartDetails = [
            ...collectPartDetailsFromCtmData(sourceData),
            ...collectPartDetailsFromCtmData(canvasNode?.detail),
          ];
          const normalizedSourceData =
            extractedPartDetails.length > 0
              ? {
                  ...(sourceData || {}),
                  part_details: extractedPartDetails,
                }
              : sourceData;

          setCanvasNodeFromProcedureNodes({
            ...canvasNode,
            detail: {
              ...(canvasNode.detail || {}),
              current_canvas_data_json: normalizedSourceData,
            },
          });
        }
      } catch (err) {
        if (!cancelled) {
          setCanvasNodeFromProcedureNodes(null);
        }
      }
    };

    fetchProcedureNodesAndSetCanvasNode();
    return () => {
      cancelled = true;
    };
  }, [mode, id, partId, revisionId, dispatch, collectPartDetailsFromCtmData]);

  // Fetch canvas JSON and process with current_canvas_data_json so every page card can show CanvasPageRenderer
  const canvasJsonUrl =
    canvasNodeFromProcedureNodes?.detail?.canvas_json_data_file ??
    canvasData?.detail?.canvas_json_data_file;
  useEffect(() => {
    if (!canvasJsonUrl) {
      setProcessedCanvasPagesFromApi(null);
      return;
    }

    const nodeForFetch = canvasNodeFromProcedureNodes || canvasData;
    let cancelled = false;

    const fetchAndProcess = async () => {
      setProcessedCanvasPagesLoading(true);
      try {
        const response = await fetch(canvasJsonUrl, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
          mode: "cors",
          credentials: "omit",
          cache: "no-cache",
        });
        if (!response.ok || cancelled) return;

        const data = await response.json();
        const sourceData = nodeForFetch?.detail?.current_canvas_data_json;
        const sourcePartDetails = [
          ...collectPartDetailsFromCtmData(sourceData),
          ...collectPartDetailsFromCtmData(nodeForFetch?.detail),
        ];
        const normalizedSourceData =
          sourcePartDetails.length > 0
            ? { ...(sourceData || {}), part_details: sourcePartDetails }
            : sourceData;

        let processed = processCanvasJsonWithPartDetails(data, normalizedSourceData);
        const sourcePartNumbers =
          sourcePartDetails.length > 0
            ? sourcePartDetails.map((part) => part?.part_number).filter(Boolean)
            : [];
        processed = await refreshCanvasPagesWithLatestParts(processed, {
          extraPartNumbers: sourcePartNumbers,
          dispatch,
        });
        if ((!processed || processed.length === 0) && data && !cancelled) {
          const rawPages = Array.isArray(data)
            ? data
            : data?.results?.pages || data?.pages || data?.data?.pages;
          if (Array.isArray(rawPages) && rawPages.length > 0) {
            processed = rawPages;
          }
        }
        if (!cancelled) setProcessedCanvasPagesFromApi(processed || null);
      } catch (err) {
        if (!cancelled) {
          setProcessedCanvasPagesFromApi(null);
        }
      } finally {
        if (!cancelled) setProcessedCanvasPagesLoading(false);
      }
    };

    fetchAndProcess();
    return () => {
      cancelled = true;
    };
  }, [canvasJsonUrl, canvasNodeFromProcedureNodes, canvasData, dispatch, collectPartDetailsFromCtmData]);

  // Pagination state
  const [limit, setLocalLimit] = useState(pagination?.limit || 10);
  const [offset, setLocalOffset] = useState(pagination?.offset || 0);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentApiQuery, setCurrentApiQuery] = useState("");
  const [index, setIndex] = useState(0);
  const [isSearching, setIsSearching] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  // Memoized search parameters
  const searchParams = useMemo(() => {
    const params = {
      limit,
      offset,
      searchQuery: currentApiQuery || debouncedSearchQuery || "",
      hasSearch: !!(currentApiQuery || debouncedSearchQuery),
    };
    return params;
  }, [limit, offset, currentApiQuery, debouncedSearchQuery]);

  // Consolidated API call function
  const fetchTemplates = useCallback(
    async (params) => {
      const requestKey = `${params.searchQuery}-${
        params.searchType || "all_columns"
      }-${params.limit}-${params.offset}`;

      if (isUpdatingRef.current) {
        return;
      }

      if (isUpdatingRef.current === requestKey) {
        return;
      }

      try {
        isUpdatingRef.current = requestKey;
        setIsSearching(true);
        await dispatch(fetchAllCanvasPageTemplates(params)).unwrap();
      } catch (error) {
        console.error("Error fetching templates:", error);
      } finally {
        isUpdatingRef.current = false;
        setIsSearching(false);
      }
    },
    [dispatch],
  );

  // useEffect for currentApiQuery changes (from handleFilterSort)
  useEffect(() => {
    if (isInitialLoad) {
      return;
    }

    if (!currentApiQuery) {
      return;
    }

    // Parse search and search_type from currentApiQuery
    const params = new URLSearchParams(currentApiQuery);
    const search = params.get("search") || "";
    const searchType = params.get("search_type") || "all_columns";

    const apiParams = {
      limit: pagination?.limit || 10,
      offset: 0,
      searchQuery: search,
      searchType: searchType,
    };

    dispatch(setOffset(0));
    setLocalOffset(0);

    const timeoutId = setTimeout(() => {
      fetchTemplates(apiParams);
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [
    currentApiQuery,
    isInitialLoad,
    pagination?.limit,
    dispatch,
    fetchTemplates,
  ]);

  // useEffect for search changes
  useEffect(() => {
    if (!searchParams.hasSearch) {
      return;
    }

    if (isInitialLoad) {
      return;
    }

    const currentLimit = pagination?.limit || 10;

    const apiParams = {
      limit: currentLimit,
      offset: 0,
      searchQuery: searchParams.searchQuery,
    };

    dispatch(setOffset(0));
    setLocalOffset(0);

    const timeoutId = setTimeout(() => {
      fetchTemplates(apiParams);
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [
    searchParams.hasSearch,
    searchParams.searchQuery,
    pagination?.limit,
    fetchTemplates,
    isInitialLoad,
    dispatch,
  ]);

  // useEffect for pagination changes
  useEffect(() => {
    if (isInitialLoad) {
      return;
    }

    if (pagination?.limit === undefined) {
      return;
    }

    // Parse search_type from currentApiQuery if it exists
    let searchType = "all_columns";
    let search = searchParams.searchQuery || "";

    if (currentApiQuery) {
      const params = new URLSearchParams(currentApiQuery);
      search = params.get("search") || "";
      searchType = params.get("search_type") || "all_columns";
    }

    const apiParams = {
      limit: pagination.limit,
      offset: pagination.offset || 0,
      searchQuery: search,
      searchType: searchType,
    };

    const timeoutId = setTimeout(() => {
      fetchTemplates(apiParams);
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [
    pagination?.limit,
    pagination?.offset,
    searchParams.searchQuery,
    currentApiQuery,
    fetchTemplates,
    isInitialLoad,
  ]);

  // Initial data load effect
  useEffect(() => {
    if (pagination?.limit === undefined) {
      return;
    }

    if (!isInitialLoad) {
      return;
    }

    const apiParams = {
      limit: pagination.limit,
      offset: pagination.offset || 0,
      searchQuery: "",
    };

    fetchTemplates(apiParams);
    setIsInitialLoad(false);
  }, [pagination, fetchTemplates, isInitialLoad]);

  // Sync localStorage to Redux on mount
  useEffect(() => {
    if (pagination?.limit === undefined) {
      const savedLimit = localStorage.getItem("canvasTemplates_limit");
      const savedOffset = localStorage.getItem("canvasTemplates_offset");

      if (savedLimit) {
        dispatch(setLimit(parseInt(savedLimit)));
      }
      if (savedOffset) {
        dispatch(setOffset(parseInt(savedOffset)));
      }
    }
  }, []);

  // Sync local state with Redux pagination state
  useEffect(() => {
    if (pagination?.limit !== undefined && pagination.limit !== limit) {
      setLocalLimit(pagination.limit);
    }
    if (pagination?.offset !== undefined && pagination.offset !== offset) {
      setLocalOffset(pagination.offset);
    }
  }, [pagination?.limit, pagination?.offset, limit, offset]);

  // Track if we've done initial hydration to prevent infinite loops
  const hasHydratedRef = useRef(false);
  // Track processed returns from MyCanvas to prevent duplicate processing
  const processedReturnRef = useRef(null);
  // Store original API state for each page (before any IndexedDB hydration)
  // This is used to detect changes by comparing IndexedDB against original API data
  const originalPageStatesRef = useRef(new Map());
  // Track if we've stored original state for pages (to prevent re-storing after hydration)
  const hasStoredOriginalStateRef = useRef(false);
  // State to track when hydration completes (triggers useEffect)
  const [hydrationComplete, setHydrationComplete] = useState(false);

  // SIMPLE: Store initial snapshots ONCE when pages first load - NEVER OVERWRITE
  // Compare snapshots to detect any changes
  // CRITICAL: Use sessionStorage to persist across component unmounts (when navigating to/from MyCanvas)
  const initialSnapshotsRef = useRef(new Map());
  const storedPageIdsRef = useRef(new Set());
  const hasCompletedInitialStorageRef = useRef(false);
  const snapshotsBeingLoadedRef = useRef(new Set()); // Track which pages are currently being loaded

  // Load initial snapshots from sessionStorage on mount
  useEffect(() => {
    try {
      const stored = sessionStorage.getItem("initialCanvasSnapshots");
      if (stored) {
        const parsed = JSON.parse(stored);

        // CRITICAL: Use the ref that was captured BEFORE flags were cleared
        // This ensures we correctly detect if we're coming from MyCanvas
        const isComingFromMyCanvas = isComingFromMyCanvasRef.current;

        if (mode === "edit" && !isComingFromMyCanvas) {
          sessionStorage.removeItem("initialCanvasSnapshots");
          // Clear removedCanvasPages state and localStorage to prevent false positives
          localStorage.removeItem("removedCanvasPages");
          setRemovedCanvasPages([]);
          return;
        }

        // CRITICAL: Normalize each snapshot to only include canvas element arrays
        // This prevents false positives from old snapshots with metadata fields
        const normalizedSnapshots = {};
        for (const [pageId, snapshot] of Object.entries(parsed)) {
          try {
            const snapshotData =
              typeof snapshot === "string" ? JSON.parse(snapshot) : snapshot;

            // If it's a JSON object (not a URL string), normalize it
            if (typeof snapshotData === "object" && snapshotData !== null) {
              const normalized = {
                texts: snapshotData.texts || [],
                rectangles: snapshotData.rectangles || [],
                circles: snapshotData.circles || [],
                arrows: snapshotData.arrows || [],
                lines: snapshotData.lines || [],
                doubleSidedArrows: snapshotData.doubleSidedArrows || [],
                scribbles: snapshotData.scribbles || [],
                images: snapshotData.images || [],
              };
              normalizedSnapshots[pageId] = JSON.stringify(normalized);
            } else {
              // Keep URL strings as-is
              normalizedSnapshots[pageId] = snapshot;
            }
          } catch (e) {
            // If parsing fails, keep the original
            normalizedSnapshots[pageId] = snapshot;
          }
        }

        initialSnapshotsRef.current = new Map(
          Object.entries(normalizedSnapshots),
        );
        storedPageIdsRef.current = new Set(Object.keys(normalizedSnapshots));
        hasCompletedInitialStorageRef.current =
          storedPageIdsRef.current.size > 0;
      }
    } catch (error) {
      console.error(
        "Error loading initial snapshots from sessionStorage:",
        error,
      );
    }
  }, [mode]); // Run when mode changes

  // Store initial snapshots ONCE when pages first load - NEVER OVERWRITE
  // Compare snapshots to detect any changes
  // CRITICAL: Use sessionStorage to persist across component unmounts (when navigating to/from MyCanvas)
  useEffect(() => {
    const isFreshEditLoadFromViewChildNode =
      mode === "edit" &&
      location.state?.forceFreshEditLoad === true &&
      !location.state?.pageId;

    // CRITICAL: Only store when pages are FIRST loaded, before any navigation
    // Store from page objects (original from API)

    if (
      mode === "edit" &&
      canvasPages.length > 0 &&
      !hasCompletedInitialStorageRef.current
    ) {
      // Check if we have incorrectly stored snapshots and clear them
      const currentCanvasPageIds = canvasPages
        .map((p) => p.canvas_page_id || p.id || p.pageId)
        .filter(Boolean);
      const storedKeys = Array.from(initialSnapshotsRef.current.keys());

      // Clear snapshots that don't match current canvas page IDs
      storedKeys.forEach((storedKey) => {
        if (!currentCanvasPageIds.includes(storedKey)) {
          initialSnapshotsRef.current.delete(storedKey);
          storedPageIdsRef.current.delete(storedKey);
        }
      });

      const pagesToStore = canvasPages.filter((page) => {
        // CRITICAL: Use canvas_page_id consistently for storage and retrieval
        const pageId = page.canvas_page_id || page.id || page.pageId;
        // Only store if pageId exists AND hasn't been stored yet AND not already in map
        const shouldStore =
          pageId &&
          !storedPageIdsRef.current.has(pageId) &&
          !initialSnapshotsRef.current.has(pageId);
        if (pageId && !shouldStore) {
        }
        return shouldStore;
      });

      // CRITICAL: Don't mark as complete here - wait for all async operations to finish
      // The completion will be handled by tracking when all pages actually have snapshots stored
      const checkAllSnapshotsStored = () => {
        const allPagesStored = canvasPages.every((page) => {
          // CRITICAL: Use canvas_page_id consistently for storage and retrieval
          const pageId = page.canvas_page_id || page.id || page.pageId;
          return pageId && initialSnapshotsRef.current.has(pageId);
        });

        if (allPagesStored && canvasPages.length > 0) {
          hasCompletedInitialStorageRef.current = true;
        }
      };

      // Store snapshots immediately from page objects (original from API)
      pagesToStore.forEach((page) => {
        // CRITICAL: Use canvas_page_id consistently for storage and retrieval
        const pageId = page.canvas_page_id || page.id || page.pageId;
        if (!pageId) return;

        // CRITICAL: Triple-check - NEVER overwrite
        if (
          storedPageIdsRef.current.has(pageId) ||
          initialSnapshotsRef.current.has(pageId)
        ) {
          return;
        }

        // Mark immediately to prevent duplicate storage
        storedPageIdsRef.current.add(pageId);

        // CRITICAL: Don't store anything initially - wait for IndexedDB data
        // We'll store the proper canvas state asynchronously

        // CRITICAL: Skip if already being loaded or already loaded
        if (snapshotsBeingLoadedRef.current.has(pageId)) {
          return;
        }

        // Mark as being loaded
        snapshotsBeingLoadedRef.current.add(pageId);
        // CRITICAL: Store the actual canvas arrays from IndexedDB for proper change detection
        // This needs to be loaded after IndexedDB is populated
        const loadAndStoreInitialState = async () => {
          try {
            let canvasState = null;

            if (isFreshEditLoadFromViewChildNode) {
              // Fresh edit flow should baseline from API payload, not prior IndexedDB state.
              canvasState = {
                texts: page.elements?.texts || page.texts || [],
                rectangles: page.elements?.rectangles || page.rectangles || [],
                circles: page.elements?.circles || page.circles || [],
                arrows: page.elements?.arrows || page.arrows || [],
                lines: page.elements?.lines || page.lines || [],
                doubleSidedArrows:
                  page.elements?.doubleSidedArrows || page.doubleSidedArrows || [],
                scribbles: page.elements?.scribbles || page.scribbles || [],
                images: page.elements?.images || page.images || [],
              };
            } else {
              // Try multiple times with increasing delays to ensure IndexedDB is ready
              let attempts = 0;
              const maxAttempts = 5;
              const delays = [1000, 2000, 3000, 4000, 5000];

              while (attempts < maxAttempts && !canvasState) {
                await new Promise((resolve) =>
                  setTimeout(resolve, delays[attempts]),
                );

                const currentResult = await loadFromIndexedDB(
                  "canvasElements",
                  pageId,
                );
                canvasState = currentResult?.data || {};

                // Check if we have meaningful data (not just empty arrays)
                // CRITICAL: Include images in the check - even if only images exist, that's valid data
                const hasData = Object.keys(canvasState).some((key) => {
                  const arr = canvasState[key];
                  return Array.isArray(arr) && arr.length > 0;
                });

                if (hasData) {
                  break;
                } else {
                  canvasState = null;
                }

                attempts++;
              }

              if (!canvasState) {
                return;
              }
            }

            // CRITICAL: Store the complete canvas state as JSON string
            // This includes ALL arrays (texts, rectangles, circles, arrows, lines, doubleSidedArrows, scribbles, images)
            // so that we can detect changes in image positions, not just element counts

            // CRITICAL: Normalize the state to only include canvas element arrays
            // This prevents false positives from metadata fields like videos, snapshot, canvasHeight, etc.
            const normalizedState = {
              texts: canvasState.texts || [],
              rectangles: canvasState.rectangles || [],
              circles: canvasState.circles || [],
              arrows: canvasState.arrows || [],
              lines: canvasState.lines || [],
              doubleSidedArrows: canvasState.doubleSidedArrows || [],
              scribbles: canvasState.scribbles || [],
              images: canvasState.images || [],
            };

            const stateJson = JSON.stringify(normalizedState);

            // CRITICAL: Only store if not already stored - NEVER overwrite the initial snapshot
            if (initialSnapshotsRef.current.has(pageId)) {
              return;
            }

            // Update both in-memory state and sessionStorage
            initialSnapshotsRef.current.set(pageId, stateJson);

            // Update sessionStorage immediately
            const currentStored = sessionStorage.getItem(
              "initialCanvasSnapshots",
            );
            let storedData = {};
            if (currentStored) {
              try {
                storedData = JSON.parse(currentStored);
              } catch (e) {
                storedData = {};
              }
            }
            storedData[pageId] = stateJson;
            sessionStorage.setItem(
              "initialCanvasSnapshots",
              JSON.stringify(storedData),
            );

            // Remove from loading set
            snapshotsBeingLoadedRef.current.delete(pageId);

            // Check if all snapshots are now stored
            checkAllSnapshotsStored();
          } catch (error) {
            console.error(
              `Error loading initial canvas state for ${pageId}:`,
              error,
            );
            // Fallback to snapshot string - but this won't detect image position changes
            const snapshot = page.snapshot || page.canva_snap_url || "";
            initialSnapshotsRef.current.set(pageId, snapshot);

            // Remove from loading set
            snapshotsBeingLoadedRef.current.delete(pageId);

            // Also check completion for fallback case
            checkAllSnapshotsStored();
          }
        };

        // Load asynchronously without blocking
        loadAndStoreInitialState();
      });

      // Check periodically if all snapshots are stored
      const checkInterval = setInterval(checkAllSnapshotsStored, 1000);
      setTimeout(() => clearInterval(checkInterval), 10000); // Stop checking after 10 seconds
    } else if (hasCompletedInitialStorageRef.current) {
      // Storage complete - never store again
      return;
    }
  }, [mode, canvasPages.length, location.state]);

  // Hydrate elements from IndexedDB when component mounts or pages change
  // CRITICAL: This ensures IndexedDB elements (with newly created elements) are loaded
  // and take precedence over API data in edit mode
  useEffect(() => {
    const isFreshEditLoadFromViewChildNode =
      mode === "edit" &&
      location.state?.forceFreshEditLoad === true &&
      !location.state?.pageId;

    const hydrateElements = async () => {
      if (canvasPages.length === 0) return;

      if (isFreshEditLoadFromViewChildNode) {
        hasHydratedRef.current = true;
        return;
      }

      // In edit mode, only hydrate once when pages are first loaded from API
      // Don't re-hydrate when user adds new pages
      const shouldForceHydrate = mode === "edit" && !hasHydratedRef.current;
      const hasAllElements = canvasPages.every(
        (page) =>
          page.elements &&
          (page.elements.texts?.length > 0 ||
            page.elements.rectangles?.length > 0 ||
            page.elements.images?.length > 0),
      );

      // In edit mode, only hydrate if we haven't hydrated yet
      // Even if pages seem to have elements, they might be from API and missing new ones
      const needsHydration =
        shouldForceHydrate || canvasPages.some((page) => !page.elements);

      if (!needsHydration && !shouldForceHydrate) {
        return;
      }

      // NOTE: Original state storage moved to separate useEffect to avoid multiple storage

      const hydratedPages = await Promise.all(
        canvasPages.map(async (page) => {
          try {
            const pageIdToUse = page.id || page.pageId;
            if (!pageIdToUse) {
              return page;
            }

            // Try both original and sanitized pageId
            const sanitizedPageId = sanitizeId(pageIdToUse, pageIdToUse);
            let result = null;

            try {
              result = await loadFromIndexedDB("canvasElements", pageIdToUse);
            } catch (error) {
            }

            if (!result && sanitizedPageId !== pageIdToUse) {
              try {
                result = await loadFromIndexedDB(
                  "canvasElements",
                  sanitizedPageId,
                );
              } catch (error) {
              }
            }

            const elements = result?.data || result || {};

            // CRITICAL: In edit mode, ALWAYS prefer IndexedDB elements over page.elements
            // This is because page.elements might be from stale API data
            // IndexedDB has the latest elements including newly created ones
            const hasIndexedDBElements =
              elements.texts?.length > 0 ||
              elements.rectangles?.length > 0 ||
              elements.images?.length > 0 ||
              elements.circles?.length > 0 ||
              elements.arrows?.length > 0;

            const useIndexedDBElements =
              mode === "edit" && hasIndexedDBElements;

            if (useIndexedDBElements) {
            }

            return {
              ...page,
              // Use IndexedDB snapshot if available (it's the latest), otherwise keep existing
              snapshot:
                elements.snapshot || page.snapshot || page.canva_snap_url,
              // CRITICAL: In edit mode, ALWAYS use IndexedDB elements if they exist
              // This ensures newly created elements from IndexedDB override stale API elements
              elements: useIndexedDBElements
                ? {
                    texts: elements.texts || [],
                    rectangles: elements.rectangles || [],
                    circles: elements.circles || [],
                    arrows: elements.arrows || [],
                    scribbles: elements.scribbles || [],
                    lines: elements.lines || [],
                    doubleSidedArrows: elements.doubleSidedArrows || [],
                    images: elements.images || [],
                  }
                : page.elements || {
                    texts: elements.texts || [],
                    rectangles: elements.rectangles || [],
                    circles: elements.circles || [],
                    arrows: elements.arrows || [],
                    scribbles: elements.scribbles || [],
                    lines: elements.lines || [],
                    doubleSidedArrows: elements.doubleSidedArrows || [],
                    images: elements.images || [],
                  },
              // Persist per-page canvas height
              canvasHeight: resolveCanvasHeightValue(
                elements.canvasHeight,
                page.canvasHeight,
                page.layout_config?.layout?.page_height_vh,
                page.layout?.page_height_vh,
              ),
            };
          } catch (error) {
            console.error(
              `Error hydrating elements for page ${page.id}:`,
              error,
            );
            return {
              ...page,
              elements: page.elements || {
                texts: [],
                rectangles: [],
                circles: [],
                arrows: [],
                scribbles: [],
                lines: [],
                doubleSidedArrows: [],
                images: [],
              },
            };
          }
        }),
      );

      // Check if we actually changed anything
      const hasChanges = hydratedPages.some((hydratedPage, idx) => {
        const originalPage = canvasPages[idx];
        if (!originalPage) return true;

        const origElements = originalPage.elements || {};
        const hydratedElements = hydratedPage.elements || {};

        return (
          (origElements.texts?.length || 0) !==
            (hydratedElements.texts?.length || 0) ||
          (origElements.rectangles?.length || 0) !==
            (hydratedElements.rectangles?.length || 0) ||
          (origElements.images?.length || 0) !==
            (hydratedElements.images?.length || 0) ||
          originalPage.snapshot !== hydratedPage.snapshot
        );
      });

      if (hasChanges || shouldForceHydrate) {
        setCanvasPages(hydratedPages);
        hasHydratedRef.current = true;
        // Signal that hydration is complete
        if (!hydrationComplete) {
          setHydrationComplete(true);
        }
      } else {
      }
    };

    // Only hydrate once initially when pages are loaded from API in edit mode
    if (!hasHydratedRef.current) {
      hydrateElements();
    }
  }, [canvasPages.length, mode, hydrationComplete, location.state]); // Run when canvasPages length changes or mode changes

  // Handle return from MyCanvas with location state updates
  useEffect(() => {
    // Only process snapshot updates if we have snapshot data and canvasPages are loaded
    if (
      location.state?.snapshot &&
      location.state?.pageId &&
      canvasPages.length > 0
    ) {
      // Create a unique key for this return to prevent duplicate processing
      const returnKey = `${
        location.state.pageId
      }-${location.state.snapshot?.substring(0, 50)}`;

      // Skip if we've already processed this return
      if (processedReturnRef.current === returnKey) {
        return;
      }

      // Reload elements from IndexedDB and update with the new snapshot from MyCanvas
      const reloadAndUpdateSnapshot = async () => {
        // Mark that we're processing a return to prevent other useEffects from interfering
        // This prevents useCanvasPersistence from overwriting our updates
        if (!window.isProcessingCanvasReturn) {
          window.isProcessingCanvasReturn = { current: false };
        }
        window.isProcessingCanvasReturn.current = true;

        // CRITICAL: Get original API state (stored before any IndexedDB hydration)
        // This ensures we compare IndexedDB state with the original API data, not modified state
        const editedPageId = location.state?.pageId;
        let originalPageState = null;
        if (editedPageId) {
          // First try to get from stored original API state
          originalPageState = originalPageStatesRef.current.get(editedPageId);

          // Fallback to current page state if original not stored (for newly added pages)
          if (!originalPageState) {
            const editedPage = canvasPages.find(
              (p) => p.id === editedPageId || p.pageId === editedPageId,
            );
            if (editedPage) {
              originalPageState = {
                elements: editedPage.elements
                  ? JSON.parse(JSON.stringify(editedPage.elements))
                  : {},
                canvasHeight: editedPage.canvasHeight ?? 100,
              };
            }
          }
        }

        // Preserve all current pages - don't lose any pages during reload
        const reloadedPages = await Promise.all(
          canvasPages.map(async (page) => {
            // Ensure page exists and has an ID
            if (!page || !page.id) {
              return page;
            }

            try {
              const result = await loadFromIndexedDB(
                "canvasElements",
                page.id || page.pageId,
              );

              // If no IndexedDB data exists, preserve the original page data
              // This is important for newly added pages that might not have IndexedDB data yet
              const elements = result?.data || result || {};
              const hasIndexedDBData = result && (result.data || result);

              const isReturningPage = page.id === location.state.pageId;
              const snapshotToUse = isReturningPage
                ? location.state.snapshot
                : elements.snapshot || page.snapshot;

              // Preserve all page properties, especially for newly added pages
              return {
                ...page, // Preserve all existing page properties
                // ALWAYS use the new snapshot from location.state for the page we returned from
                // This snapshot contains the updated elements rendered by toPng in MyCanvas
                snapshot: snapshotToUse || page.snapshot, // Fallback to original snapshot
                lastModified:
                  page.id === location.state.pageId
                    ? new Date().toISOString()
                    : page.lastModified || new Date().toISOString(),
                // Merge IndexedDB elements with existing page elements to preserve newly added pages
                elements: hasIndexedDBData
                  ? {
                      texts: elements.texts || page.elements?.texts || [],
                      rectangles:
                        elements.rectangles || page.elements?.rectangles || [],
                      circles: elements.circles || page.elements?.circles || [],
                      arrows: elements.arrows || page.elements?.arrows || [],
                      scribbles:
                        elements.scribbles || page.elements?.scribbles || [],
                      lines: elements.lines || page.elements?.lines || [],
                      doubleSidedArrows:
                        elements.doubleSidedArrows ||
                        page.elements?.doubleSidedArrows ||
                        [],
                      images: elements.images || page.elements?.images || [],
                    }
                  : page.elements || {
                      texts: [],
                      rectangles: [],
                      circles: [],
                      arrows: [],
                      scribbles: [],
                      lines: [],
                      doubleSidedArrows: [],
                      images: [],
                    },
                canvasHeight: resolveCanvasHeightValue(
                  elements.canvasHeight,
                  page.canvasHeight,
                  page.layout_config?.layout?.page_height_vh,
                  page.layout?.page_height_vh,
                ),
              };
            } catch (error) {
              console.error(
                `Error reloading elements for page ${page.id}:`,
                error,
              );
              // Return original page to preserve it, especially if it's newly added
              return page;
            }
          }),
        );

        // Ensure we didn't lose any pages during reload
        if (reloadedPages.length !== canvasPages.length) {
          console.error(
            `⚠️ Page count mismatch after reload! Expected ${canvasPages.length}, got ${reloadedPages.length}`,
          );
          console.error(
            "Original pages:",
            canvasPages.map((p) => p?.id),
          );
          console.error(
            "Reloaded pages:",
            reloadedPages.map((p) => p?.id),
          );

          // If we lost pages, restore from the original canvasPages
          // This is a safety net to prevent page loss
          const missingPageIds = canvasPages
            .map((p) => p?.id)
            .filter((id) => !reloadedPages.some((rp) => rp?.id === id));

          if (missingPageIds.length > 0) {
            // Find and restore missing pages
            const restoredPages = [
              ...reloadedPages,
              ...canvasPages.filter((p) => missingPageIds.includes(p?.id)),
            ];
            setCanvasPages(restoredPages);
            return; // Don't continue with the broken reload
          }
        }

        // Update pages with reloaded elements and new snapshot
        setCanvasPages(reloadedPages);

        // Persist the updated pages to localStorage (for both add and edit modes)
        // NOTE: DO NOT include snapshot in localStorage - it causes quota exceeded errors
        // Snapshots are stored in IndexedDB only
        const lightweightPages = reloadedPages.map((p) => ({
          id: p?.id || p?.pageId,
          canvas_page_id: p?.canvas_page_id, // CRITICAL: Include canvas_page_id for API pages
          zoomLevel: typeof p?.zoomLevel === "number" ? p.zoomLevel : 100,
          lastModified: p?.lastModified || new Date().toISOString(),
          canvasHeight: p?.canvasHeight,
          newlyAdded: p?.newlyAdded,
          // snapshot: p?.snapshot, // REMOVED - Snapshots stored in IndexedDB only
          shortDescription: p?.shortDescription,
          templateName: p?.templateName,
          canva_snap_url: p?.canva_snap_url,
          templateApplied: p?.templateApplied,
          text_boxes: Array.isArray(p?.text_boxes) ? p.text_boxes : [],
          text_box_content_data: Array.isArray(p?.text_box_content_data)
            ? p.text_box_content_data
            : [],
        }));

        if (mode === "add") {
          localStorage.setItem(
            "canvas_pages",
            JSON.stringify(lightweightPages),
          );
        } else {
          // In edit mode, also save to localStorage for consistency
          localStorage.setItem("canvasPages", JSON.stringify(lightweightPages));
        }

        // Return original state, page ID, and reloaded pages for change detection
        return { originalPageState, editedPageId, reloadedPages };
      };

      // Execute the reload and update
      const executeReload = async () => {
        // Set a global flag to prevent useCanvasPersistence from interfering
        window.isProcessingCanvasReturn = { current: true };

        // Reload and get original state for change detection
        const { originalPageState, editedPageId, reloadedPages } =
          await reloadAndUpdateSnapshot();

        // Mark this return as processed
        processedReturnRef.current = returnKey;

        // CRITICAL: Wait longer to ensure IndexedDB writes are complete
        // This is important because prepareAndSaveCanvasData might still be writing
        // Also wait for any pending IndexedDB transactions to complete
        // Retry reading from IndexedDB a few times to ensure we get the latest data
        await new Promise((resolve) => setTimeout(resolve, 500));

        // Additional wait and retry to ensure IndexedDB is fully synced
        let retryCount = 0;
        const maxRetries = 3;
        while (retryCount < maxRetries) {
          await new Promise((resolve) => setTimeout(resolve, 200));
          retryCount++;
        }

        // SIMPLE: Compare current snapshots with initial snapshots
        let detectedChanges = false;

        // Wait a bit for IndexedDB writes to complete
        await new Promise((resolve) => setTimeout(resolve, 1000));

        try {
          const pagesToCheck =
            reloadedPages && reloadedPages.length > 0
              ? reloadedPages
              : canvasPages;

          const changeResults = await Promise.all(
            pagesToCheck.map(async (page) => {
              const pageId = page.id || page.pageId;
              if (!pageId) return false;

              const initialSnapshot = initialSnapshotsRef.current.get(pageId);

              if (!initialSnapshot) {
                return false;
              }

              const currentResult = await loadFromIndexedDB(
                "canvasElements",
                pageId,
              );
              // Check both result.data.snapshot and result.snapshot
              const currentSnapshot =
                currentResult?.data?.snapshot ||
                currentResult?.snapshot ||
                page.snapshot ||
                page.canva_snap_url ||
                "";

              const hasChanged = currentSnapshot !== initialSnapshot;

              return hasChanged;
            }),
          );

          detectedChanges = changeResults.some(
            (hasChanges) => hasChanges === true,
          );
        } catch (error) {
          console.error("Error comparing snapshots:", error);
          detectedChanges = location.state?.hasChanges === true;
        }

        const hasChanges =
          detectedChanges || location.state?.hasChanges === true;
        setHasUnsavedChanges(hasChanges);

        // Clear the location state to prevent re-processing
        window.history.replaceState({}, document.title);

        // Clear the flag after a short delay to allow the update to complete
        setTimeout(() => {
          if (window.isProcessingCanvasReturn) {
            window.isProcessingCanvasReturn.current = false;
          }
        }, 500);
      };

      executeReload();
    } else if (
      mode === "edit" &&
      canvasPages.length > 0 &&
      initialSnapshotsRef.current.size > 0
    ) {
      // CRITICAL: Also check when returning via browser back button (no location.state)
      // This handles the case where location.state is lost
      // DISABLED: This early check was causing false positives
      // The snapshot comparison below is more accurate
      /*
      const checkOnReturn = async () => {
        // Wait a bit for IndexedDB to be ready
        await new Promise((resolve) => setTimeout(resolve, 1000));
        
        
        try {
          // SIMPLE APPROACH: Compare original API data with current IndexedDB data
          const changeResults = await Promise.all(
            canvasPages.map(async (page) => {
              const pageId = page.canvas_page_id || page.id || page.pageId;
              if (!pageId) return false;
              
              
              // Load current canvas state from IndexedDB
              const currentResult = await loadFromIndexedDB("canvasElements", pageId);
              const currentCanvasState = currentResult?.data || {};
              
              // Compare original API data with current IndexedDB data
              const hasChanges = comparePageData(page, currentCanvasState);
              
              
              return hasChanges;
            })
          );
          
          const anyChanges = changeResults.some(result => result === true);
          
          if (anyChanges) {
            setHasUnsavedChanges(true);
            // Set a flag for the AppBar back handler to use
            localStorage.setItem("canvasChangesDetected", "true");
          } else {
            // Clear any existing flag since no changes detected
            localStorage.removeItem("canvasChangesDetected");
          }
          
        } catch (error) {
          console.error("Error checking for changes:", error);
        }
      };
      
      checkOnReturn();
      */
    }
  }, [location, mode, canvasPages.length, blockerReadyTime, userHasInteracted]);

  // Simple helper functions for change detection
  const comparePageData = (originalPage, currentPageData) => {
    // Count elements in original API data
    const originalCount = {
      images: (originalPage.images || []).length,
      rectangles: (originalPage.rectangles || []).length,
      circles: (originalPage.circles || []).length,
      arrows: (originalPage.arrows || []).length,
      lines: (originalPage.lines || []).length,
      doubleSidedArrows: (originalPage.doubleSidedArrows || []).length,
      texts: (originalPage.texts || []).length,
      scribbles: (originalPage.scribbles || []).length,
    };

    // Count elements in current IndexedDB data
    const currentCount = {
      images: (currentPageData.images || []).length,
      rectangles: (currentPageData.rectangles || []).length,
      circles: (currentPageData.circles || []).length,
      arrows: (currentPageData.arrows || []).length,
      lines: (currentPageData.lines || []).length,
      doubleSidedArrows: (currentPageData.doubleSidedArrows || []).length,
      texts: (currentPageData.texts || []).length,
      scribbles: (currentPageData.scribbles || []).length,
    };

    // Check if any counts differ
    return Object.keys(originalCount).some(
      (key) => originalCount[key] !== currentCount[key],
    );
  };

  const countElements = (pageData) => {
    return {
      images: (pageData.images || []).length,
      rectangles: (pageData.rectangles || []).length,
      circles: (pageData.circles || []).length,
      arrows: (pageData.arrows || []).length,
      lines: (pageData.lines || []).length,
      doubleSidedArrows: (pageData.doubleSidedArrows || []).length,
      texts: (pageData.texts || []).length,
      scribbles: (pageData.scribbles || []).length,
    };
  };

  // Refs for change detection
  const cleanupRef = useRef(null);
  const lastCheckRef = useRef(0);

  useEffect(() => {
    // Clean up previous listeners
    if (cleanupRef.current) {
      cleanupRef.current();
      cleanupRef.current = null;
    }

    // DISABLED: Snapshot comparison is now handled by location.state.hasChanges from MyCanvas
    // This prevents false positives and simplifies the logic

    return () => {
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = null;
      }
    };
  }, [mode, canvasPages.length]);

  const setupChangeCheck = () => {
    if (
      mode !== "edit" ||
      canvasPages.length === 0 ||
      initialSnapshotsRef.current.size === 0
    ) {
      return;
    }

    const checkForChanges = async () => {
      // Throttle - don't check more than once per 2 seconds
      const now = Date.now();
      if (now - lastCheckRef.current < 2000) {
        return;
      }
      lastCheckRef.current = now;

      if (hasUnsavedChanges) {
        return; // Already detected
      }

      // Don't wait - run the check immediately
      // The snapshot should already be stored from the previous effect

      try {
        // Log the actual stored snapshot content
        initialSnapshotsRef.current.forEach((snapshot, key) => {});

        const changeResults = await Promise.all(
          canvasPages.map(async (page) => {
            // CRITICAL: Use canvas_page_id consistently for storage and retrieval
            const pageId = page.canvas_page_id || page.id || page.pageId;
            if (!pageId) return false;

            // Try multiple possible ID fields with canvas_page_id priority
            let initialSnapshot = null;
            let usedId = null;
            const possibleIds = [
              page.canvas_page_id,
              page.id,
              page.pageId,
            ].filter(Boolean);

            for (const id of possibleIds) {
              if (initialSnapshotsRef.current.has(id)) {
                initialSnapshot = initialSnapshotsRef.current.get(id);
                usedId = id;
                break;
              }
            }

            if (!initialSnapshot) {
              return false;
            }

            // Load current canvas state from IndexedDB
            const currentResult = await loadFromIndexedDB(
              "canvasElements",
              pageId,
            );
            let currentCanvasState = currentResult?.data || {};

            // CRITICAL: Normalize current state to only include canvas element arrays
            // This prevents false positives from metadata fields
            currentCanvasState = {
              texts: currentCanvasState.texts || [],
              rectangles: currentCanvasState.rectangles || [],
              circles: currentCanvasState.circles || [],
              arrows: currentCanvasState.arrows || [],
              lines: currentCanvasState.lines || [],
              doubleSidedArrows: currentCanvasState.doubleSidedArrows || [],
              scribbles: currentCanvasState.scribbles || [],
              images: currentCanvasState.images || [],
            };

            // Parse initial state from snapshot
            let initialCanvasState = {};

            // Check if the snapshot is a JSON string (new format) or URL string (old format)
            if (
              initialSnapshot.startsWith("{") ||
              initialSnapshot.startsWith("[")
            ) {
              // New format - JSON canvas state
              try {
                initialCanvasState = JSON.parse(initialSnapshot);
              } catch (e) {
                return false;
              }
            } else {
              // Old format - URL string, can't reliably detect changes

              // When snapshot is a URL string, we can't reliably detect changes
              // Conservative approach: assume NO changes (return false)
              // This prevents false positives when viewing API data without making changes
              const hasChanged = false;

              return hasChanged;
            }

            // Use the same change detection logic as canvasLeaveHandler
            // This checks array counts AND position/size changes for elements
            const hasChanged = hasCanvasArraysChangedSimple(
              currentCanvasState,
              initialCanvasState,
            );

            // CRITICAL: If simple comparison didn't find changes, do a detailed position check
            // This catches cases where elements exist but their positions changed (e.g., dragged images)
            if (!hasChanged) {
              const detailedCheck = () => {
                // Check all arrays for position/size changes
                const arraysToCheck = [
                  "texts",
                  "rectangles",
                  "circles",
                  "images",
                  "arrows",
                  "lines",
                  "doubleSidedArrows",
                  "scribbles",
                ];

                for (const arrayName of arraysToCheck) {
                  const currentArray = currentCanvasState[arrayName] || [];
                  const initialArray = initialCanvasState[arrayName] || [];

                  // Only check if arrays have the same length (otherwise count check would have caught it)
                  if (
                    currentArray.length === initialArray.length &&
                    currentArray.length > 0
                  ) {
                    // Check each element for position/size changes
                    for (let i = 0; i < currentArray.length; i++) {
                      const currentEl = currentArray[i];
                      const initialEl = initialArray.find(
                        (el) => el?.id === currentEl?.id,
                      );

                      if (initialEl && currentEl) {
                        // Check for position changes
                        if (
                          Math.abs((currentEl.x || 0) - (initialEl.x || 0)) >
                            0.1 ||
                          Math.abs((currentEl.y || 0) - (initialEl.y || 0)) >
                            0.1
                        ) {
                          return true;
                        }

                        // Check for size changes
                        if (
                          Math.abs(
                            (currentEl.width || 0) - (initialEl.width || 0),
                          ) > 0.1 ||
                          Math.abs(
                            (currentEl.height || 0) - (initialEl.height || 0),
                          ) > 0.1
                        ) {
                          return true;
                        }

                        // Check for rotation changes
                        if (
                          Math.abs(
                            (currentEl.rotation || 0) -
                              (initialEl.rotation || 0),
                          ) > 0.1
                        ) {
                          return true;
                        }
                      }
                    }
                  }
                }

                return false;
              };

              if (detailedCheck()) {
                return true;
              }
            }

            return hasChanged;
          }),
        );

        const detectedChanges = changeResults.some(Boolean);

        if (detectedChanges && blockerReadyTime && userHasInteracted) {
          // setHasUnsavedChanges(true); // DISABLED
          // Set a flag for the AppBar back handler to use
          localStorage.setItem("canvasChangesDetected", "true");
        } else if (
          detectedChanges &&
          (!blockerReadyTime || !userHasInteracted)
        ) {
          // Set the flag anyway since changes were detected
          localStorage.setItem("canvasChangesDetected", "true");
        } else {
          // Clear the flag when no changes are detected
          localStorage.removeItem("canvasChangesDetected");
        }
      } catch (error) {
        console.error("Error in change check:", error);
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        checkForChanges();
      }
    };

    const handleFocus = () => {
      checkForChanges();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);

    // Run check immediately and after delays
    const timeoutId1 = setTimeout(() => {
      checkForChanges();
    }, 2000);

    const timeoutId2 = setTimeout(() => {
      checkForChanges();
    }, 4000);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
      clearTimeout(timeoutId1);
      clearTimeout(timeoutId2);
    };
  };

  // Handle pagination changes from AppTable
  const handlePaginationChange = useCallback(
    (newLimit, newOffset) => {
      // Update Redux state
      if (newLimit !== pagination?.limit) {
        dispatch(setLimit(newLimit));
        localStorage.setItem("canvasTemplates_limit", newLimit.toString());
      }
      if (newOffset !== pagination?.offset) {
        dispatch(setOffset(newOffset));
        localStorage.setItem("canvasTemplates_offset", newOffset.toString());
      }

      // Update local state
      setLocalLimit(newLimit);
      setLocalOffset(newOffset);
    },
    [dispatch, pagination?.limit, pagination?.offset],
  );

  // Handle filter/sort changes from AppTable
  const handleFilterSort = useCallback(
    (queryString) => {
      // Remove limit and offset from AppTable's query string
      // We'll manage those ourselves through Redux
      const params = new URLSearchParams(queryString);
      params.delete("limit");
      params.delete("offset");
      const cleanedQuery = params.toString();

      // Only update if the query actually changed
      if (cleanedQuery !== currentApiQuery) {
        setCurrentApiQuery(cleanedQuery);
      }
    },
    [currentApiQuery],
  );

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    type: "",
  });

  const tableContainerRef = useRef();
  const isUpdatingRef = useRef(false);

  // CRITICAL: Log hasUnsavedChanges state changes for debugging
  useEffect(() => {}, [hasUnsavedChanges]);

  // Mark user as interacted after any interaction with the form
  useEffect(() => {
    const handleInteraction = () => {
      if (!userHasInteracted) {
        setUserHasInteracted(true);
      }
    };

    // Listen for various user interactions
    const events = ["click", "keydown", "scroll", "touchstart"];
    events.forEach((event) => {
      document.addEventListener(event, handleInteraction, { once: true });
    });

    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, handleInteraction);
      });
    };
  }, [userHasInteracted]);

  // Allow blocker to be ready after 3 seconds of being on the page
  useEffect(() => {
    const timer = setTimeout(() => {
      setBlockerReadyTime(Date.now());
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  // CRITICAL: Clean up session storage when component unmounts
  useEffect(() => {
    return () => {
      // Clean up session storage when component unmounts
      const sessionKey = `fromMyCanvas_processed_${id}`;
      sessionStorage.removeItem(sessionKey);

      // Clean up global flag when component unmounts
      if (window.fromMyCanvasProcessed?.[id]) {
        delete window.fromMyCanvasProcessed[id];
      }

      // CRITICAL: Do NOT clean up initial API pages data on unmount
      // The baseline needs to persist across navigation to/from MyCanvas
      // It will be cleared on save or when explicitly starting a new edit session
    };
  }, [id]);

  // CRITICAL: Clear stale flags on component mount
  useEffect(() => {
    // Clear any stale canvasChangesDetected flag from previous sessions
    localStorage.removeItem("canvasChangesDetected");
  }, []);

  useEffect(() => {
    // CRITICAL: Respect hasChanges from location.state
    // Priority: location.state.hasChanges > default to false
    // When location.state is undefined (e.g., browser back button), default to false
    // BUT: Don't override if hasUnsavedChanges is already true (set by back button handler)

    // First, check if location.state has explicit hasChanges value
    if (location.state?.hasChanges === true) {
      setHasUnsavedChanges(true);
    } else {
      // CRITICAL: Only reset to false if it's not already true
      // This prevents the back button handler's detection from being overridden
      setHasUnsavedChanges((currentValue) => {
        if (currentValue === true) {
          return true; // Keep true if already true
        }
        // Default to false - no changes
        // This includes:
        // - location.state?.hasChanges === false (explicitly no changes)
        // - location.state is missing (browser back button)
        // - location.state exists but hasChanges is undefined
        return false;
      });
    }
  }, [location.state?.hasChanges]);

  // Track if we've initialized shortDescription to prevent overwriting user input
  const hasInitializedShortDescriptionRef = useRef(false);

  // Sync short description from localStorage when it's set by useCanvasPersistence
  // Only run once when data loads, not on every shortDescription change
  useEffect(() => {
    if (
      mode === "edit" &&
      isInitialDataLoaded &&
      !hasInitializedShortDescriptionRef.current
    ) {
      const saved = localStorage.getItem("short_description");
      if (saved && saved !== "undefined") {
        // Process short description to handle both plain text and HTML content
        let processedSaved = saved;
        if (processedSaved && !processedSaved.includes('<div class="editor-shell">') && !processedSaved.includes('<div class=\\"editor-shell\\"')) {
          // If it's plain text (no editor-shell wrapper), convert it to HTML format
          processedSaved = `<p>${processedSaved}</p>`;
        }
        setShortDescription(cleanBaselineHtml(processedSaved));
        hasInitializedShortDescriptionRef.current = true;
      }
    }
  }, [isInitialDataLoaded, mode]);

  // Also try to get short_description from canvasData if available (fallback)
  // Only run once when canvasData loads, not on every shortDescription change
  useEffect(() => {
    if (
      mode === "edit" &&
      canvasData &&
      !hasInitializedShortDescriptionRef.current &&
      (canvasData.short_description || canvasData.detail?.short_description)
    ) {
      const desc =
        canvasData.short_description || canvasData.detail?.short_description;
      if (desc) {
        // Process short description to handle both plain text and HTML content
        let processedDesc = desc;
        if (processedDesc && !processedDesc.includes('<div class="editor-shell">') && !processedDesc.includes('<div class=\\"editor-shell\\"')) {
          // If it's plain text (no editor-shell wrapper), convert it to HTML format
          processedDesc = `<p>${processedDesc}</p>`;
        }
        
        const cleanedDesc = cleanBaselineHtml(processedDesc);
        setShortDescription(cleanedDesc);
        localStorage.setItem("short_description", cleanedDesc);
        hasInitializedShortDescriptionRef.current = true;
      }
    }
  }, [mode, canvasData, hasInitializedShortDescriptionRef.current]);

  // Store initial baseline from IndexedDB after it's been populated
  // CRITICAL: Baseline is now captured in useCanvasPersistence right after API data is loaded
  // This effect just loads the existing baseline from localStorage into the ref
  useEffect(() => {
    const loadExistingBaseline = () => {
      const storageKey = `initialApiPages_${id}`;
      const existingBaseline = localStorage.getItem(storageKey);

      // Load existing baseline from localStorage into ref
      if (existingBaseline && !initialApiPagesRef.current) {
        try {
          initialApiPagesRef.current = JSON.parse(existingBaseline);
        } catch (e) {
          console.error("Error parsing existing baseline:", e);
        }
      } else if (!existingBaseline) {
      }
    };

    loadExistingBaseline();
  }, [mode, id]);

  // Track if we've auto-opened the template modal to prevent reopening
  const hasAutoOpenedTemplateModalRef = useRef(false);

  // Auto-open template modal for add mode when data is loaded no pages
  useEffect(() => {
    if (
      mode === "add" &&
      isInitialDataLoaded &&
      canvasPages.length === 0 &&
      !templateModalOpen &&
      !hasAutoOpenedTemplateModalRef.current
    ) {
      // Generate a new page ID and open the template modal
      const newPageId = uuidv4();
      setPendingNewPageId(newPageId);
      setTemplateModalOpen(true);
      setIndex(0);
      hasAutoOpenedTemplateModalRef.current = true;
    }

    // Reset the flag when pages are added (so it can auto-open again if all pages are deleted)
    if (canvasPages.length > 0) {
      hasAutoOpenedTemplateModalRef.current = false;
    }
  }, [mode, isInitialDataLoaded, canvasPages.length, templateModalOpen]);

  const showSnackbar = (message, type) => {
    setSnackbar({ open: true, message, type });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Handle save
  const handleSaveClick = () => {
    setShowSaveModal(true);
  };

  const handleCloseModal = () => {
    setShowSaveModal(false);
  };

  // Generate layout config from elements
  const generateLayoutConfig = (elements) => {
    const leftColumnElements = [];
    const rightColumnElements = [];

    const assignToColumn = (el) => {
      const x = el.x || 0;
      return x < 230 ? leftColumnElements : rightColumnElements;
    };

    // Texts
    (elements.texts || []).forEach((text) => {
      assignToColumn(text).push({
        id: text.id,
        type: "text_box",
        position: { x: text.x, y: text.y },
        size: { width: text.width || 200, height: text.height || 100 },
        text: text.text || "",
        style: {
          fontSize: text.fontSize || 14,
          fontWeight: text.fontStyle?.includes("bold") ? "bold" : "normal",
          color: text.fill || "#000000",
        },
      });
    });

    // Images
    (elements.images || []).forEach((img) => {
      const exactPosition = { x: img.x, y: img.y };
      const exactSize = { width: img.width, height: img.height };

      assignToColumn(img).push({
        id: img.id,
        type: "image",
        position: exactPosition,
        size: exactSize,
      });
    });

    // Rectangles
    (elements.rectangles || []).forEach((rect) => {
      assignToColumn(rect).push({
        id: rect.id,
        type: "rectangle",
        position: { x: rect.x, y: rect.y },
        size: { width: rect.width, height: rect.height },
        style: {
          fillColor: rect.fillColor || "#000000",
        },
      });
    });

    // Circles
    (elements.circles || []).forEach((circle) => {
      assignToColumn(circle).push({
        id: circle.id,
        type: "circle",
        position: { x: circle.x, y: circle.y },
        radius: circle.radius,
        style: {
          fillColor: circle.fillColor || "#000000",
        },
      });
    });

    // Arrows
    (elements.arrows || []).forEach((arrow) => {
      assignToColumn(arrow).push({
        id: arrow.id,
        type: "arrow",
        points: arrow.points || [],
        style: {
          stroke: arrow.stroke || "#000000",
          strokeWidth: arrow.strokeWidth || 2,
        },
      });
    });

    const pageHeightVh = parseInt(elements?.canvasHeight) || 100;

    return {
      layout: {
        type: "two_column",
        page_height_vh: pageHeightVh,
        columns: [
          {
            id: "left_column",
            width: "50%",
            elements: leftColumnElements,
          },
          {
            id: "right_column",
            width: "50%",
            elements: rightColumnElements,
          },
        ],
      },
    };
  };

  const handleConfirmSave = async () => {
    if (!comment) {
      showSnackbar("Comment is mandatory.", "error");
      return;
    }

    if (!canvasPages || canvasPages.length === 0) {
      showSnackbar(
        "Cannot save canvas without any pages. Please add at least one canvas page.",
        "error",
      );
      return;
    }

    // Also log the raw canvasPages to see all properties

    setIsLoading(true);
    setShowSaveModal(false);
    setSaveProgress({ step: "Saving Canvas...", percentage: 10 });

    try {
      // Get removed IDs from IndexedDB (for edit mode) - do this before processing pages
      // so we can add removed part references with qty: 0 to text_box_content_data
      const getAllRemovedShapeIds = async () => {
        const allRemovedShapeIds = [];
        const allRemovedTextIds = [];
        const allRemovedTextContentIds = [];
        const allRemovedImageIds = [];
        const allRemovedPartReferences = [];

        try {
          const db = await openCanvasDB();
          const transaction = db.transaction(["canvasElements"], "readonly");
          const store = transaction.objectStore("canvasElements");

          // Get removed shape IDs for each page
          for (const page of canvasPages) {
            const pageId = page.id || page.pageId;

            if (pageId) {
              try {
                const removedShapeData = await new Promise(
                  (resolve, reject) => {
                    const request = store.get(`${pageId}_removed_shapes`);
                    request.onsuccess = () => resolve(request.result);
                    request.onerror = () => reject(request.error);
                  },
                );

                if (removedShapeData?.data) {
                  // Extract all removed ids from both main + off-canvas buckets
                  Object.entries(removedShapeData.data).forEach(
                    ([key, shapeArray]) => {
                      if (!Array.isArray(shapeArray)) return;

                      if (key === "removedPartReferences") {
                        // Store removed part references (these are objects, not just IDs)
                        allRemovedPartReferences.push(...shapeArray);
                        return;
                      }

                      const idValues = shapeArray.filter(
                        (id) =>
                          (typeof id === "string" || typeof id === "number") &&
                          String(id).trim() !== "",
                      );
                      if (idValues.length === 0) return;

                      // Keep generic shape removal behavior
                      allRemovedShapeIds.push(...idValues);

                      // Text removals (main + off-canvas + legacy key variants)
                      if (
                        key === "removedTexts" ||
                        key === "removedOffCanvasTexts" ||
                        key === "removed_off_canvas_texts"
                      ) {
                        allRemovedTextIds.push(...idValues);
                        allRemovedTextContentIds.push(...idValues);
                      }

                      // Image removals (main + off-canvas + legacy key variants)
                      if (
                        key === "removedImages" ||
                        key === "removedOffCanvasImages" ||
                        key === "removed_off_canvas_images"
                      ) {
                        allRemovedImageIds.push(...idValues);
                      }
                    },
                  );
                }
              } catch (error) {
                // Continue to next page if error
              }
            }
          }

          // Remove duplicates and filter out empty values
          const uniqueShapeIds = [...new Set(allRemovedShapeIds)].filter(
            (id) => id && String(id).trim() !== "",
          );
          const uniqueTextIds = [...new Set(allRemovedTextIds)].filter(
            (id) => id && String(id).trim() !== "",
          );
          const uniqueTextContentIds = [
            ...new Set(allRemovedTextContentIds),
          ].filter((id) => id && String(id).trim() !== "");
          const uniqueImageIds = [...new Set(allRemovedImageIds)].filter(
            (id) => id && String(id).trim() !== "",
          );

          // Deduplicate removed part references by part_ref + text_box_id combination
          const uniqueRemovedPartReferences = [];
          const seenPartRefs = new Set();
          allRemovedPartReferences.forEach((partRef) => {
            if (!partRef || !partRef.part_ref) return;
            const key = `${partRef.part_ref}_${partRef.text_box_id || ""}`;
            if (!seenPartRefs.has(key)) {
              seenPartRefs.add(key);
              uniqueRemovedPartReferences.push(partRef);
            }
          });

          return {
            shapeIds: uniqueShapeIds,
            textIds: uniqueTextIds,
            textContentIds: uniqueTextContentIds,
            imageIds: uniqueImageIds,
            removedPartReferences: uniqueRemovedPartReferences,
          };
        } catch (error) {
          console.error("Error getting removed shape IDs:", error);
          return {
            shapeIds: [],
            textIds: [],
            textContentIds: [],
            imageIds: [],
            removedPartReferences: [],
          };
        }
      };

      // Get removed IDs early (for edit mode)
      const removedIds =
        mode === "edit"
          ? await getAllRemovedShapeIds()
          : {
              shapeIds: [],
              textIds: [],
              textContentIds: [],
              imageIds: [],
              removedPartReferences: [],
            };

      const pagesData = [];
      // Track text_box_content_id for replaced/removed part refs across all pages
      const allReplacedPartRefContentIds = new Set();

      // Get a map of canvas_page_id by page id from canvasData for reference
      const canvasPageIdMap = new Map();
      if (canvasData?.pages && Array.isArray(canvasData.pages)) {
        canvasData.pages.forEach((apiPage) => {
          canvasPageIdMap.set(
            apiPage.id || apiPage.canvas_page_id,
            apiPage.canvas_page_id,
          );
        });
      }

      // CRITICAL: Also build a map from localStorage pages to recover canvas_page_id if missing
      const localStoragePageIdMap = new Map();
      try {
        let savedPagesRaw = localStorage.getItem("canvasPages");
        if (!savedPagesRaw || savedPagesRaw === "undefined") {
          savedPagesRaw = localStorage.getItem("originalCanvasPages");
        }
        if (savedPagesRaw && savedPagesRaw !== "undefined") {
          const savedPages = JSON.parse(savedPagesRaw);
          if (Array.isArray(savedPages)) {
            savedPages.forEach((p) => {
              if (p.id && p.canvas_page_id) {
                localStoragePageIdMap.set(p.id, p.canvas_page_id);
              }
            });
          }
        }
      } catch (err) {
      }

      for (const page of canvasPages) {
        const pageId = page.id || page.pageId;
        if (!pageId) continue;

        const isApiPage = Boolean(page.canvas_page_id);

        // If canvas_page_id is missing, try to get it from multiple sources
        let canvas_page_id = page.canvas_page_id;

        // First try: canvasData (from API)
        if (!canvas_page_id && canvasData?.pages) {
          const apiPage = canvasData.pages.find(
            (p) => p.id === pageId || p.canvas_page_id === pageId,
          );
          canvas_page_id = apiPage?.canvas_page_id;
        }

        // Second try: localStorage (from previous saves)
        if (!canvas_page_id && localStoragePageIdMap.has(pageId)) {
          canvas_page_id = localStoragePageIdMap.get(pageId);
        }

        // Skip removed pages - they're tracked in remove_canvas_pages but not sent in pages array
        const pageIdToCheck = canvas_page_id || pageId;
        if (mode === "edit" && removedCanvasPages.includes(pageIdToCheck)) {
          continue;
        }

        // Use same key as saveCanvasElementsToIndexedDB (sanitized) so elements
        // saved when leaving MyCanvas (e.g. user-added text) are found here
        const loadKey = sanitizeId(pageId, pageId);
        let elementsResult = await loadFromIndexedDB("canvasElements", loadKey);
        let offCanvasElementsResult = await loadFromIndexedDB(
          "offCanvasElements",
          loadKey,
        );
        if (!elementsResult?.data && loadKey !== pageId) {
          elementsResult = await loadFromIndexedDB("canvasElements", pageId);
        }
        if (!offCanvasElementsResult?.data && loadKey !== pageId) {
          offCanvasElementsResult = await loadFromIndexedDB(
            "offCanvasElements",
            pageId,
          );
        }
        const elements = elementsResult?.data || elementsResult;
        const offCanvasElements =
          offCanvasElementsResult?.data || offCanvasElementsResult;
        const mergeElementArrays = (mainArr, offArr) => [
          ...(Array.isArray(mainArr) ? mainArr : []),
          ...(Array.isArray(offArr)
            ? offArr.map((el) => ({
                ...el,
                isOffCanvas: true,
              }))
            : []),
        ];
        const allElements = {
          ...(elements || {}),
          texts: mergeElementArrays(elements?.texts, offCanvasElements?.texts),
          rectangles: mergeElementArrays(
            elements?.rectangles,
            offCanvasElements?.rectangles,
          ),
          circles: mergeElementArrays(
            elements?.circles,
            offCanvasElements?.circles,
          ),
          arrows: mergeElementArrays(elements?.arrows, offCanvasElements?.arrows),
          lines: mergeElementArrays(elements?.lines, offCanvasElements?.lines),
          doubleSidedArrows: mergeElementArrays(
            elements?.doubleSidedArrows,
            offCanvasElements?.doubleSidedArrows,
          ),
          scribbles: mergeElementArrays(
            elements?.scribbles,
            offCanvasElements?.scribbles,
          ),
          images: mergeElementArrays(elements?.images, offCanvasElements?.images),
          videos: mergeElementArrays(elements?.videos, offCanvasElements?.videos),
        };

        const loadedTextCount = allElements?.texts?.length ?? 0;
        const userTextsLoaded = (allElements?.texts || []).filter(
          (t) => t.createdOnCanvas === true,
        );

        if (!elements && !offCanvasElements) continue;

        // Process shapes
        const shapeData = [];
        const imageData = [];
        const textBoxContentData = [];
        const textBoxContentDedup = new Set();
        const addTextBoxEntries = (entries = []) => {
          if (!Array.isArray(entries)) {
            return;
          }

          entries.forEach((entry) => {
            if (!entry) {
              return;
            }

            const dedupKey =
              entry.text_box_content_id ||
              `${entry.text_box_id || "new"}-${entry.part_ref || ""}-${
                entry.inline_note || ""
              }-${entry.qty ?? 0}-${entry.units || ""}`;

            if (textBoxContentDedup.has(dedupKey)) {
              return;
            }

            textBoxContentDedup.add(dedupKey);
            textBoxContentData.push(entry);
          });
        };

        const pushShapes = (arr, type) => {
          arr.forEach((el) => {
            const shapeFill = el.fill || el.fillColor || "";
            const shapeColor = el.stroke || el.strokeColor || "";
            const strokeWidth = el.strokeWidth;

            const getColorValue = (color) => {
              if (!color || color === "transparent") return 0;
              if (color.startsWith("#"))
                return parseInt(color.replace("#", ""), 16);
              if (color.startsWith("rgb")) {
                const matches = color.match(/\d+/g);
                if (matches && matches.length >= 3) {
                  const [r, g, b] = matches;
                  return (r << 16) | (g << 8) | b;
                }
              }
              return 0;
            };

            const fillValue = getColorValue(shapeFill);
            const strokeValue = getColorValue(shapeColor);

            // Client-only ids (marker / line_ temp ids) — omit top-level shape_id for API
            const isMarkerShape =
              isMarkerId(el.shape_id) || isMarkerId(el.id);

            // Check if this element comes from a template
            // Support both flag names for compatibility: isFromTemplate and fromCanvasTemplate
            const isFromTemplate =
              el.isFromTemplate === true ||
              el.fromCanvasTemplate === true ||
              page.fromCanvasTemplate === true;

            const isOffCanvasElement =
              el.isOffCanvas === true ||
              el.is_off_canvas === true ||
              el.isOffcanvas === true;
            const elForPayload = { ...el };
            delete elForPayload.isOffCanvas;
            delete elForPayload.is_off_canvas;
            delete elForPayload.isOffcanvas;
            const shapeObj = {
              height: Math.round(Number(el.height) || 0),
              width: Math.round(Number(el.width) || 0),
              shape_fill: fillValue,
              shape_color: strokeValue,
              stroke_color: strokeValue,
              stroke_size: Math.round(Number(el.strokeWidth) || 0),
              shape_info: {
                ...elForPayload,
                ...(isOffCanvasElement ? { isOffcanvas: true } : {}),
              },
              shape_properties: {
                ...elForPayload,
                ...(isOffCanvasElement ? { isOffcanvas: true } : {}),
              },
              type,
            };

            // For Text type, ensure canvas_page_location_and_size with rotation is included
            if (type === "Text") {
              shapeObj.canvas_page_location_and_size = {
                x: el.x ?? 0,
                y: el.y ?? 0,
                width: el.width ?? 200,
                height: el.height ?? 100,
                rotation: el.rotation ?? 0,
              };
              shapeObj.shape_properties.canvas_page_location_and_size = {
                x: el.x ?? 0,
                y: el.y ?? 0,
                width: el.width ?? 200,
                height: el.height ?? 100,
                rotation: el.rotation ?? 0,
              };
              shapeObj.shape_info.canvas_page_location_and_size = {
                x: el.x ?? 0,
                y: el.y ?? 0,
                width: el.width ?? 200,
                height: el.height ?? 100,
                rotation: el.rotation ?? 0,
              };
            }

            // Add shape_id only when it's a valid UUID (API). Don't send shape_id for marker / line_ temp ids — backend expects UUID and will assign one for new shapes.
            const isUserCreatedText =
              type === "Text" && el.createdOnCanvas === true;
            const candidateShapeId = el.shape_id || el.id;
            const omitTopLevelShapeIdForApi =
              candidateShapeId &&
              (isMarkerId(candidateShapeId) ||
                isClientTempLineLikeId(candidateShapeId));
            if (
              !omitTopLevelShapeIdForApi &&
              (isUserCreatedText ||
                (!isMarkerShape && (!isFromTemplate || isApiPage)))
            ) {
              shapeObj.shape_id = candidateShapeId;
            }
            // Omit marker / client temp line-like ids from top-level shape_id (not valid UUIDs for API)

            shapeData.push(shapeObj);
          });
        };

        // Process different shape types
        pushShapes(allElements.rectangles || [], "Rect");
        pushShapes(allElements.circles || [], "Circle");
        pushShapes(allElements.arrows || [], "Arrow");
        pushShapes(allElements.doubleSidedArrows || [], "DoubleSidedArrow");
        pushShapes(allElements.lines || [], "Line");
        // Include texts that are either non-marker OR created on canvas (new user-added texts have marker id but createdOnCanvas: true)
        const textsForShapeData = (allElements.texts || []).filter(
          (t) =>
            t.isOffCanvas === true ||
            t.is_off_canvas === true ||
            t.isOffcanvas === true ||
            t.createdOnCanvas === true ||
            (!isMarkerId(t.id) && !isMarkerId(t.shape_id)),
        );
        pushShapes(textsForShapeData, "Text");

        // In edit mode, collect original part refs from page data BEFORE processing current elements
        // This allows us to detect replaced part refs later
        // CRITICAL: Collect from both text_box_content_data and text_boxes to ensure we capture all original part refs
        const originalPartRefs = new Map(); // key: `${part_ref}_${text_box_id || ""}`, value: entry
        if (mode === "edit") {
          // Method 1: Collect from text_box_content_data (flattened format)
          if (
            page.text_box_content_data &&
            Array.isArray(page.text_box_content_data)
          ) {
            page.text_box_content_data.forEach((entry) => {
              if (entry && entry.part_ref) {
                const key = `${entry.part_ref}_${entry.text_box_id || ""}`;
                // Store the entry with all its properties, especially text_box_content_id
                originalPartRefs.set(key, { ...entry });
              }
            });
          }

          // Method 2: Also collect from text_boxes (nested format) - this is the source of truth from API
          if (Array.isArray(page.text_boxes)) {
            page.text_boxes.forEach((textBox) => {
              if (!textBox) return;
              const textBoxId = textBox.text_box_id;
              const textBoxContent = textBox.text_box_content || [];
              textBoxContent.forEach((content) => {
                if (content && content.part_ref) {
                  const key = `${content.part_ref}_${textBoxId || ""}`;
                  // Only set if not already set (text_box_content_data might have more complete data)
                  if (!originalPartRefs.has(key)) {
                    originalPartRefs.set(key, {
                      ...content,
                      text_box_id: textBoxId,
                    });
                  } else {
                    // Merge to ensure we have text_box_content_id if available
                    const existing = originalPartRefs.get(key);
                    if (
                      content.text_box_content_id &&
                      !existing.text_box_content_id
                    ) {
                      originalPartRefs.set(key, {
                        ...existing,
                        text_box_content_id: content.text_box_content_id,
                      });
                    }
                  }
                }
              });
            });
          }
        }

        // Process text elements to extract text_box_content_data and text_box_properties
        // This represents the CURRENT state from IndexedDB
        const currentPartRefsFromElements = new Set(); // Track part refs from current elements only
        (allElements.texts || []).forEach((text) => {
          const { text_box_content_data, text_box_properties } =
            convertTextToPayload(text);
          if (
            (!text_box_content_data || text_box_content_data.length === 0) &&
            (text?.partsByType || text?.text_box_content_data)
          ) {
          }
          if (text_box_content_data.length > 0) {
            // Filter out units key if it's empty or null
            const filteredTextBoxData = text_box_content_data.map((item) => {
              const filteredItem = { ...item };
              if (
                !filteredItem.units ||
                filteredItem.units === null ||
                filteredItem.units === ""
              ) {
                delete filteredItem.units;
              }
              // Track current part refs from elements
              if (mode === "edit" && filteredItem.part_ref) {
                const key = `${filteredItem.part_ref}_${
                  filteredItem.text_box_id || ""
                }`;
                currentPartRefsFromElements.add(key);
              }
              return filteredItem;
            });
            addTextBoxEntries(filteredTextBoxData);
          }
        });

        // Add original entries from page data (for edit mode, these may include replaced part refs)
        // CRITICAL: Add these AFTER processing current elements so we can detect replacements
        // The original entries will be added to textBoxContentData, and then we'll mark replaced ones with qty 0
        if (
          page.text_box_content_data &&
          Array.isArray(page.text_box_content_data)
        ) {
          addTextBoxEntries(page.text_box_content_data);
        } else if (Array.isArray(page.text_boxes)) {
          const flattened = page.text_boxes.flatMap((textBox) =>
            (textBox?.text_box_content || []).map((content) => ({
              ...content,
              text_box_id: textBox?.text_box_id,
            })),
          );
          addTextBoxEntries(flattened);
        }

        // Set qty to 0 for removed/replaced part references (edit mode only)
        // Simply modify existing entries in textBoxContentData - don't add new ones
        // Also collect text_box_content_id for replaced/removed part refs to include in remove_text_box_content_ids
        const replacedPartRefContentIds = new Set(); // Track text_box_content_id for replaced/removed part refs
        if (mode === "edit") {
          // CRITICAL: Process ALL entries in textBoxContentData and check if they should have qty 0
          // This ensures we catch entries that were added from page data with original qty
          // This is the PRIMARY method - iterate through all entries and mark replaced ones
          textBoxContentData.forEach((entry) => {
            if (entry.part_ref) {
              // Check if this entry's part_ref exists in original but not in current elements
              const key = `${entry.part_ref}_${entry.text_box_id || ""}`;
              const isInOriginal = originalPartRefs.has(key);
              const isInCurrent = currentPartRefsFromElements.has(key);

              // If it's in original but not in current, it was replaced/removed
              if (isInOriginal && !isInCurrent) {
                // Set qty to 0
                entry.qty = 0;
                // Collect text_box_content_id if available
                if (entry.text_box_content_id) {
                  replacedPartRefContentIds.add(entry.text_box_content_id);
                } else {
                  // If entry doesn't have text_box_content_id, try to get it from originalPartRefs
                  const originalEntry = originalPartRefs.get(key);
                  if (originalEntry?.text_box_content_id) {
                    entry.text_box_content_id =
                      originalEntry.text_box_content_id;
                    replacedPartRefContentIds.add(
                      originalEntry.text_box_content_id,
                    );
                  }
                }
              }
            }
          });

          // Also process originalPartRefs to catch any that might not be in textBoxContentData yet
          // Find part refs that existed in original but don't exist in current elements (replaced/removed)
          originalPartRefs.forEach((originalEntry, key) => {
            if (!currentPartRefsFromElements.has(key)) {
              // This part ref was replaced or removed
              // CRITICAL: Always collect text_box_content_id first, even if we don't find the entry
              // This ensures we include it in remove_text_box_content_ids
              const contentId = originalEntry.text_box_content_id;
              if (contentId) {
                replacedPartRefContentIds.add(contentId);
              }

              // First, try to find it by text_box_content_id (most reliable)
              let existingEntry = null;
              if (contentId) {
                existingEntry = textBoxContentData.find(
                  (entry) => entry.text_box_content_id === contentId,
                );
              }

              // If not found by text_box_content_id, try to find by part_ref + text_box_id
              // Use more flexible matching to handle cases where text_box_id might be missing
              if (!existingEntry) {
                existingEntry = textBoxContentData.find((entry) => {
                  const partRefMatch =
                    entry.part_ref === originalEntry.part_ref;
                  if (!partRefMatch) return false;

                  // Match text_box_id if both exist, or if both are missing/null
                  const textBoxIdMatch =
                    (entry.text_box_id &&
                      originalEntry.text_box_id &&
                      entry.text_box_id === originalEntry.text_box_id) ||
                    (!entry.text_box_id && !originalEntry.text_box_id);

                  return textBoxIdMatch;
                });
              }

              if (existingEntry) {
                // Set qty to 0 for the replaced/removed part ref
                existingEntry.qty = 0;
                // Ensure text_box_content_id is preserved if it exists in original
                if (contentId && !existingEntry.text_box_content_id) {
                  existingEntry.text_box_content_id = contentId;
                }
              } else {
                // If entry doesn't exist in textBoxContentData, we need to add it with qty 0
                // This handles cases where the entry wasn't added due to deduplication or other issues
                // But we only add if it's not already being handled by removedPartReferences
                const isInRemovedPartRefs =
                  removedIds.removedPartReferences?.some((removed) => {
                    const partRefMatch =
                      removed.part_ref === originalEntry.part_ref;
                    if (!partRefMatch) return false;

                    const textBoxIdMatch =
                      (removed.text_box_id &&
                        originalEntry.text_box_id &&
                        removed.text_box_id === originalEntry.text_box_id) ||
                      (!removed.text_box_id && !originalEntry.text_box_id);

                    return textBoxIdMatch;
                  });

                if (!isInRemovedPartRefs) {
                  // Add entry with qty 0 to ensure the backend knows this part ref was removed
                  // CRITICAL: Include text_box_content_id so it can be added to remove_text_box_content_ids
                  const entryToAdd = {
                    part_ref: originalEntry.part_ref,
                    text_box_id: originalEntry.text_box_id || null,
                    qty: 0,
                    units: originalEntry.units || null,
                    inline_note: originalEntry.inline_note || null,
                    text_box_content_id: contentId || null,
                  };
                  textBoxContentData.push(entryToAdd);
                  // Also add to dedup set to prevent duplicates
                  if (entryToAdd.text_box_content_id) {
                    textBoxContentDedup.add(entryToAdd.text_box_content_id);
                  } else {
                    const dedupKey = `${entryToAdd.text_box_id || "new"}-${
                      entryToAdd.part_ref || ""
                    }-${entryToAdd.inline_note || ""}-${entryToAdd.qty ?? 0}-${
                      entryToAdd.units || ""
                    }`;
                    textBoxContentDedup.add(dedupKey);
                  }
                }
              }
            }
          });

          // Handle removed text boxes - set qty to 0 for all part refs in removed text boxes
          // removedIds.textIds contains text element IDs, not text_box_ids
            // Since removed text elements are no longer in allElements.texts, we need to use page data
          if (removedIds.textIds && removedIds.textIds.length > 0) {
            const removedTextElementIds = new Set(removedIds.textIds);
            const removedTextBoxIds = new Set();

            // Method 1: Check page.text_boxes to find text boxes that contain removed content
            if (page.text_boxes && Array.isArray(page.text_boxes)) {
              page.text_boxes.forEach((textBox) => {
                // Check if any content in this text box has text_box_content_id matching removed text element IDs
                const textBoxContent = textBox.text_box_content || [];
                const hasRemovedContent = textBoxContent.some((content) => {
                  // The text_box_content_id might match, or we need to check differently
                  // Actually, text_box_content_id is different from text element ID
                  // Let's try a different approach - check if the text box itself was removed
                  return removedTextElementIds.has(content.text_box_content_id);
                });

                if (hasRemovedContent && textBox.text_box_id) {
                  removedTextBoxIds.add(textBox.text_box_id);
                }
              });
            }

            // Method 2: Check page.text_box_content_data directly
            if (
              page.text_box_content_data &&
              Array.isArray(page.text_box_content_data)
            ) {
              page.text_box_content_data.forEach((entry) => {
                // If the text_box_content_id matches a removed text element ID, mark its text_box_id
                if (
                  entry.text_box_content_id &&
                  removedTextElementIds.has(entry.text_box_content_id)
                ) {
                  if (entry.text_box_id) {
                    removedTextBoxIds.add(entry.text_box_id);
                  }
                }
              });
            }

            // Method 3: If we have removed text element IDs, check which text_box_ids
            // don't have corresponding text elements anymore - those text boxes were removed
            const existingTextBoxIds = new Set();
            (allElements.texts || []).forEach((text) => {
              const textBoxId =
                text.text_box_id ||
                text.textboxId ||
                text.textBoxId ||
                text.canvas_text_box_id ||
                text.canvasTextBoxId;
              if (textBoxId) {
                existingTextBoxIds.add(textBoxId);
              }
            });

            // If an entry's text_box_id is not in existing text elements AND we have removed text IDs,
            // that text box was removed - set qty to 0 for all its entries
            textBoxContentData.forEach((entry) => {
              if (
                entry.text_box_id &&
                !existingTextBoxIds.has(entry.text_box_id)
              ) {
                // This text_box_id doesn't have a corresponding text element, so it was removed
                removedTextBoxIds.add(entry.text_box_id);
              }
            });

            // Now set qty to 0 for all entries with these text_box_ids
            removedTextBoxIds.forEach((textBoxId) => {
              textBoxContentData.forEach((entry) => {
                if (entry.text_box_id === textBoxId) {
                  entry.qty = 0;
                }
              });
            });
          }

          // Then, handle individually removed part references (explicitly deleted, not replaced)
          if (
            removedIds.removedPartReferences &&
            removedIds.removedPartReferences.length > 0
          ) {
            removedIds.removedPartReferences.forEach((removedPart) => {
              if (!removedPart.part_ref) return;

              // Find the existing entry in textBoxContentData that matches this part_ref and text_box_id
              const existingEntry = textBoxContentData.find(
                (entry) =>
                  entry.part_ref === removedPart.part_ref &&
                  (entry.text_box_id === removedPart.text_box_id ||
                    (!entry.text_box_id && !removedPart.text_box_id)),
              );

              // Collect text_box_content_id for this explicitly removed part ref
              const contentId =
                removedPart.text_box_content_id ||
                existingEntry?.text_box_content_id;
              if (contentId) {
                replacedPartRefContentIds.add(contentId);
              }

              if (existingEntry) {
                // Simply set qty to 0 for the existing entry
                existingEntry.qty = 0;
              }
            });
          }

          // Add all replaced/removed part ref content IDs from this page to the global set
          replacedPartRefContentIds.forEach((id) => {
            allReplacedPartRefContentIds.add(id);
          });
        }

        // Helper function to filter out base64 fields from objects
        const filterBase64Fields = (obj) => {
          if (!obj || typeof obj !== "object") return obj;
          const filtered = { ...obj };
          const base64Fields = [
            "src",
            "imageDataUrl",
            "originalImageDataUrl",
            "uncompressedCroppedImageDataUrl",
            "imgDataUrl",
            "compressedImageDataUrl",
          ];
          for (const field of base64Fields) {
            if (
              filtered[field] &&
              typeof filtered[field] === "string" &&
              (filtered[field].startsWith("data:image/") ||
                filtered[field].startsWith("blob:"))
            ) {
              delete filtered[field];
            }
          }
          return filtered;
        };

        // Helper function to flatten nested image_properties
        const flattenImageProperties = (imgObj) => {
          const { image_properties, shape_info, ...rest } = imgObj;
          let merged = { ...rest };

          // Flatten shape_info if it exists
          if (shape_info && typeof shape_info === "object") {
            const nestedShapeInfoImageProps = shape_info.image_properties;
            if (
              nestedShapeInfoImageProps &&
              typeof nestedShapeInfoImageProps === "object"
            ) {
              merged = {
                ...merged,
                ...shape_info,
                ...nestedShapeInfoImageProps,
              };
            } else {
              merged = { ...merged, ...shape_info };
            }
          }

          // Flatten image_properties if it exists
          if (image_properties && typeof image_properties === "object") {
            const nestedImageProps = image_properties.image_properties;
            if (nestedImageProps && typeof nestedImageProps === "object") {
              merged = { ...merged, ...image_properties, ...nestedImageProps };
            } else {
              merged = { ...merged, ...image_properties };
            }
          }

          return merged;
        };

        // Process images in parallel for better performance
        const imageProcessingPromises = (allElements.images || []).map(
          async (img) => {
            try {
              // Check if this image already has an S3 URL (from template)
              if (img.image_path && img.image_path.startsWith("http")) {
                // Use existing S3 URL
                const s3Url = img.image_path;

                // Preserve exact position data
                const exactX = img.x || img.image_properties?.x || 0;
                const exactY = img.y || img.image_properties?.y || 0;
                const exactWidth =
                  img.width || img.image_properties?.width || 100;
                const exactHeight =
                  img.height || img.image_properties?.height || 100;

                // Clean up img object to prevent nested duplication
                const {
                  image_properties,
                  shape_info,
                  image_id,
                  image_path: img_path,
                  shape_properties,
                  shapeData: imgShapeData,
                  imageData: imgData,
                  ...cleanImg
                } = img;

                // Filter out any base64 fields from cleanImg
                const filteredCleanImg = filterBase64Fields(cleanImg);

                // Flatten nested image_properties
                const flattenedCleanImg =
                  flattenImageProperties(filteredCleanImg);
                const resolvedIsOnTopLayer =
                  flattenedCleanImg.isOnTopLayer ??
                  img.isOnTopLayer ??
                  img.image_properties?.isOnTopLayer ??
                  img.shape_info?.isOnTopLayer ??
                  false;

                // Check if image_id contains "marker"
                const isMarkerImage = img.id && img.id.includes("marker");

                // Check if this image comes from a template
                // Support both flag names for compatibility: isFromTemplate and fromCanvasTemplate
                const isFromTemplate =
                  img.isFromTemplate === true ||
                  img.fromCanvasTemplate === true ||
                  page.fromCanvasTemplate === true;

                const isOffCanvasImage =
                  img.isOffCanvas === true ||
                  img.is_off_canvas === true ||
                  img.isOffcanvas === true;
                const imageDataObj = {
                  image_path: s3Url,
                  image_properties: {
                    id: flattenedCleanImg.id || img.id,
                    x: exactX,
                    y: exactY,
                    width: exactWidth,
                    height: exactHeight,
                    scaleX: Math.abs(
                      flattenedCleanImg.scaleX ||
                        img.scaleX ||
                        img.image_properties?.scaleX ||
                        1,
                    ),
                    scaleY: Math.abs(
                      flattenedCleanImg.scaleY ||
                        img.scaleY ||
                        img.image_properties?.scaleY ||
                        1,
                    ),
                    rotation:
                      flattenedCleanImg.rotation ||
                      img.rotation ||
                      img.image_properties?.rotation ||
                      0,
                    src: s3Url,
                    cropData: flattenedCleanImg.cropData || img.cropData,
                    imageDataUrl: s3Url,
                    originalImageDataUrl: s3Url,
                    flipHorizontal:
                      flattenedCleanImg.flipHorizontal ||
                      img.flipHorizontal ||
                      false,
                    flipVertical:
                      flattenedCleanImg.flipVertical ||
                      img.flipVertical ||
                      false,
                    hasBakedTransforms: false,
                    originalCropRotation:
                      flattenedCleanImg.originalCropRotation ||
                      img.originalCropRotation ||
                      0,
                    maintainAspectRatio:
                      flattenedCleanImg.maintainAspectRatio ||
                      img.maintainAspectRatio,
                    compress: flattenedCleanImg.compress || img.compress,
                    originalWidth:
                      flattenedCleanImg.originalWidth || img.originalWidth,
                    originalHeight:
                      flattenedCleanImg.originalHeight || img.originalHeight,
                    name:
                      flattenedCleanImg.name ||
                      img.name ||
                      img.image_properties?.name ||
                      "Picture 1",
                    referenceUrl:
                      flattenedCleanImg.referenceUrl ||
                      img.referenceUrl ||
                      img.image_properties?.referenceUrl ||
                      "",
                    isOnTopLayer: resolvedIsOnTopLayer,
                    ...(isOffCanvasImage ? { isOffcanvas: true } : {}),
                  },
                };

                // Only include image_id if it doesn't contain "marker" and is NOT from template
                if (!isMarkerImage && (!isFromTemplate || isApiPage)) {
                  imageDataObj.image_id = img.id;
                }

                const shapeDataObj = {
                  height: parseInt(exactHeight) || 0,
                  width: parseInt(exactWidth) || 0,
                  shape_fill: 0,
                  shape_color: 0,
                  stroke_color: 0,
                  stroke_size: 0,
                  shape_info: {
                    id: flattenedCleanImg.id || img.id,
                    src: s3Url,
                    x: exactX,
                    y: exactY,
                    width: exactWidth,
                    height: exactHeight,
                    scaleX: Math.abs(
                      flattenedCleanImg.scaleX ||
                        img.scaleX ||
                        img.image_properties?.scaleX ||
                        1,
                    ),
                    scaleY: Math.abs(
                      flattenedCleanImg.scaleY ||
                        img.scaleY ||
                        img.image_properties?.scaleY ||
                        1,
                    ),
                    rotation:
                      flattenedCleanImg.rotation ||
                      img.rotation ||
                      img.image_properties?.rotation ||
                      0,
                    cropData: flattenedCleanImg.cropData || img.cropData,
                    imageDataUrl: s3Url,
                    flipHorizontal:
                      flattenedCleanImg.flipHorizontal ||
                      img.flipHorizontal ||
                      false,
                    flipVertical:
                      flattenedCleanImg.flipVertical ||
                      img.flipVertical ||
                      false,
                    isOnTopLayer: resolvedIsOnTopLayer,
                    ...(isOffCanvasImage ? { isOffcanvas: true } : {}),
                  },
                  shape_properties: {
                    id: flattenedCleanImg.id || img.id,
                    src: s3Url,
                    x: exactX,
                    y: exactY,
                    width: exactWidth,
                    height: exactHeight,
                    scaleX: Math.abs(
                      flattenedCleanImg.scaleX ||
                        img.scaleX ||
                        img.image_properties?.scaleX ||
                        1,
                    ),
                    scaleY: Math.abs(
                      flattenedCleanImg.scaleY ||
                        img.scaleY ||
                        img.image_properties?.scaleY ||
                        1,
                    ),
                    rotation:
                      flattenedCleanImg.rotation ||
                      img.rotation ||
                      img.image_properties?.rotation ||
                      0,
                    cropData: flattenedCleanImg.cropData || img.cropData,
                    imageDataUrl: s3Url,
                    flipHorizontal:
                      flattenedCleanImg.flipHorizontal ||
                      img.flipHorizontal ||
                      false,
                    flipVertical:
                      flattenedCleanImg.flipVertical ||
                      img.flipVertical ||
                      false,
                    isOnTopLayer: resolvedIsOnTopLayer,
                    ...(isOffCanvasImage ? { isOffcanvas: true } : {}),
                  },
                  type: "Image",
                };

                // Only add shape_id at top level if it doesn't contain "marker" and is NOT from template
                // shape_id should NOT be inside shape_info or shape_properties
                if (!isMarkerImage && (!isFromTemplate || isApiPage)) {
                  shapeDataObj.shape_id = img.id;
                }

                return {
                  imageData: imageDataObj,
                  shapeData: shapeDataObj,
                };
              }

              // Need to upload to S3
              const timestamp = new Date().getTime();
              const fileName = `canvas-image-${img.id}-${timestamp}.png`;
              const fileType = "image/png";

              let dataToUpload;
              if (img.originalBlob instanceof Blob) {
                dataToUpload = img.originalBlob;
              } else {
                const base64Candidates = [
                  img.originalImageDataUrl,
                  img.uncompressedCroppedImageDataUrl,
                  img.imgDataUrl,
                  img.imageDataUrl,
                  img.src,
                ];

                let base64Data = null;
                for (const candidate of base64Candidates) {
                  if (
                    typeof candidate === "string" &&
                    (candidate.startsWith("data:image/") ||
                      candidate.startsWith("blob:"))
                  ) {
                    base64Data = candidate;
                    break;
                  }
                }

                if (!base64Data) {
                  return null;
                }

                dataToUpload = base64Data;
              }

              const s3Url = await uploadToS3(dataToUpload, fileName, fileType);

              // Clean up img object
              const {
                image_properties,
                shape_info,
                image_id,
                image_path: img_path,
                shape_properties,
                shapeData: imgShapeData,
                imageData: imgData,
                ...cleanImg
              } = img;

              const filteredCleanImg = filterBase64Fields(cleanImg);
              const flattenedCleanImg =
                flattenImageProperties(filteredCleanImg);
              const resolvedIsOnTopLayer =
                flattenedCleanImg.isOnTopLayer ??
                img.isOnTopLayer ??
                img.image_properties?.isOnTopLayer ??
                img.shape_info?.isOnTopLayer ??
                false;

              // Check if image_id contains "marker"
              const isMarkerImage = img.id && img.id.includes("marker");

              // Check if this image comes from a template
              // Support both flag names for compatibility: isFromTemplate and fromCanvasTemplate
              const isFromTemplate =
                img.isFromTemplate === true ||
                img.fromCanvasTemplate === true ||
                page.fromCanvasTemplate === true;

              const isOffCanvasImage =
                img.isOffCanvas === true ||
                img.is_off_canvas === true ||
                img.isOffcanvas === true;
              const imageDataObj = {
                image_path: s3Url,
                image_properties: {
                  id: flattenedCleanImg.id || img.id,
                  x: img.x || 0,
                  y: img.y || 0,
                  width: img.width || 100,
                  height: img.height || 100,
                  scaleX: Math.abs(flattenedCleanImg.scaleX || img.scaleX || 1),
                  scaleY: Math.abs(flattenedCleanImg.scaleY || img.scaleY || 1),
                  rotation: flattenedCleanImg.rotation || img.rotation || 0,
                  src: s3Url,
                  cropData: flattenedCleanImg.cropData || img.cropData,
                  imageDataUrl: s3Url,
                  originalImageDataUrl: s3Url,
                  flipHorizontal:
                    flattenedCleanImg.flipHorizontal ||
                    img.flipHorizontal ||
                    false,
                  flipVertical:
                    flattenedCleanImg.flipVertical || img.flipVertical || false,
                  hasBakedTransforms: false,
                  originalCropRotation:
                    flattenedCleanImg.originalCropRotation ||
                    img.originalCropRotation ||
                    0,
                  maintainAspectRatio:
                    flattenedCleanImg.maintainAspectRatio ||
                    img.maintainAspectRatio,
                  compress: flattenedCleanImg.compress || img.compress,
                  originalWidth:
                    flattenedCleanImg.originalWidth || img.originalWidth,
                  originalHeight:
                    flattenedCleanImg.originalHeight || img.originalHeight,
                  name:
                    flattenedCleanImg.name ||
                    img.name ||
                    img.image_properties?.name ||
                    "Picture 1",
                  referenceUrl:
                    flattenedCleanImg.referenceUrl ||
                    img.referenceUrl ||
                    img.image_properties?.referenceUrl ||
                    "",
                  isOnTopLayer: resolvedIsOnTopLayer,
                  ...(isOffCanvasImage ? { isOffcanvas: true } : {}),
                },
              };

              // Only include image_id if it doesn't contain "marker" and is NOT from template
              if (!isMarkerImage && !isFromTemplate) {
                imageDataObj.image_id = img.id;
              }

              const shapeDataObj = {
                height: parseInt(img.height) || 0,
                width: parseInt(img.width) || 0,
                shape_fill: 0,
                shape_color: 0,
                stroke_color: 0,
                stroke_size: 0,
                shape_info: {
                  id: flattenedCleanImg.id || img.id,
                  src: s3Url,
                  x: img.x || 0,
                  y: img.y || 0,
                  width: img.width || 100,
                  height: img.height || 100,
                  scaleX: Math.abs(flattenedCleanImg.scaleX || img.scaleX || 1),
                  scaleY: Math.abs(flattenedCleanImg.scaleY || img.scaleY || 1),
                  rotation: flattenedCleanImg.rotation || img.rotation || 0,
                  cropData: flattenedCleanImg.cropData || img.cropData,
                  imageDataUrl: s3Url,
                  flipHorizontal:
                    flattenedCleanImg.flipHorizontal ||
                    img.flipHorizontal ||
                    false,
                  flipVertical:
                    flattenedCleanImg.flipVertical || img.flipVertical || false,
                  isOnTopLayer: resolvedIsOnTopLayer,
                  ...(isOffCanvasImage ? { isOffcanvas: true } : {}),
                },
                shape_properties: {
                  id: flattenedCleanImg.id || img.id,
                  src: s3Url,
                  x: img.x || 0,
                  y: img.y || 0,
                  width: img.width || 100,
                  height: img.height || 100,
                  scaleX: Math.abs(flattenedCleanImg.scaleX || img.scaleX || 1),
                  scaleY: Math.abs(flattenedCleanImg.scaleY || img.scaleY || 1),
                  rotation: flattenedCleanImg.rotation || img.rotation || 0,
                  cropData: flattenedCleanImg.cropData || img.cropData,
                  imageDataUrl: s3Url,
                  flipHorizontal:
                    flattenedCleanImg.flipHorizontal ||
                    img.flipHorizontal ||
                    false,
                  flipVertical:
                    flattenedCleanImg.flipVertical || img.flipVertical || false,
                  isOnTopLayer: resolvedIsOnTopLayer,
                  ...(isOffCanvasImage ? { isOffcanvas: true } : {}),
                },
                type: "Image",
              };

              // Only add shape_id at top level if it doesn't contain "marker" and is NOT from template
              // shape_id should NOT be inside shape_info or shape_properties
              if (!isMarkerImage && !isFromTemplate) {
                shapeDataObj.shape_id = img.id;
              }

              return {
                imageData: imageDataObj,
                shapeData: shapeDataObj,
              };
            } catch (error) {
              console.error(`Failed to upload image ${img.id} to S3:`, error);
              return null;
            }
          },
        );

        // Wait for all image processing to complete
        setSaveProgress({ step: "Uploading images...", percentage: 30 });
        const imageResults = await Promise.all(imageProcessingPromises);

        // Add processed images to arrays
        imageResults.forEach((result) => {
          if (result) {
            imageData.push(result.imageData);
            shapeData.push(result.shapeData);
          }
        });

        const layoutConfig = generateLayoutConfig(allElements);

        // Build text_box_data array - each text element from IndexedDB texts array becomes one entry
        // Each entry contains text_box_properties and text_box_content_data for that text element
        const textBoxData = [];
        const textBoxContentDataByTextBoxId = new Map();

        // Group text_box_content_data by text_box_id for easy lookup
        textBoxContentData.forEach((entry) => {
          const textBoxId = entry.text_box_id || null;
          if (textBoxId) {
            if (!textBoxContentDataByTextBoxId.has(textBoxId)) {
              textBoxContentDataByTextBoxId.set(textBoxId, []);
            }
            textBoxContentDataByTextBoxId.get(textBoxId).push(entry);
          }
        });

        // Create text_box_data entries from text elements (texts array from IndexedDB)
        (allElements.texts || []).forEach((text) => {
          const {
            text_box_properties,
            text_box_content_data: textContentDataFromConvert,
          } = convertTextToPayload(text);

          // Get text_box_id from the text element
          const textBoxId =
            text.text_box_id ||
            text.textboxId ||
            text.textBoxId ||
            text.canvas_text_box_id ||
            text.canvasTextBoxId ||
            null;

          // Get text_box_content_data for this text_box_id
          // Priority: 1) Grouped data from textBoxContentData, 2) Data from convertTextToPayload
          let contentDataForThisTextBox = [];
          if (textBoxId && textBoxContentDataByTextBoxId.has(textBoxId)) {
            contentDataForThisTextBox =
              textBoxContentDataByTextBoxId.get(textBoxId);
          } else if (
            textContentDataFromConvert &&
            textContentDataFromConvert.length > 0
          ) {
            contentDataForThisTextBox = textContentDataFromConvert;
          }
          // Fallback 1: use raw per-text content if grouped + convert paths are empty
          if (
            (!contentDataForThisTextBox || contentDataForThisTextBox.length === 0) &&
            Array.isArray(text?.text_box_content_data) &&
            text.text_box_content_data.length > 0
          ) {
            contentDataForThisTextBox = text.text_box_content_data;
          }
          // Fallback 2: synthesize minimal row from selected content part_ref
          if (
            (!contentDataForThisTextBox || contentDataForThisTextBox.length === 0) &&
            text?.selectedContent?.part_ref
          ) {
            contentDataForThisTextBox = [
              {
                part_ref: text.selectedContent.part_ref,
                qty: parseInt(text?.selectedContent?.qty) || 0,
                inline_note:
                  text?.selectedContent?.notePlainText ||
                  text?.selectedContent?.note ||
                  "",
                ...(text?.selectedContent?.units
                  ? { units: text.selectedContent.units }
                  : {}),
                ...(textBoxId ? { text_box_id: textBoxId } : {}),
              },
            ];
          }
          // Fallback 3: derive directly from partsByType (covers replace flows where
          // selectedContent/text_box_content_data may be missing in persisted text)
          if (
            (!contentDataForThisTextBox || contentDataForThisTextBox.length === 0) &&
            text?.partsByType &&
            typeof text.partsByType === "object"
          ) {
            const derivedFromPartsByType = [];
            Object.values(text.partsByType).forEach((group) => {
              const parts = Array.isArray(group?.parts) ? group.parts : [];
              parts.forEach((partItem) => {
                if (!partItem?.part_ref) return;
                derivedFromPartsByType.push({
                  part_ref: partItem.part_ref,
                  qty: parseInt(partItem.qty) || 0,
                  inline_note: partItem.notePlainText || partItem.note || "",
                  ...(partItem.units ? { units: partItem.units } : {}),
                  ...(textBoxId ? { text_box_id: textBoxId } : {}),
                });
              });
            });
            if (derivedFromPartsByType.length > 0) {
              contentDataForThisTextBox = derivedFromPartsByType;
            }
          }
          if (
            (!contentDataForThisTextBox || contentDataForThisTextBox.length === 0) &&
            (text?.partsByType || text?.text_box_content_data || text?.selectedContent?.part_ref)
          ) {
          }

          // Create text_box_data entry for this text element
          textBoxData.push({
            text_box_properties: text_box_properties || {},
            text_box_content_data: contentDataForThisTextBox,
          });
        });

        // Also handle any text_box_content_data entries that don't have a corresponding text element
        // (e.g., from page data that was added but the text element was removed in edit mode)
        const processedTextBoxIds = new Set();
        (allElements.texts || []).forEach((text) => {
          const textBoxId =
            text.text_box_id ||
            text.textboxId ||
            text.textBoxId ||
            text.canvas_text_box_id ||
            text.canvasTextBoxId;
          if (textBoxId) {
            processedTextBoxIds.add(textBoxId);
          }
        });

        // Add entries for text_box_content_data that don't have a corresponding text element
        // This handles cases in edit mode where content data exists but the text element was removed
        textBoxContentDataByTextBoxId.forEach((contentData, textBoxId) => {
          if (
            textBoxId &&
            !processedTextBoxIds.has(textBoxId) &&
            contentData.length > 0
          ) {
            // This text_box_id doesn't have a corresponding text element
            // Create a text_box_data entry with empty text_box_properties
            textBoxData.push({
              text_box_properties: {},
              text_box_content_data: contentData,
            });
          }
        });

        const textShapeCount = shapeData.filter(
          (s) => s.type === "Text",
        ).length;

        const pageData = {
          layout_config: layoutConfig,
          shape_data: shapeData,
          image_data: imageData,
          text_box_data: textBoxData,
        };

        // In edit mode, use canvas_page_id for existing pages, don't send page_id for new pages
        // In add mode, don't send page_id
        if (mode === "edit" && canvas_page_id) {
          // Only set canvas_page_id for existing pages from API (they have canvas_page_id)
          // Use the local canvas_page_id variable which was retrieved from canvasData if missing from page
          pageData.canvas_page_id = canvas_page_id;
        }
        // Note: We intentionally don't send page_id for newly added pages in edit mode or any pages in add mode

        pagesData.push(pageData);
      }

      setSaveProgress({ step: "Saving to server...", percentage: 50 });

      // Collect all unique part IDs from text_box_content_data across all pages
      const allPartIds = new Set();
      pagesData.forEach((page) => {
        if (page.text_box_data) {
          page.text_box_data.forEach((textBox) => {
            if (textBox.text_box_content_data) {
              textBox.text_box_content_data.forEach((content) => {
                if (content.part_ref) {
                  allPartIds.add(content.part_ref);
                }
              });
            }
          });
        }
      });

      // Retrieve part details from IndexedDB for all collected part IDs
      const pastCanvasDataJson = {};
      if (allPartIds.size > 0) {
        for (const partId of allPartIds) {
          try {
            const partDetailsResult = await loadFromIndexedDB(
              "partDetails",
              partId,
            );
            const partDetails = partDetailsResult?.data || partDetailsResult;

            if (partDetails) {
              // Extract only required fields from IndexedDB data
              const filteredPartData = {
                part_ref: partDetails.part_id,
                part_number: partDetails.part_number,
                textbox_note: partDetails.textbox_note,
                description: partDetails.description,
                type_name:
                  partDetails.type_details?.name || partDetails.type_name,
                type_info: {
                  order: partDetails.type_details?.orders,
                },
              };
              pastCanvasDataJson[partId] = filteredPartData;
            } else {
              try {
                const result = await dispatch(getPartById(partId)).unwrap();
                if (result) {
                  // Extract only the 'results' key from the API response
                  const partData = result.results || result;
                  // Extract only required fields from API data
                  const filteredPartData = {
                    part_ref: partData.part_id,
                    part_number: partData.part_number,
                    textbox_note: partData.textbox_note,
                    description: partData.description,
                    type_name:
                      partData.type_details?.name || partData.type_name,
                    type_info: {
                      order: partData.type_details?.orders,
                    },
                  };
                  pastCanvasDataJson[partId] = filteredPartData;
                  // Save to IndexedDB for next time - use the filtered part data
                  await saveToIndexedDB(
                    "partDetails",
                    partId,
                    filteredPartData,
                  );
                }
              } catch (fetchError) {
                console.error(
                  `❌ Failed to fetch details for part_id ${partId}:`,
                  fetchError,
                );
              }
            }
          } catch (error) {
            console.error(
              `❌ Failed to retrieve details for part_id ${partId}:`,
              error,
            );
          }
        }
      }

      // Upload pages array as JSON file to S3
      let canvasJsonDataFileUrl = null;
      try {
        setSaveProgress({ step: "Uploading pages JSON...", percentage: 85 });

        // Convert pagesData to JSON string
        const pagesJsonString = JSON.stringify(pagesData, null, 2);

        // Create a base64 data URL from the JSON string
        const base64Json = btoa(unescape(encodeURIComponent(pagesJsonString)));
        const dataUrl = `data:application/json;base64,${base64Json}`;

        // Generate unique filename for JSON upload
        const timestamp = new Date().getTime();
        const fileName = `canvas-pages-${timestamp}.json`;
        const fileType = "application/json";

        // Upload to S3
        canvasJsonDataFileUrl = await uploadToS3(dataUrl, fileName, fileType);
      } catch (jsonUploadError) {
        console.error("❌ Error uploading pages JSON to S3:", jsonUploadError);
        showSnackbar(
          "Error uploading pages JSON to S3: " +
            (jsonUploadError?.message || "Unknown error"),
          "error",
        );
        setIsLoading(false);
        return;
      }

      const canvasdt = {
        name: pageTitle,
        short_description: shortDescription,
        pages: pagesData,
        revision_id_ref: revisionId,
        section_id_ref: sectionId,
        part_required_id_ref: partId,
        substep_id_ref: substepId === "null" ? "" : substepId,
        step_id_ref: stepId,
        sibling_order: siblingOrder?.toString() || "1",
        //Make it array of distinct values
        canvas_json_data_file: canvasJsonDataFileUrl,
      };

      let result;
      if (mode === "edit") {
        // removedIds was already retrieved earlier before processing pages

        // Edit mode - add procedure_tree_ref (required for update)
        // Get procedure_tree_ref from canvasData or use procedureId from hook
        canvasdt.procedure_tree_ref =
          canvasData?.procedure_tree_ref ||
          canvasData?.detail?.procedure_tree_ref ||
          procedureId ||
          canvasData?.procedure_tree_id;

        // Add removal tracking fields
        // Merge removedIds.textContentIds with replaced/removed part ref content IDs
        // Deduplicate by converting to Set and back to array
        const allRemovedTextContentIds = [
          ...new Set([
            ...(removedIds.textContentIds || []),
            ...Array.from(allReplacedPartRefContentIds),
          ]),
        ].filter((id) => id && String(id).trim() !== ""); // Remove empty/null values

        canvasdt.remove_canvas_pages = removedCanvasPages || [];
        canvasdt.remove_shape_ids = removedIds.shapeIds || [];
        canvasdt.remove_text_box_content_ids = allRemovedTextContentIds;
        canvasdt.remove_text_box_ids = removedIds.textIds || [];
        canvasdt.remove_image_ids = removedIds.imageIds || [];

        // Edit mode - implement update if needed
        const audit_trail_data = await getAuditTrailData({
          action: "UPDATE_CANVAS",
          comment: comment,
          parent_version: versionId,
          version_content: {
            action_to_show: `Edit ${pageTitle} for ${partnumber}, Rev ${revision}`,
            partNumber: partnumber,
            revision,
            status:currentStatus,
            item_code_type,
            type: "canvas",
          },
        });

        canvasdt.audit_trail_data = audit_trail_data;

        // Update the canvas
        await dispatch(
          updateCanvas({
            data: canvasdt,
            id,
            allNodesData,
            partnumber,
            revision,
          }),
        ).unwrap();

        showSnackbar("Canvas updated successfully!", "success");

        // Clear local storage and session storage
        localStorage.removeItem("canvasPages");
        localStorage.removeItem("canvasSnapshot");
        localStorage.removeItem("removedCanvasPages"); // Clear removed pages tracking
        sessionStorage.removeItem("canvasStateInfo");

        // CRITICAL: Clear persistent unsaved changes flag when saving
        // This ensures the flag is cleared for all pages of this canvas
        if (id) {
          // Clear flag for this canvas (persists across MyCanvas visits)
          const persistentFlagKey = `canvasHasUnsavedChanges_${id}`;
          localStorage.removeItem(persistentFlagKey);
        }
        // Also clear fromMyCanvas flag and currentCanvasId
        localStorage.removeItem("fromMyCanvas");
        localStorage.removeItem("currentCanvasId");

        // CRITICAL: Clear persistent unsaved changes flag when saving
        // This ensures the flag is cleared for all pages of this canvas
        if (id) {
          const persistentFlagKey = `canvasHasUnsavedChanges_${id}`;
          localStorage.removeItem(persistentFlagKey);
        }
        // Also clear fromMyCanvas flag
        localStorage.removeItem("fromMyCanvas");

        setHasUnsavedChanges(false);
        setRemovedCanvasPages([]); // Reset removed pages state

        // Clear IndexedDB for each page (like EditCanvasPage)
        const dbName = "canvasElementsDB";
        try {
          const currentVersion = await getCurrentIndexedDBVersion(dbName);
          const request = indexedDB.open(dbName, currentVersion);
          request.onsuccess = (event) => {
            const db = event.target.result;
            const transaction = db.transaction(["canvasElements"], "readwrite");
            const store = transaction.objectStore("canvasElements");

            for (const page of canvasPages) {
              const pageId = page.id || page.pageId;
              if (pageId) {
                store.delete(pageId);
                // Also delete the removed shape IDs for this page
                store.delete(`${pageId}_removed_shapes`);
              }
            }

            db.close();
          };
        } catch (dbError) {
          console.error("Error clearing IndexedDB:", dbError);
          // Continue with navigation even if IndexedDB cleanup fails
        }

        setIsLoading(false);

        // CRITICAL: Clear the initial API pages baseline after successful save
        // This ensures the baseline is refreshed on next edit
        const storageKey = `initialApiPages_${id}`;
        localStorage.removeItem(storageKey);
        initialApiPagesRef.current = null;

        // Navigate to view-childnode after successful update
        // In edit mode, id is the canvas tree node ID
        const treeNodeId =
          id ||
          node_id_ref ||
          canvasData?.canvas_tree_node_id ||
          canvasData?.detail?.canvas_tree_node_id ||
          canvasData?.tree_node_id;

        if (!treeNodeId) {
          console.error("No tree node ID found for navigation");
          console.error("Available values:", { id, node_id_ref, canvasData });
          showSnackbar("Error: No tree node ID found", "error");
          return;
        }

        // Navigate immediately without setTimeout to ensure it happens
        navigate(
          `/view-childnode/${partnumber}/${revision}/${partId}/${revisionId}/${section_id_ref}/${step_id_ref}/${treeNodeId}${
            substep_id_ref ? `?substep_id_ref=${substep_id_ref}` : ""
          }`,
        );
      } else {
        // Add mode - use createCanvas
        result = await dispatch(
          createCanvas({
            canvasdt,
            comment,
            version_content: {
              action_to_show: `Save ${pageTitle} for ${partnumber}, Rev ${revision}`,
              partNumber: partnumber,
              revision,
              status:currentStatus,
              item_code_type,
              type: "canvas",
            },
            allNodesData,
            partnumber,
            revision,
          }),
        ).unwrap();

        // Clear local storage
        localStorage.removeItem("canvasPages");
        localStorage.removeItem("canvasSnapshot");
        sessionStorage.removeItem("canvasStateInfo");
        // CRITICAL: Clear persistent unsaved changes flag when saving
        // This ensures the flag is cleared for all pages of this canvas
        if (id) {
          const persistentFlagKey = `canvasHasUnsavedChanges_${id}`;
          localStorage.removeItem(persistentFlagKey);
        }
        // Also clear fromMyCanvas flag
        localStorage.removeItem("fromMyCanvas");

        setHasUnsavedChanges(false);

        showSnackbar("Canvas created successfully!", "success");

        // Navigate to view-childnode with setTimeout like RichTextForm
        // Based on API response structure, we need to use the canvas_tree_node_id from CanvasTreeNodeData
        const treeNodeId =
          result?.results?.CanvasTreeNodeData?.canvas_tree_node_id ||
          result?.results?.tree_node_id;

        if (!treeNodeId) {
          console.error("No tree node ID found in result:", result);
          showSnackbar("Error: No tree node ID found in response", "error");
          return;
        }

        setTimeout(() => {
          navigate(
            `/view-childnode/${partnumber}/${revision}/${partId}/${revisionId}/${section_id_ref}/${step_id_ref}/${treeNodeId}${
              substep_id_ref ? `?substep_id_ref=${substep_id_ref}` : ""
            }`,
          );
        }, 1000);
      }
    } catch (error) {
      console.error("Error saving canvas:", error);
      showSnackbar(error.message || "Error saving canvas", "error");
      setIsLoading(false);
    }
  };

  // Handle preview
  const handlePreviewClick = useCallback(async () => {
    try {
      // Validate that at least one canvas page exists
      if (!canvasPages || canvasPages.length === 0) {
        showSnackbar(
          "Cannot preview canvas without any pages. Please add at least one canvas page.",
          "error",
        );
        return;
      }

      // Save shortDescription to localStorage before navigating to preview
      localStorage.setItem("short_description", shortDescription || "");

      // For edit mode, save draft to IndexedDB for persistence
      if (mode === "edit") {
        try {
          await saveForPreview();
        } catch (err) {
          // Continue with preview even if save fails
        }
      }

      // Load canvas pages data from IndexedDB for preview
      // Format as ctm_data array (matching CanvasNode.js expectation)
      let ctmData = [];
      if (canvasPages && canvasPages.length > 0) {
        ctmData = await Promise.all(
          canvasPages.map(async (page) => {
            try {
              const pageId = page.id || page.pageId;
              if (!pageId) return null;

              const result = await loadFromIndexedDB("canvasElements", pageId);
              const elements = result?.data || result || {};

              // Return in ctm_data format (matching CanvasNode.js expectation)
              return {
                canva_snap_url:
                  page.snapshot || page.canva_snap_url || elements.snapshot,
              };
            } catch (error) {
              console.error(
                `Error loading page ${page.id} for preview:`,
                error,
              );
              return {
                canva_snap_url: page.snapshot || page.canva_snap_url,
              };
            }
          }),
        );
        // Filter out null entries
        ctmData = ctmData.filter((page) => page !== null);
      }

      // Construct draftChild object for canvas preview
      // For edit mode, use the correct tree_node_id from canvasData or id
      // Canvas nodes can have tree_node_id, canvas_tree_node_id, or detail.canvas_tree_node_id
      const getCanvasTreeNodeId = () => {
        if (mode === "edit") {
          // Try multiple sources for the correct ID
          return (
            canvasData?.canvas_tree_node_id ||
            canvasData?.detail?.canvas_tree_node_id ||
            canvasData?.tree_node_id ||
            id
          );
        }
        return `draft-canvas-${Date.now()}`;
      };

      const draftChild = {
        tree_node_id: getCanvasTreeNodeId(),
        type: "canva",
        mode: mode, // "add" or "edit"
        isDraft: true,
        sibling_order: (mode === "add"
          ? (siblingOrder ?? 1)
          : canvasData?.sibling_order || 1
        ).toString(),
        section_id_ref: section_id_ref,
        step_id_ref: step_id_ref,
        substep_id_ref: substep_id_ref || null,
        detail: {
          short_description: shortDescription,
          ctm_data: ctmData, // Use ctm_data format for CanvasNode.js
          // Also include canvas_tree_node_id in detail for matching
          canvas_tree_node_id:
            mode === "edit" ? getCanvasTreeNodeId() : undefined,
        },
      };

      // Dispatch preview with draft canvas
      dispatch(
        openPreview({
          partId: partId,
          revisionId: revisionId,
          draftChild: draftChild,
        }),
      );
    } catch (error) {
      console.error("Error preparing preview:", error);
    }
  }, [
    saveForPreview,
    dispatch,
    mode,
    id,
    shortDescription,
    canvasPages,
    partId,
    revisionId,
    siblingOrder,
    canvasData,
    section_id_ref,
    step_id_ref,
    substep_id_ref,
    showSnackbar,
  ]);

  // Add canvas page
  const handleAddCanvasPage = () => {
    const newPageId = uuidv4();
    setPendingNewPageId(newPageId);
    setTemplateModalOpen(true);
    setIndex(canvasPages.length);
  };

  // Delete canvas page
  const handleDeleteCanvasPage = async (idx) => {
    if (idx < 0 || idx >= canvasPages.length) return;
    if (canvasPages.length === 1) {
      alert("At least one canvas page is required.");
      return;
    }

    // Get the page being deleted to track its canvas_page_id (from API) or id
    const deletedPage = canvasPages[idx];

    // In edit mode, track removed pages by their canvas_page_id (from API) or id
    if (mode === "edit" && deletedPage) {
      // Prefer canvas_page_id (from API response) over id/pageId
      const pageIdToTrack =
        deletedPage.canvas_page_id || deletedPage.id || deletedPage.pageId;

      if (pageIdToTrack) {
        // Add to removed pages list
        setRemovedCanvasPages((prev) => {
          // Avoid duplicates
          if (prev.includes(pageIdToTrack)) {
            return prev;
          }
          const updated = [...prev, pageIdToTrack];
          // Also persist immediately to ensure it's saved
          localStorage.setItem("removedCanvasPages", JSON.stringify(updated));
          return updated;
        });

        // Delete the page data from IndexedDB
        try {
          const db = await openCanvasDB();
          if (db.objectStoreNames.contains("canvasElements")) {
            const transaction = db.transaction(["canvasElements"], "readwrite");
            const store = transaction.objectStore("canvasElements");

            // Delete the main page record
            await new Promise((resolve, reject) => {
              const deleteRequest = store.delete(pageIdToTrack);
              deleteRequest.onsuccess = () => resolve();
              deleteRequest.onerror = () => reject(deleteRequest.error);
            });

            // Also delete any related records (like removed shapes)
            const removedShapesKey = `${pageIdToTrack}_removed_shapes`;
            await new Promise((resolve) => {
              const deleteRequest = store.delete(removedShapesKey);
              deleteRequest.onsuccess = () => resolve();
              deleteRequest.onerror = () => resolve(); // Don't fail if this doesn't exist
            });
          }
        } catch (dbError) {
          console.error("Error deleting page from IndexedDB:", dbError);
          // Continue even if IndexedDB deletion fails - the page ID is still tracked
        }
      }
    }

    // CRITICAL: Set persistent unsaved changes flag immediately when page is deleted
    // This ensures the flag persists across MyCanvas visits and navigation
    if (mode === "edit" && id) {
      const persistentFlagKey = `canvasHasUnsavedChanges_${id}`;
      localStorage.setItem(persistentFlagKey, "true");
    }

    setCanvasPages((pages) => {
      const updated = pages.filter((_, i) => i !== idx);
      setHasUnsavedChanges(true);
      return updated;
    });
  };

  // Handle navigation to canvas editor
  const handleNavigateToCanvas = async (canvasPageIndex) => {
    if (canvasPageIndex < 0 || canvasPageIndex >= canvasPages.length) return;

    const selectedPage = canvasPages[canvasPageIndex];
    if (!selectedPage || !selectedPage.id) return;

    setTemplateProcessing(true);

    // CRITICAL: Store canvas ID in localStorage so navigateBack can use it for persistent flag
    // This ensures the persistent flag uses the correct canvas ID (not page ID)
    if (id) {
      localStorage.setItem("currentCanvasId", id);
    }

    // CRITICAL: Store the initial snapshot BEFORE navigating to MyCanvas
    // This captures the state before any changes are made
    try {
      const pageId = selectedPage.id;
      const currentSnapshot = sessionStorage.getItem("initialCanvasSnapshots");
      let snapshots = {};

      if (currentSnapshot) {
        try {
          snapshots = JSON.parse(currentSnapshot);
        } catch (e) {
        }
      }

      // Only store if not already stored (to preserve existing snapshots)
      if (!snapshots[pageId]) {
        // Get current state from IndexedDB
        const db = await openCanvasDB();
        const transaction = db.transaction(["canvasElements"], "readonly");
        const store = transaction.objectStore("canvasElements");

        const result = await new Promise((resolve, reject) => {
          const request = store.get(pageId);
          request.onsuccess = () => resolve(request.result);
          request.onerror = () => reject(request.error);
        });

        const currentData = result?.data || {};

        // Normalize to only include canvas element arrays
        const normalizedState = {
          texts: currentData.texts || [],
          rectangles: currentData.rectangles || [],
          circles: currentData.circles || [],
          arrows: currentData.arrows || [],
          lines: currentData.lines || [],
          doubleSidedArrows: currentData.doubleSidedArrows || [],
          scribbles: currentData.scribbles || [],
          images: currentData.images || [],
        };

        snapshots[pageId] = JSON.stringify(normalizedState);
        sessionStorage.setItem(
          "initialCanvasSnapshots",
          JSON.stringify(snapshots),
        );
      }
    } catch (error) {
      console.error("Error storing initial snapshot before navigation:", error);
    }

    // Merged elements for selected page (with updated part details from API) - so MyCanvas shows updated texts
    let mergedElementsForSelectedPage = null;

    // CRITICAL: Save ALL pages to IndexedDB before navigating away
    // This ensures newly added pages are preserved and MyCanvas has the latest data.
    // Do NOT rely on index only: processed API pages can be in a different order.
    const selectedPageRefId =
      selectedPage.canvas_page_id ?? selectedPage.id ?? selectedPage.pageId;
    const selectedPageIsApiBacked = Boolean(selectedPage?.canvas_page_id);
    const processedPageForSelected =
      selectedPageIsApiBacked &&
      Array.isArray(processedCanvasPagesFromApi) &&
      processedCanvasPagesFromApi.length > 0
        ? processedCanvasPagesFromApi.find((p) => {
            const processedRefId = p.canvas_page_id ?? p.page_id ?? p.id;
            if (processedRefId == null || selectedPageRefId == null) return false;
            return String(processedRefId) === String(selectedPageRefId);
          }) || null
        : null;

    try {
      const db = await openCanvasDB();
      if (db.objectStoreNames.contains("canvasElements")) {
        const transaction = db.transaction(["canvasElements"], "readwrite");
        const store = transaction.objectStore("canvasElements");

        // Save ALL pages to IndexedDB, not just the selected one
        // This is critical for newly added pages
        await Promise.all(
          canvasPages.map(async (page, pageIdx) => {
            if (!page || !page.id) {
              return;
            }

            try {
              // Get existing data from IndexedDB for this page (robust key lookup).
              const pageKeyCandidates = Array.from(
                new Set(
                  [page.id, page.canvas_page_id, page.pageId]
                    .filter(Boolean)
                    .map((k) => String(k)),
                ),
              );
              let existingResult = null;
              for (const candidateKey of pageKeyCandidates) {
                const row = await new Promise((resolve, reject) => {
                  const getRequest = store.get(candidateKey);
                  getRequest.onsuccess = () => resolve(getRequest.result);
                  getRequest.onerror = () => reject(getRequest.error);
                });
                if (row?.data) {
                  existingResult = row;
                  break;
                }
              }

              const existingData = existingResult?.data || existingResult || {};
              const hasExistingIndexedRow = Boolean(existingResult?.data);
              const pickPreferredArray = (pageArr, existingArr) => {
                if (Array.isArray(pageArr) && pageArr.length > 0) return pageArr;
                if (Array.isArray(existingArr)) return existingArr;
                return [];
              };

              // Build the elements data to save - merge with existing to preserve data
              let elementsToSave = {
                texts: pickPreferredArray(page.elements?.texts, existingData.texts),
                rectangles: pickPreferredArray(
                  page.elements?.rectangles,
                  existingData.rectangles,
                ),
                circles: pickPreferredArray(
                  page.elements?.circles,
                  existingData.circles,
                ),
                arrows: pickPreferredArray(page.elements?.arrows, existingData.arrows),
                lines: pickPreferredArray(page.elements?.lines, existingData.lines),
                doubleSidedArrows: pickPreferredArray(
                  page.elements?.doubleSidedArrows,
                  existingData.doubleSidedArrows,
                ),
                scribbles: pickPreferredArray(
                  page.elements?.scribbles,
                  existingData.scribbles,
                ),
                images: pickPreferredArray(page.elements?.images, existingData.images),
                snapshot: page.snapshot || existingData.snapshot,
                canvasHeight: resolveCanvasHeightValue(
                  page.canvasHeight,
                  existingData.canvasHeight,
                  page.layout_config?.layout?.page_height_vh,
                  page.layout?.page_height_vh,
                ),
              };

              // For the selected API-backed page, merge in processed API data (updated part details)
              // so MyCanvas shows updated texts. Never apply API fallback by index for newly added pages.
              // CRITICAL: Only use shapes with type "Text" for canvas texts - never add from image text_boxes
              const pageForShapes =
                pageIdx === canvasPageIndex && selectedPageIsApiBacked
                  ? processedPageForSelected || page
                  : page;
              if (
                pageIdx === canvasPageIndex &&
                (pageForShapes?.shapes?.length > 0 ||
                  pageForShapes?.shape_data?.length > 0)
              ) {
                const rawShapes =
                  pageForShapes.shape_data || pageForShapes.shapes || [];
                const imageTextBoxIds = new Set();
                (pageForShapes.images || []).forEach((img) => {
                  (img.text_boxes || []).forEach((tb) => {
                    if (tb?.text_box_id) imageTextBoxIds.add(tb.text_box_id);
                  });
                });
                const shapes = rawShapes.filter((s) => {
                  const norm = normalizeShapeType(s?.type);
                  if (norm !== "Text") return true;
                  const sid = s?.shape_id || s?.shape_properties?.shape_id;
                  return !sid || !imageTextBoxIds.has(sid);
                });
                const converted = convertApiShapesToMyCanvasFormat(
                  shapes,
                  pageForShapes,
                );
                console.log("[CanvasFormDebug][convertApiShapesToMyCanvasFormat]", {
                  pageIdx,
                  rawShapeCount: rawShapes.length,
                  filteredShapeCount: shapes.length,
                  convertedCounts: {
                    lines: converted?.lines?.length || 0,
                    arrows: converted?.arrows?.length || 0,
                    doubleSidedArrows: converted?.doubleSidedArrows?.length || 0,
                  },
                  convertedLineLikeSample: [
                    ...(converted?.lines || []).slice(0, 6),
                    ...(converted?.arrows || []).slice(0, 6),
                    ...(converted?.doubleSidedArrows || []).slice(0, 6),
                  ].slice(0, 8).map((it) => ({
                    id: it?.shape_id || it?.id,
                    type: it?.type,
                    x: it?.x,
                    y: it?.y,
                    points: Array.isArray(it?.points) ? it.points : null,
                    width: it?.width,
                    height: it?.height,
                  })),
                  apiLineShapeSample: shapes
                    .filter((s) => {
                      const t = normalizeShapeType(s?.type);
                      return (
                        t === "Line" ||
                        t === "Arrow" ||
                        t === "DoubleSidedArrow"
                      );
                    })
                    .slice(0, 8)
                    .map((s) => ({
                      shape_id: s?.shape_id,
                      type: s?.type,
                      rootPoints: Array.isArray(s?.points) ? s.points : null,
                      shapeInfoPoints: Array.isArray(s?.shape_info?.points)
                        ? s.shape_info.points
                        : null,
                      shapePropertiesPoints: Array.isArray(
                        s?.shape_properties?.points,
                      )
                        ? s.shape_properties.points
                        : null,
                    })),
                });
                if (converted.texts && converted.texts.length > 0) {
                  const existingTexts = elementsToSave.texts || [];
                  const mergedTexts = [...existingTexts];
                  converted.texts.forEach((convText) => {
                    const idx = mergedTexts.findIndex((t) =>
                      canvasTextsRepresentSameEntity(t, convText),
                    );
                    if (idx >= 0) {
                      const existing = mergedTexts[idx];
                      // Prefer existing (IndexedDB/canvas) content/note/task over API so user edits
                      // are never overwritten by stale API data when returning via "edit in full screen"
                      const hasContent = (v) =>
                        v != null && String(v).trim() !== "";
                      // CRITICAL: Spread API first, then existing. The previous order (...existing, ...convText)
                      // overwrote IndexedDB x/y with stale API positions on every "edit in full screen".
                      mergedTexts[idx] = {
                        ...convText,
                        ...existing,
                        partsByType:
                          convText.partsByType ?? existing.partsByType,
                        content: hasContent(existing.content)
                          ? existing.content
                          : convText.content,
                        note: hasContent(existing.note)
                          ? existing.note
                          : convText.note,
                        task: hasContent(existing.task)
                          ? existing.task
                          : convText.task,
                        x: existing.x ?? convText.x,
                        y: existing.y ?? convText.y,
                        width: existing.width ?? convText.width,
                        height: existing.height ?? convText.height,
                        rotation: existing.rotation ?? convText.rotation,
                        scaleX: existing.scaleX ?? convText.scaleX,
                        scaleY: existing.scaleY ?? convText.scaleY,
                      };
                      const locE = existing.canvas_page_location_and_size;
                      const locC = convText.canvas_page_location_and_size;
                      if (locE || locC) {
                        const m = mergedTexts[idx];
                        m.canvas_page_location_and_size = {
                          ...(locC || {}),
                          ...(locE || {}),
                          x: m.x,
                          y: m.y,
                          width:
                            m.width ??
                            locE?.width ??
                            locC?.width,
                          height:
                            m.height ??
                            locE?.height ??
                            locC?.height,
                          rotation:
                            m.rotation ??
                            locE?.rotation ??
                            locC?.rotation ??
                            0,
                        };
                      }
                    } else {
                      mergedTexts.push(convText);
                    }
                  });
                  elementsToSave = { ...elementsToSave, texts: mergedTexts };
                  mergedElementsForSelectedPage = elementsToSave;
                }

                // In edit mode for API-backed selected page only:
                // use canvas_json_data_file converted arrays as source of truth for
                // non-text shapes/images (no id-matching fallback).
                // This ensures get-by-id-only stale shapes are not carried into IndexedDB/MyCanvas.
                if (mode === "edit" && processedPageForSelected) {
                  // Keep IDs from get-by-id/IndexedDB elements. Use canvas_json_data_file
                  // only as a geometric whitelist so stale extra elements are dropped.
                  const toNum = (v) => {
                    const n = Number(v);
                    return Number.isFinite(n) ? n : 0;
                  };
                  const round2 = (n) => Math.round(toNum(n) * 100) / 100;
                  const pointsSig = (pts) =>
                    Array.isArray(pts) && pts.length >= 4
                      ? pts.slice(0, 8).map((p) => round2(p)).join(",")
                      : "";
                  const sigFor = (item, kind) => {
                    const base = [
                      round2(item?.x),
                      round2(item?.y),
                      round2(item?.width),
                      round2(item?.height),
                    ].join("|");
                    if (
                      kind === "arrows" ||
                      kind === "lines" ||
                      kind === "doubleSidedArrows"
                    ) {
                      return `${base}|${pointsSig(item?.points)}`;
                    }
                    if (kind === "circles") {
                      return `${base}|${round2(item?.radius)}`;
                    }
                    return base;
                  };
                  const sourceMap = {
                    texts: converted?.texts || [],
                    rectangles: converted?.rectangles || [],
                    circles: converted?.circles || [],
                    arrows: converted?.arrows || [],
                    lines: converted?.lines || [],
                    doubleSidedArrows: converted?.doubleSidedArrows || [],
                    scribbles: converted?.scribbles || [],
                    images: converted?.images || [],
                  };
                  const combinedLineLikeSource = [
                    ...(sourceMap.arrows || []),
                    ...(sourceMap.lines || []),
                    ...(sourceMap.doubleSidedArrows || []),
                  ];
                  const getEntityId = (it) =>
                    it?.shape_id || it?.id || it?.shape_info?.id || null;
                  const isUserCreatedCanvasShape = (it) => {
                    const id = getEntityId(it);
                    return it?.createdOnCanvas === true || (id && isMarkerId(id));
                  };
                  const filterBySourceGeometry = (existingArr, sourceArr, kind) => {
                    const src = Array.isArray(sourceArr) ? sourceArr : [];
                    const srcSigSet = new Set(src.map((it) => sigFor(it, kind)));
                    const srcIdSet = new Set(src.map(getEntityId).filter(Boolean));
                    const existingRaw = Array.isArray(existingArr) ? existingArr : [];

                    // Preserve identity for non-text elements that initially came from
                    // API/canvas-json but may be edited later (e.g. moved x/y).
                    // If a row has no id/shape_id yet, attach the source id using an
                    // exact initial geometry match so future loads can keep it by id.
                    const sourceIdsBySig = new Map();
                    src.forEach((it) => {
                      const sid = getEntityId(it);
                      if (!sid) return;
                      const sig = sigFor(it, kind);
                      if (!sourceIdsBySig.has(sig)) {
                        sourceIdsBySig.set(sig, []);
                      }
                      sourceIdsBySig.get(sig).push(sid);
                    });
                    const existing = existingRaw.map((it) => {
                      if (!it) return it;
                      const existingId = getEntityId(it);
                      if (existingId) return it;
                      const sig = sigFor(it, kind);
                      const candidateIds = sourceIdsBySig.get(sig);
                      if (!candidateIds || candidateIds.length === 0) return it;
                      const nextId = candidateIds.shift();
                      return {
                        ...it,
                        shape_id: it.shape_id || nextId,
                        id: it.id || nextId,
                      };
                    });

                    console.log(`[CanvasForm] filterBySourceGeometry for ${kind}:`, {
                      existingCount: existing.length,
                      sourceCount: src.length,
                      existingIds: existing.map(getEntityId),
                      sourceIds: src.map(getEntityId)
                    });

                    // If we only have source:
                    // - for existing IndexedDB rows, keep empty to preserve deletions
                    // - otherwise, use source as initial payload
                    if (existing.length === 0) {
                      const lineLikeKind =
                        kind === "arrows" ||
                        kind === "lines" ||
                        kind === "doubleSidedArrows";
                      // Line-like: first paint with no IndexedDB row hydrates from canvas_json;
                      // if a row exists and this bucket is empty, keep [] so local deletes are not undone.
                      if (lineLikeKind) {
                        const perKindSource =
                          kind === "arrows"
                            ? sourceMap.arrows || []
                            : kind === "lines"
                              ? sourceMap.lines || []
                              : sourceMap.doubleSidedArrows || [];
                        // IndexedDB row exists and this bucket is empty: trust local state
                        // (user removed line-likes or leave-save cleared the bucket). Do not
                        // refill from canvas_json — that reintroduces "deleted" arrows until API saves.
                        if (hasExistingIndexedRow) {
                          return [];
                        }
                        if (perKindSource.length > 0) {
                          return [...perKindSource];
                        }
                        return [];
                      }
                      if (hasExistingIndexedRow) {
                        return [];
                      }
                      return src;
                    }
                    // If source is empty, preserve user-created elements to avoid wiping out new elements.
                    if (src.length === 0) {
                      const userCreated = existing.filter(isUserCreatedCanvasShape);
                      console.log(`[CanvasForm] Source empty for ${kind}, preserving ${userCreated.length} user-created elements`);
                      return userCreated;
                    }

                    const filtered = existing.filter((it) => {
                      // Keep all user-created shapes even when not present in API JSON.
                      if (isUserCreatedCanvasShape(it)) {
                        console.log(`[CanvasForm] Preserving user-created ${kind} element:`, {
                          id: getEntityId(it),
                          name: it.name,
                          createdOnCanvas: it.createdOnCanvas,
                          kind
                        });
                        return true;
                      }
                      const id = getEntityId(it);
                      if (id && srcIdSet.has(id)) return true;
                      return srcSigSet.has(sigFor(it, kind));
                    });

                    console.log(`[CanvasForm] Filtered ${kind} from ${existing.length} to ${filtered.length} elements`);
                    return filtered;
                  };
                  let nextArrows = filterBySourceGeometry(
                    elementsToSave.arrows,
                    combinedLineLikeSource,
                    "arrows",
                  );
                  let nextLines = filterBySourceGeometry(
                    elementsToSave.lines,
                    combinedLineLikeSource,
                    "lines",
                  );
                  let nextDoubleSidedArrows = filterBySourceGeometry(
                    elementsToSave.doubleSidedArrows,
                    combinedLineLikeSource,
                    "doubleSidedArrows",
                  );
                  const doubleIds = new Set(
                    nextDoubleSidedArrows.map(getEntityId).filter(Boolean),
                  );
                  nextArrows = nextArrows.filter(
                    (a) => !doubleIds.has(getEntityId(a)),
                  );
                  nextLines = nextLines.filter(
                    (l) => !doubleIds.has(getEntityId(l)),
                  );
                  const arrowIds = new Set(
                    nextArrows.map(getEntityId).filter(Boolean),
                  );
                  nextLines = nextLines.filter(
                    (l) => !arrowIds.has(getEntityId(l)),
                  );

                  elementsToSave = {
                    ...elementsToSave,
                    // Keep text elements from get-by-id/IndexedDB as source of truth.
                    // Text geometry can drift slightly from canvas_json_data_file and
                    // must not be dropped by geometric matching.
                    texts: Array.isArray(elementsToSave.texts)
                      ? elementsToSave.texts
                      : [],
                    rectangles: filterBySourceGeometry(
                      elementsToSave.rectangles,
                      sourceMap.rectangles,
                      "rectangles",
                    ),
                    circles: filterBySourceGeometry(
                      elementsToSave.circles,
                      sourceMap.circles,
                      "circles",
                    ),
                    // Keep only line-like elements that are either:
                    // 1) present in API source (id/geometry), or
                    // 2) user-created (createdOnCanvas / marker id).
                    // This removes stale get-by-id extras not present in canvas_json_data_file.
                    arrows: nextArrows,
                    lines: nextLines,
                    doubleSidedArrows: nextDoubleSidedArrows,
                    scribbles: filterBySourceGeometry(
                      elementsToSave.scribbles,
                      sourceMap.scribbles,
                      "scribbles",
                    ),
                    images: filterBySourceGeometry(
                      elementsToSave.images,
                      sourceMap.images,
                      "images",
                    ),
                  };

                  console.log("[CanvasFormDebug][api-json-filtered-elements]", {
                    pageIdx,
                    canvasPageIndex,
                    source: "canvas_json_data_file_filter_keep_getbyid_ids",
                    savedCounts: {
                      texts: elementsToSave.texts?.length || 0,
                      rectangles: elementsToSave.rectangles?.length || 0,
                      circles: elementsToSave.circles?.length || 0,
                      arrows: elementsToSave.arrows?.length || 0,
                      lines: elementsToSave.lines?.length || 0,
                      doubleSidedArrows:
                        elementsToSave.doubleSidedArrows?.length || 0,
                      scribbles: elementsToSave.scribbles?.length || 0,
                      images: elementsToSave.images?.length || 0,
                    },
                  });
                }
                // Always keep selected-page merged payload updated for navigation,
                // even when there are no converted texts.
                if (pageIdx === canvasPageIndex) {
                  mergedElementsForSelectedPage = elementsToSave;
                }
              }

              // Save to IndexedDB with the proper structure (include validTextShapeIds so MyCanvas can filter after refresh)
              // CRITICAL: Only set validTextShapeIds from API shapes (processedPageForSelected). Never derive from page.shape_data
              // (shape_data can be built from elements.texts and include image text_box ids like 2cc43d96, which would overwrite
              // the correct [abf54041, 1c4aefba] and cause only 1 text to show). When we don't have API source, preserve existing.
              const imageTextBoxIdsForSave = new Set();
              const imagesForTextBoxIds =
                pageForShapes?.images || page?.elements?.images || [];
              imagesForTextBoxIds.forEach((img) => {
                (img.text_boxes || []).forEach((tb) => {
                  if (tb?.text_box_id)
                    imageTextBoxIdsForSave.add(tb.text_box_id);
                });
              });
              let validIdsForPage = undefined;
              if (pageIdx === canvasPageIndex) {
                if (
                  processedPageForSelected &&
                  (processedPageForSelected.shapes?.length > 0 ||
                    processedPageForSelected.shape_data?.length > 0)
                ) {
                  const apiShapes =
                    processedPageForSelected.shape_data ||
                    processedPageForSelected.shapes ||
                    [];
                  validIdsForPage = apiShapes
                    .filter(
                      (s) =>
                        normalizeShapeType(s?.type) === "Text" &&
                        s?.shape_id &&
                        !imageTextBoxIdsForSave.has(s.shape_id),
                    )
                    .map((s) => s.shape_id)
                    .filter(Boolean);
                }
                if (
                  !Array.isArray(validIdsForPage) ||
                  validIdsForPage.length === 0
                ) {
                  const existing = existingResult?.data?.validTextShapeIds;
                  validIdsForPage = Array.isArray(existing)
                    ? existing.filter(
                        (id) => id && !imageTextBoxIdsForSave.has(id),
                      )
                    : undefined;
                }
                // Include shape_id/id for every text we're saving: non-marker or createdOnCanvas (user-added texts)
                const fromTexts = (elementsToSave.texts || [])
                  .filter(
                    (t) =>
                      t.createdOnCanvas === true ||
                      !isMarkerId(t.shape_id || t.id),
                  )
                  .map((t) => t.shape_id || t.id)
                  .filter((id) => id && !imageTextBoxIdsForSave.has(id));
                if (fromTexts.length > 0) {
                  const merged = [
                    ...new Set([...(validIdsForPage || []), ...fromTexts]),
                  ];
                  validIdsForPage =
                    merged.length > 0 ? merged : validIdsForPage;
                }
              }
              const storageId =
                existingResult?.id || page.id || page.canvas_page_id || page.pageId;
              const updatedData = {
                id: storageId,
                data: {
                  ...elementsToSave,
                  ...(Array.isArray(validIdsForPage) &&
                  validIdsForPage.length > 0
                    ? { validTextShapeIds: validIdsForPage }
                    : {}),
                },
                lastModified: new Date().toISOString(),
              };

              await store.put(updatedData);
            } catch (error) {
              console.error(
                `Error saving page ${page.id} to IndexedDB:`,
                error,
              );
            }
          }),
        );
      }
    } catch (error) {
      console.error("Error saving elements to IndexedDB:", error);
    }

    // Handle session storage for text content
    const stateInfo = sessionStorage.getItem("canvasStateInfo");
    const hasTextContent = stateInfo && JSON.parse(stateInfo).includesText;

    sessionStorage.setItem(
      "canvasStateInfo",
      JSON.stringify({
        hasChanges: true,
        lastUpdate: new Date().toISOString(),
        includesText: hasTextContent,
        preserveTextOnReturn: true,
      }),
    );

    localStorage.setItem("short_description", shortDescription);

    // Save canvasPages to localStorage before navigating (for both add and edit modes)
    // This ensures newly added pages are preserved when returning from MyCanvas
    // NOTE: DO NOT include snapshot in localStorage - it causes quota exceeded errors
    // Snapshots are stored in IndexedDB only
    const lightweightPages = canvasPages.map((p) => ({
      id: p?.id || p?.pageId,
      canvas_page_id: p?.canvas_page_id, // CRITICAL: Include canvas_page_id for API pages
      zoomLevel: typeof p?.zoomLevel === "number" ? p.zoomLevel : 100,
      lastModified: p?.lastModified || new Date().toISOString(),
      canvasHeight: p?.canvasHeight,
      newlyAdded: p?.newlyAdded,
      // snapshot: p?.snapshot, // REMOVED - Snapshots stored in IndexedDB only
      shortDescription: p?.shortDescription,
      templateName: p?.templateName,
      canva_snap_url: p?.canva_snap_url,
      templateApplied: p?.templateApplied,
      text_boxes: Array.isArray(p?.text_boxes) ? p.text_boxes : [],
      text_box_content_data: Array.isArray(p?.text_box_content_data)
        ? p.text_box_content_data
        : [],
    }));

    if (mode === "add") {
      localStorage.setItem("canvasPages", JSON.stringify(lightweightPages));
    } else {
      // In edit mode, also save to localStorage to preserve newly added pages
      localStorage.setItem("canvasPages", JSON.stringify(lightweightPages));
    }

    try {
      sessionStorage.setItem(
        "lastCanvasFormPagesBeforeMyCanvas",
        JSON.stringify(lightweightPages),
      );
    } catch (_e) {
      // ignore quota / private mode
    }

    // Only API Text shape_ids (exclude image text_boxes) so MyCanvas shows exactly 2 texts when API has 2
    // CRITICAL: Use ONLY processedPageForSelected (API) for this list. Never use selectedPage.shape_data/shapes
    // (those can be built from elements.texts and include image text_box ids, which would make only 1 text show).
    const imageTextBoxIdsForPage = new Set();
    (
      processedPageForSelected?.images ||
      selectedPage?.elements?.images ||
      selectedPage?.images ||
      []
    ).forEach((img) => {
      (img.text_boxes || []).forEach((tb) => {
        if (tb?.text_box_id) imageTextBoxIdsForPage.add(tb.text_box_id);
      });
    });
    let validTextShapeIds =
      processedPageForSelected &&
      (processedPageForSelected.shapes?.length > 0 ||
        processedPageForSelected.shape_data?.length > 0)
        ? (
            processedPageForSelected.shape_data ||
            processedPageForSelected.shapes ||
            []
          )
            .filter(
              (s) =>
                normalizeShapeType(s?.type) === "Text" &&
                s?.shape_id &&
                !imageTextBoxIdsForPage.has(s.shape_id),
            )
            .map((s) => s.shape_id)
            .filter(Boolean)
        : undefined;
    if (!Array.isArray(validTextShapeIds) || validTextShapeIds.length === 0) {
      const stored = selectedPage?.elements?.validTextShapeIds;
      validTextShapeIds = Array.isArray(stored)
        ? stored.filter((id) => id && !imageTextBoxIdsForPage.has(id))
        : undefined;
    }
    // Include user-created text ids (createdOnCanvas) so MyCanvas keeps them when navigating back
    const userCreatedTextIds = (selectedPage?.elements?.texts || [])
      .filter((t) => t.createdOnCanvas === true)
      .map((t) => t.shape_id || t.id)
      .filter((id) => id && !imageTextBoxIdsForPage.has(id));
    if (userCreatedTextIds.length > 0) {
      validTextShapeIds = [
        ...new Set([...(validTextShapeIds || []), ...userCreatedTextIds]),
      ];
    }

    // Ensure we have all required navigation state
    // Note: stepId from useCanvasData is the actual step_id_ref (database ID) from the step object
    // Prefer merged elements (with updated part details from API) so MyCanvas shows updated texts
  const ctmPartDetailsForNavigation = [
    ...collectPartDetailsFromCtmData(
      canvasNodeFromProcedureNodes?.detail?.current_canvas_data_json ??
        canvasData?.detail?.current_canvas_data_json,
    ),
    ...collectPartDetailsFromCtmData(
      canvasNodeFromProcedureNodes?.detail ?? canvasData?.detail,
    ),
  ];

  const navigationState = {
      fromCanvasPage: true,
      hasTextContent,
      pageIndex: canvasPageIndex,
      pageId: selectedPage.id,
      stepId: stepId, // Pass stepId (step_id_ref - database ID) for Parts popup BOM filtering
      canvasHeight:
        selectedPage.canvasHeight ??
        selectedPage.elements?.canvasHeight ??
        undefined,
      canvasPages: canvasPages, // Pass the entire canvasPages array for return navigation
      canvasData: mergedElementsForSelectedPage ?? selectedPage.elements ?? {}, // Merged = updated texts from current_canvas_data_json
      ctmPartDetails: ctmPartDetailsForNavigation,
      validTextShapeIds, // Only these shape_ids are real API Text shapes (MyCanvas filters to show only 2 texts)
      // Explicit return path so MyCanvas can navigate back to the correct CanvasForm URL
      returnPath: location.pathname + location.search,
    // Preserve "where to go next" intent set by the page that opened this CanvasForm
    // (e.g. ViewStep/ViewSubstep/ViewChildNode). Consumed by CanvasForm Back button
    // after returning from MyCanvas.
    returnTo: location.state?.returnTo,
    };

    navigate(`/canvas`, {
      state: navigationState,
    });

    setTemplateProcessing(false);
  };

  // Change detection: compare API baseline with current IndexedDB data
  // This includes checking canvas height changes made in MyCanvas
  const detectChangesFromIndexedDB = useCallback(async () => {
    if (mode !== "edit" || !id) {
      return false;
    }


    // CRITICAL: DO NOT check hasUnsavedChanges here - always do the comparison
    // The hasUnsavedChanges flag might be set by MyCanvas with false positives
    // We need to do our own reliable comparison

    try {
      // Load the original API baseline (State A)
      const storageKey = `initialApiPages_${id}`;
      let storedBaseline = localStorage.getItem(storageKey);

      // CRITICAL: If no baseline exists, we can't detect changes reliably
      // This should never happen if baseline was captured correctly on load
      // Return false (no changes) to avoid false positives
      if (!storedBaseline) {

        // CRITICAL: If no baseline exists, capture it now from IndexedDB
        // This ensures we have a baseline for future comparisons
        try {
          const {
            loadCurrentStateFromIndexedDB,
          } = require("../utils/canvasStateComparison");

          // Load current state from IndexedDB
          // We need to get the page IDs from canvasPages
          if (Array.isArray(canvasPages) && canvasPages.length > 0) {
            const currentState =
              await loadCurrentStateFromIndexedDB(canvasPages);

            if (currentState && currentState.length > 0) {
              // Store this as the baseline
              const storageKey = `initialApiPages_${id}`;
              localStorage.setItem(storageKey, JSON.stringify(currentState));

              // Now that we have a baseline, return false (no changes)
              // because we just captured the current state as the baseline
              return false;
            }
          }
        } catch (error) {
          console.error(
            "🔍 [CANVAS-FORM] Error capturing baseline from IndexedDB:",
            error,
          );
        }

        // If we couldn't capture a baseline, check if hasUnsavedChanges was set by user actions (not MyCanvas)
        return hasUnsavedChanges;
      }

      let apiBaseline = null;
      try {
        apiBaseline = JSON.parse(storedBaseline);
      } catch (e) {
        console.error("🔍 [CANVAS-FORM] Error parsing API baseline:", e);
        return hasUnsavedChanges;
      }

      if (!Array.isArray(apiBaseline) || apiBaseline.length === 0) {
        return hasUnsavedChanges;
      }


      // CRITICAL: Log the baseline element counts for debugging
      apiBaseline.forEach((page) => {
      });

      // CRITICAL: Check for newly added pages (pages with newlyAdded flag)
      // This is important because newly added pages should always be detected as changes
      let hasNewlyAddedPages =
        Array.isArray(canvasPages) &&
        canvasPages.some((page) => page?.newlyAdded === true);

      // Also check localStorage directly in case state hasn't loaded yet
      if (!hasNewlyAddedPages) {
        try {
          const savedPagesRaw = localStorage.getItem("canvasPages");
          if (savedPagesRaw && savedPagesRaw !== "undefined") {
            const savedPages = JSON.parse(savedPagesRaw);
            if (Array.isArray(savedPages)) {
              hasNewlyAddedPages = savedPages.some(
                (page) => page?.newlyAdded === true,
              );
            }
          }
        } catch (error) {
          console.error(
            "Error checking localStorage for newly added pages:",
            error,
          );
        }
      }

      if (hasNewlyAddedPages) {
        return true;
      }

      // Check for removed pages
      const hasRemovedPages =
        Array.isArray(removedCanvasPages) && removedCanvasPages.length > 0;
      if (hasRemovedPages) {
        return true;
      }

      // Check page count
      const currentPageCount = Array.isArray(canvasPages)
        ? canvasPages.length
        : 0;
      if (currentPageCount !== apiBaseline.length) {
        return true;
      }

      // Load current state from IndexedDB (State B)
      const {
        loadCurrentStateFromIndexedDB,
        compareCanvasStates,
      } = require("../utils/canvasStateComparison");

      const currentState = await loadCurrentStateFromIndexedDB(apiBaseline);

      if (!currentState || currentState.length === 0) {
        return false;
      }


      // CRITICAL: Log the current state element counts for debugging
      currentState.forEach((page) => {
      });

      // Compare State A (API baseline) with State B (current IndexedDB state)
      const statesAreEqual = compareCanvasStates(apiBaseline, currentState);

      // If states are equal, no changes; if different, has changes
      const hasChanges = !statesAreEqual;


      return hasChanges;
    } catch (error) {
      console.error("🔍 [CANVAS-FORM] Error during change detection:", error);
      // On error, return false to avoid false positives
      return false;
    }
  }, [mode, id, hasUnsavedChanges, canvasPages, removedCanvasPages]);

  // AppBar back handler
  // Navigate explicitly back to ViewChildNode instead of relying on history.back(),
  // so we don't bounce back into MyCanvas after returning from it.
  // Use node_id_ref when present (legacy flows), otherwise fall back to id which
  // is the child_node_id for the /edit-canvas-new route used by ViewChildNode.
  const handleAppBarBack = useCallback(() => {
    const explicitReturnPath = location.state?.returnTo;
    const childNodeId = node_id_ref || id;
    const currentQueryParams = new URLSearchParams(location.search || "");
    const substepIdFromQuery = currentQueryParams.get("substep_id_ref");
    const effectiveSubstepIdRef = substepIdFromQuery || substep_id_ref || "";
    const targetPath = `/view-childnode/${partnumber}/${revision}/${partId}/${revisionId}/${section_id_ref}/${step_id_ref}/${childNodeId}${
      effectiveSubstepIdRef
        ? `?substep_id_ref=${encodeURIComponent(effectiveSubstepIdRef)}`
        : ""
    }`;
    const canvasId = localStorage.getItem("currentCanvasId") || id;
    const persistentFlagKeysToTry = [
      `canvasHasUnsavedChanges_${canvasId}`,
      `canvasHasUnsavedChanges_${id}`,
    ];
    const hasPersistentChanges = persistentFlagKeysToTry.some(
      (k) => localStorage.getItem(k) === "true"
    );

    const shouldPrompt = hasUnsavedChanges || isShortDescriptionDirty || hasPersistentChanges;
    if (shouldPrompt) {
      const confirmed = window.confirm(
        "You have unsaved changes. Are you sure you want to leave?"
      );
      if (!confirmed) {
        return;
      }
    }
    navigate(explicitReturnPath || targetPath);
  }, [
    navigate,
    location.state,
    partnumber,
    revision,
    partId,
    revisionId,
    section_id_ref,
    step_id_ref,
    substep_id_ref,
    location.search,
    node_id_ref,
    id,
    hasUnsavedChanges,
    isShortDescriptionDirty,
  ]);

  // Upload to S3 helper function
  const uploadToS3 = async (data, fileName, fileType) => {
    try {
      // Get authorization token from localStorage or other storage
      const token =
        localStorage.getItem("token") ||
        localStorage.getItem("access_token") ||
        localStorage.getItem("auth_token") ||
        sessionStorage.getItem("token") ||
        "";

      if (!token) {
        throw new Error("No authorization token found");
      }

      // 1. Get presigned URL from backend
      const response = await fetch(`${BASE_URL}upload/get-upload-urls`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          folder: "canvas",
          files: [{ fileName, fileType }],
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to get upload URL: ${response.status}`);
      }

      const responseData = await response.json();
      const { uploadUrl, fileUrl } = responseData.files[0];

      // 2. Convert to blob if needed
      let blob;

      if (data instanceof Blob || data instanceof File) {
        blob = data;
      } else if (typeof data === "string") {
        if (!data.startsWith("data:")) {
          throw new Error("Invalid data format");
        }

        try {
          const base64Response = await fetch(data);
          if (!base64Response.ok) {
            throw new Error(
              `Failed to convert base64 to blob: ${base64Response.status}`,
            );
          }
          blob = await base64Response.blob();
        } catch (blobError) {
          throw new Error(`Base64 conversion failed: ${blobError.message}`);
        }
      } else {
        throw new Error("Invalid data type provided");
      }

      // Validate blob size
      if (blob.size === 0) {
        throw new Error("Generated blob is empty");
      }

      const sizeInMB = blob.size / (1024 * 1024);
      if (sizeInMB > 50) {
        throw new Error(`File too large: ${sizeInMB.toFixed(2)}MB (max 50MB)`);
      }

      // 3. Upload to S3 using presigned URL with timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);

      try {
        const s3Response = await fetch(uploadUrl, {
          method: "PUT",
          headers: {
            "Content-Type": fileType,
          },
          body: blob,
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        if (!s3Response.ok) {
          throw new Error(`S3 upload failed: ${s3Response.status}`);
        }
      } catch (fetchError) {
        clearTimeout(timeoutId);
        if (fetchError.name === "AbortError") {
          throw new Error("S3 upload timed out after 30 seconds");
        }
        throw fetchError;
      }

      return fileUrl;
    } catch (error) {
      throw error;
    }
  };

  // Set task reference for edit mode
  useEffect(() => {
    if (mode === "edit" && canvasData) {
      setTaskReference(
        dispatch,
        `Canvas ${partnumber}`,
        canvasData?.detail?.canvas_tree_node_id,
        canvasData?.tree_node_id,
        canvasData?.ltree_path,
      );
    }
  }, [mode, canvasData, dispatch, partnumber]);

  // Show loader until Canvas Index Number and Tree Path are loaded
  if (!isInitialDataLoaded) {
    return <Loader text="Loading Canvas information..." />;
  }

  return (
    <>
      <div style={{ paddingBottom: "100px" }}>
        {/* AppBar with Navigation Buttons */}
        <AppBar
          onBack={handleAppBarBack}
          subTitleAboveTitle={
            <>
              {localStorage.getItem("user_name")} is{" "}
              {mode === "add" ? "Creating" : "Editing"}
            </>
          }
          title={pageTitle}
          showTablesDropdown={true}
          buttons={[
            { label: "Preview", onClick: handlePreviewClick },
            {
              label: "Save",
              onClick: canEdit ? handleSaveClick : undefined,
              disabled: !canEdit,
              title: !canEdit
                ? "You do not have permission to save canvas pages."
                : undefined,
            },
          ]}
          subheading={breadcrumbText}
          showBlackTitle={true}
          versionHistoryHandler={
            mode === "edit" ? () => setShowVersionHistory(true) : null
          }
        />

        {/* Template Processing Loading Indicator */}
        {templateProcessing && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 9999,
            }}
          >
            <div
              style={{
                backgroundColor: "white",
                padding: "20px",
                borderRadius: "8px",
                textAlign: "center",
              }}
            >
              <div style={{ marginBottom: "10px" }}>
                <div
                  style={{
                    width: "40px",
                    height: "40px",
                    border: "4px solid #f3f3f3",
                    borderTop: "4px solid #007bff",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                    margin: "0 auto",
                  }}
                ></div>
              </div>
              <p>Processing template...</p>
              <p style={{ fontSize: "12px", color: "#666" }}>
                Generating preview snapshot
              </p>
            </div>
          </div>
        )}

        <div className="form-group">
          <label style={{ fontWeight: "bold" }}>Version ID (Read Only)</label>
          <input
            type="text"
            name="id"
            value={versionId || ""}
            style={{ width: "30%" }}
            readOnly
          />
        </div>

        {/* Substep Details */}
        <div className="form-group" style={{ marginLeft: "10px", marginRight: "10px" }}>
          <label className="block font-semibold" style={{ fontWeight: "bold" }}>
            Short Description
          </label>
          <ShortDescriptionEditor
            value={shortDescription}
            onChange={(value) => {
              setShortDescription(value);
              // Persist to localStorage for draft recovery
              localStorage.setItem("short_description", value);
              // NOTE: setHasUnsavedChanges is intentionally NOT called here.
              // Dirty detection is handled via the Lexical undo history
              // (onDirtyChange below) to avoid false positives on init.
            }}
            onDirtyChange={handleShortDescriptionDirtyChange}
            placeholder="Enter short description"
            minHeight="100px"
          />
        </div>

        {/* Canvas Preview Section */}
        <div style={{ position: "relative", width: "100%" }}>
          {!templateModalOpen &&
            canvasPages.map((page, idx) => (
              <div
                key={idx}
                style={{ position: "relative", marginBottom: "40px" }}
              >
                <h4
                  style={{
                    width: "50%",
                    fontSize: "12px",
                    margin: "0 auto",
                    backgroundColor: "#fff",
                    color: "rgb(102, 102, 102)",
                    fontFamily: "Roboto",
                    fontWeight: "normal",
                  }}
                >
                  Canvas Page {idx + 1} ({idx + 1}/{canvasPages.length})
                </h4>

                {/* Main Content Box */}
                <div
                  style={{
                    width: "55%",
                    margin: "10px auto",
                    border: "1px solid #e0e0e0",
                    borderRadius: "8px",
                    backgroundColor: "#fff",
                    fontFamily: "sans-serif",
                  }}
                >
                  {/* Top Toolbar */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "10px 20px",
                      borderBottom: "1px solid #eee",
                      fontSize: "14px",
                    }}
                  >
                    <div style={{ display: "none" }} />
                    <div style={{ color: "#666", textAlign: "center" }}>
                      <i>
                        You are in Preview Mode. <br />
                        To edit click{" "}
                        <span style={{ textDecoration: "underline" }}>
                          "Edit in Full Screen"
                        </span>
                        .
                      </i>
                    </div>
                    <PrimaryButton
                      onClick={() =>
                        !templateProcessing && handleNavigateToCanvas(idx)
                      }
                      disabled={templateProcessing}
                      loading={templateProcessing}
                      loadingText="Loading Template..."
                    >
                      Edit in Full Screen
                    </PrimaryButton>
                    <DangerButton
                      title="Delete Canvas Page"
                      onClick={() => handleDeleteCanvasPage(idx)}
                      icon={<FontAwesomeIcon icon={faTrashCan} />}
                      iconOnly={true}
                      ariaLabel="Delete Canvas Page"
                    />
                  </div>

                  {/* Canvas Content: When returning from MyCanvas, show toPng snapshot first; else CanvasPageRenderer when we have processed data; else canva_snap_url/snapshot image; else No Data */}
                  {(() => {
                    // Prefer PNG snapshot from MyCanvas (toPng of main stage) when available - show it instead of CanvasPageRenderer
                    const hasPngSnapshot =
                      page.snapshot &&
                      typeof page.snapshot === "string" &&
                      (page.snapshot.startsWith("data:image/png") ||
                        page.snapshot.startsWith("data:image/jpeg"));
                    if (hasPngSnapshot) {
                      return (
                        <div
                          style={{
                            position: "relative",
                            padding: "0",
                            cursor: "pointer",
                            textAlign: "left",
                          }}
                          onClick={() => handleNavigateToCanvas(idx)}
                        >
                          <img
                            src={page.snapshot}
                            alt={`Canvas Snapshot ${idx + 1}`}
                            className="canvas-page-snapshot"
                            style={{
                              width: "100%",
                              height: "auto",
                              maxHeight: "none",
                              display: "block",
                              margin: 0,
                              objectFit: "contain",
                            }}
                          />
                        </div>
                      );
                    }

                    // Prefer index: API page order matches card order, so use idx when in range
                    const matchedProcessedPage =
                      processedCanvasPagesFromApi &&
                      processedCanvasPagesFromApi.length > 0 &&
                      idx < processedCanvasPagesFromApi.length
                        ? processedCanvasPagesFromApi[idx]
                        : processedCanvasPagesFromApi?.length > 0
                          ? (processedCanvasPagesFromApi.find((p) => {
                              const a = p.canvas_page_id ?? p.page_id ?? p.id;
                              const b =
                                page.canvas_page_id ?? page.id ?? page.pageId;
                              if (a == null || b == null) return false;
                              return String(a) === String(b);
                            }) ?? null)
                          : null;

                    if (matchedProcessedPage) {
                      return (
                        <div
                          style={{
                            position: "relative",
                            padding: "0",
                            cursor: "pointer",
                            textAlign: "left",
                            maxWidth: "100%",
                            overflow: "visible",
                          }}
                          onClick={() => handleNavigateToCanvas(idx)}
                        >
                          <CanvasPagePreviewMeasured
                            canvasData={{ pages: [matchedProcessedPage] }}
                          />
                        </div>
                      );
                    }
                    if (processedCanvasPagesLoading) {
                      return (
                        <div
                          style={{
                            borderTop: "1px dashed #ccc",
                            padding: "60px 20px",
                            textAlign: "center",
                            backgroundColor: "#f9f9f9",
                          }}
                        >
                          <Loader />
                        </div>
                      );
                    }
                    // When no processed data (e.g. page has no canvas_json_data_file URL), show canva_snap_url or snapshot image
                    const snapUrl = page.snapshot || page.canva_snap_url;
                    if (snapUrl) {
                      return (
                        <div
                          style={{
                            position: "relative",
                            padding: "0",
                            cursor: "pointer",
                            textAlign: "left",
                          }}
                          onClick={() => handleNavigateToCanvas(idx)}
                        >
                          <img
                            src={snapUrl}
                            alt={`Canvas Snapshot ${idx + 1}`}
                            className="canvas-page-snapshot"
                            style={{
                              width: "100%",
                              height: "auto",
                              maxHeight: "none",
                              display: "block",
                              margin: 0,
                              objectFit: "contain",
                            }}
                          />
                        </div>
                      );
                    }
                    return (
                      <div
                        style={{
                          borderTop: "1px dashed #ccc",
                          padding: "60px 20px",
                          textAlign: "center",
                          cursor: "pointer",
                          backgroundColor: "#f9f9f9",
                        }}
                        onClick={() => handleNavigateToCanvas(idx)}
                      >
                        <p
                          style={{
                            marginBottom: "15px",
                            color: "#666",
                            fontSize: "16px",
                          }}
                        >
                          No Data Available
                        </p>
                        <button
                          style={{
                            backgroundColor: "#007bff",
                            color: "white",
                            padding: "8px 16px",
                            borderRadius: "4px",
                            border: "none",
                            cursor: "pointer",
                            fontSize: "14px",
                          }}
                        >
                          Create New Canvas
                        </button>
                      </div>
                    );
                  })()}
                </div>
              </div>
            ))}
        </div>

        {/* Add Canvas Page Button */}
        <div
          style={{
            textAlign: canvasPages.length === 0 ? "center" : "left",
            marginTop: canvasPages.length === 0 ? "100px" : "-20px",
            marginLeft: canvasPages.length === 0 ? "0" : "25%",
          }}
        >
          <button
            style={{
              backgroundColor: "#007bff",
              color: "white",
              padding: "8px 16px",
              borderRadius: "4px",
              border: "none",
              cursor: "pointer",
              fontSize: "14px",
            }}
            onClick={handleAddCanvasPage}
          >
            Add Canvas Page
          </button>
        </div>

        {/* Loader */}
        {isLoading && <Loader text={`${saveProgress.step}`} />}

        {/* Save Modal */}
        {showSaveModal && (
          <SaveModal
            isOpen={true}
            onClose={handleCloseModal}
            onSave={handleConfirmSave}
            title={`Save ${pageTitle}`}
            subtitle={`For ${breadcrumbText}`}
            editorName={localStorage.getItem("user_name") || "Unknown User"}
            comment={comment}
            onCommentChange={(e) => setComment(e.target.value)}
            isLoading={isLoading}
            showLoader={true}
          />
        )}

        {/* Snackbar */}
        {snackbar.open && (
          <Snackbar
            message={snackbar.message}
            type={snackbar.type}
            onClose={handleCloseSnackbar}
          />
        )}

        {/* Version History Popup */}
        {showVersionHistory && (
          <VersionHistoryPopup
            onClose={() => setShowVersionHistory(false)}
            procedurePartNumber={partnumber}
            procedureRevision={revision}
            versionHistories={versionHistories}
            mode={mode}
            versionId={versionId}
            breadcrumbText={breadcrumbText}
            revisionTreeNodes={allNodesData}
            nodeId={canvasData?.tree_node_id || id || ""}
            nodeIdField="canvas_tree_node_id"
            nodeHistoryMatchIds={
              Array.isArray(canvasVersionHistoryMatchIds) &&
              canvasVersionHistoryMatchIds.length > 0
                ? canvasVersionHistoryMatchIds
                : undefined
            }
            nodeHistoryLeafMediaOrdinal={
              canvasMediaOrdinalForNodeHistory != null &&
              Number.isFinite(Number(canvasMediaOrdinalForNodeHistory))
                ? Number(canvasMediaOrdinalForNodeHistory)
                : undefined
            }
            nodeHistoryLeafMediaPhraseRegex="Canvas"
            procedureRevisionIdForNodeHistory={revisionId}
            restrictNodeHistoryToProcedureRevision={
              allVersionHistories.length === 0
            }
            emptyMessage={
              mode === "add"
                ? "No version history available for new canvases"
                : "No version history available"
            }
            initialFilterType="versions_for_this_page"
            onViewDetails={(row) => {
              const aid = row.audit_trail_id || row.audit_trail_id_ref;
              if (!aid) return;
              const path = getVersionHistoryAuditPreviewPath(
                aid,
                row.action || row.action_to_show
              );
              if (!path) return;
              setShowVersionHistory(false);
              navigate(path);
            }}
            isRowViewActionDisabled={(row) =>
              !(row.audit_trail_id || row.audit_trail_id_ref)
            }
            exportCsvDisabled={versionHistoryLoading}
          />
        )}

        {/* Template Selection Modal */}
        {templateModalOpen && (
          <Popup
            title="Select Canvas Page Template"
            closeModal={() => {
              setTemplateModalOpen(false);
            }}
          >
            <AppTable
              usingInPopup={true}
              ref={tableContainerRef}
              columnsData={columnNames}
              showSearchInput={true}
              paginate={true}
              pagination={{
                limit: [
                  limit,
                  (newLimit) => handlePaginationChange(newLimit, offset),
                ],
                offset: [
                  offset,
                  (newOffset) => handlePaginationChange(limit, newOffset),
                ],
                pagination,
                maxVsbleButtons: 7,
                justifyContent: "center",
                paginationPosition: {
                  position: "center",
                },
                paginationShadow: true,
              }}
              onFilterSortHandler={handleFilterSort}
              reduxSlice={reduxSlice}
              enableLocalStoragePersistence={true}
            >
              {loading ? (
                <tr>
                  <td
                    colSpan="4"
                    style={{ textAlign: "center", padding: "20px" }}
                  >
                    Loading...
                  </td>
                </tr>
              ) : templates.length > 0 ? (
                templates.map((template, index) => {
                  return (
                    <tr
                      key={template.canvas_page_template_id}
                      style={{ cursor: "pointer" }}
                    >
                      <td>{template.canvas_page_template_name || ""}</td>
                      <td>{template.canvas_page_template_description}</td>
                      <td>{template.category_details?.category_name || ""}</td>
                      <td>
                        <div className="app-action-btns">
                          <button
                            className="select-arrow"
                            onClick={async (e) => {
                              e.stopPropagation();

                              try {
                                // Get full template data
                                const result = await dispatch(
                                  getCanvasPageTemplateById(
                                    template.canvas_page_template_id,
                                  ),
                                );

                                if (result.meta.requestStatus !== "fulfilled") {
                                  setTemplateModalOpen(false); // Close modal on error
                                  showSnackbar(
                                    "Failed to load template",
                                    "error",
                                  );
                                  return;
                                }

                                const templateData = result.payload;
                                const selectedPage =
                                  templateData.results?.pages?.[0];

                                if (!selectedPage) {
                                  setTemplateModalOpen(false); // Close modal on error
                                  showSnackbar(
                                    "Template has no pages",
                                    "error",
                                  );
                                  return;
                                }

                                // Show loading
                                setTemplateModalOpen(false);
                                setTemplateProcessing(true);

                                // Check if canva_snap_url exists in template - prefer it over generation
                                // Check both template level and page level for canva_snap_url
                                let templateSnapshot;
                                const templateLevelSnapUrl =
                                  templateData.results?.canva_snap_url;
                                const pageLevelSnapUrl =
                                  selectedPage.canva_snap_url;

                                if (templateLevelSnapUrl || pageLevelSnapUrl) {
                                  // Use the existing snapshot URL from template (prefer page level over template level)
                                  templateSnapshot =
                                    pageLevelSnapUrl || templateLevelSnapUrl;
                                } else {
                                  // Generate snapshot from template page data
                                  try {
                                    templateSnapshot =
                                      await generateSnapshotFromPageDataHTML(
                                        selectedPage,
                                      );
                                  } catch (error) {
                                    console.error(
                                      "Failed to generate snapshot from template:",
                                      error,
                                    );
                                    templateSnapshot = ""; // Fallback to empty string
                                  }
                                }

                                // Create new page
                                const currentPageId =
                                  pendingNewPageId || uuidv4();

                                // Save template elements to IndexedDB FIRST, then add to state
                                try {
                                  const db = await openCanvasDB();
                                  if (
                                    db.objectStoreNames.contains(
                                      "canvasElements",
                                    )
                                  ) {
                                    const isOffCanvasElement = (element = {}) => {
                                      const shapeInfo = element?.shape_info || {};
                                      const shapeProps =
                                        element?.shape_properties || {};
                                      const imageProps =
                                        element?.image_properties || {};
                                      return (
                                        element?.isOffCanvas === true ||
                                        element?.is_off_canvas === true ||
                                        element?.isOffcanvas === true ||
                                        shapeInfo?.isOffCanvas === true ||
                                        shapeInfo?.is_off_canvas === true ||
                                        shapeInfo?.isOffcanvas === true ||
                                        shapeProps?.isOffCanvas === true ||
                                        shapeProps?.is_off_canvas === true ||
                                        shapeProps?.isOffcanvas === true ||
                                        imageProps?.isOffCanvas === true ||
                                        imageProps?.is_off_canvas === true ||
                                        imageProps?.isOffcanvas === true
                                      );
                                    };

                                    const transaction = db.transaction(
                                      ["canvasElements", "offCanvasElements"],
                                      "readwrite",
                                    );
                                    const store =
                                      transaction.objectStore("canvasElements");
                                    const offCanvasStore = transaction.objectStore(
                                      "offCanvasElements",
                                    );

                                    // Transform template elements to the format MyCanvas expects
                                    // Get template elements from selected page
                                    const templateElements =
                                      selectedPage.elements ||
                                      selectedPage.canvas_elements ||
                                      {};

                                    // Transform template images to match MyCanvas expected structure
                                    const transformImage = (img) => {
                                      const imgProps =
                                        img.image_properties || {};
                                      const shapeInfo = img.shape_info || {};

                                      // Combine properties
                                      const combinedProps = {
                                        ...imgProps,
                                        ...shapeInfo,
                                      };
                                      const resolvedIsOnTopLayer =
                                        combinedProps.isOnTopLayer ??
                                        img.isOnTopLayer ??
                                        imgProps.isOnTopLayer ??
                                        shapeInfo.isOnTopLayer ??
                                        false;

                                      // Get the image URL - prefer image_path, then src, then shape_info.src
                                      const imageUrl =
                                        img.image_path ||
                                        img.src ||
                                        shapeInfo.src ||
                                        imgProps.src;

                                      return {
                                        id:
                                          img.image_id ||
                                          img.id ||
                                          combinedProps.id ||
                                          uuidv4(),
                                        image_id:
                                          img.image_id ||
                                          img.id ||
                                          combinedProps.id ||
                                          uuidv4(),
                                        src: imageUrl,
                                        image_path: imageUrl,
                                        // For MyCanvas to load the image, it needs imageDataUrl pointing to the S3 URL
                                        imageDataUrl: imageUrl,
                                        originalImageDataUrl: imageUrl,
                                        // Position and size from image_properties or shape_info
                                        x: combinedProps.x || 0,
                                        y: combinedProps.y || 0,
                                        width: combinedProps.width || 100,
                                        height: combinedProps.height || 100,
                                        // Transform properties
                                        scaleX: combinedProps.scaleX || 1,
                                        scaleY: combinedProps.scaleY || 1,
                                        rotation: combinedProps.rotation || 0,
                                        // Flip properties
                                        flipHorizontal:
                                          combinedProps.flipHorizontal || false,
                                        flipVertical:
                                          combinedProps.flipVertical || false,
                                        // Crop properties
                                        cropData:
                                          combinedProps.cropData || null,
                                        originalCropRotation:
                                          combinedProps.originalCropRotation ||
                                          0,
                                        // Image properties
                                        maintainAspectRatio:
                                          combinedProps.maintainAspectRatio !==
                                          false,
                                        compress:
                                          combinedProps.compress !== false,
                                        hasBakedTransforms: false,
                                        originalWidth:
                                          combinedProps.originalWidth ||
                                          combinedProps.width ||
                                          100,
                                        originalHeight:
                                          combinedProps.originalHeight ||
                                          combinedProps.height ||
                                          100,
                                        // Preserve name and referenceUrl from template
                                        name:
                                          combinedProps.name ||
                                          img.name ||
                                          "Picture 1",
                                        referenceUrl:
                                          combinedProps.referenceUrl ||
                                          img.referenceUrl ||
                                          img.image_properties?.referenceUrl ||
                                          "",
                                        imageNumber:
                                          combinedProps.imageNumber ||
                                          img.imageNumber ||
                                          1,
                                        // Mark as from template
                                        fromCanvasTemplate: true,
                                        isFromTemplate: true, // Also set isFromTemplate for compatibility
                                        isOnTopLayer: resolvedIsOnTopLayer,
                                        // Shape info for MyCanvas
                                        shape_info: {
                                          id:
                                            img.image_id ||
                                            img.id ||
                                            combinedProps.id,
                                          src: imageUrl,
                                          x: combinedProps.x || 0,
                                          y: combinedProps.y || 0,
                                          width: combinedProps.width || 100,
                                          height: combinedProps.height || 100,
                                          scaleX: combinedProps.scaleX || 1,
                                          scaleY: combinedProps.scaleY || 1,
                                          rotation: combinedProps.rotation || 0,
                                          cropData:
                                            combinedProps.cropData || null,
                                          imageDataUrl: imageUrl,
                                          flipHorizontal:
                                            combinedProps.flipHorizontal ||
                                            false,
                                          flipVertical:
                                            combinedProps.flipVertical || false,
                                          name:
                                            combinedProps.name ||
                                            img.name ||
                                            "Picture 1",
                                          referenceUrl:
                                            combinedProps.referenceUrl ||
                                            img.referenceUrl ||
                                            img.image_properties
                                              ?.referenceUrl ||
                                            "",
                                          isOnTopLayer: resolvedIsOnTopLayer,
                                        },
                                        // Image properties for MyCanvas
                                        image_properties: {
                                          id:
                                            img.image_id ||
                                            img.id ||
                                            combinedProps.id,
                                          src: imageUrl,
                                          x: combinedProps.x || 0,
                                          y: combinedProps.y || 0,
                                          width: combinedProps.width || 100,
                                          height: combinedProps.height || 100,
                                          scaleX: combinedProps.scaleX || 1,
                                          scaleY: combinedProps.scaleY || 1,
                                          rotation: combinedProps.rotation || 0,
                                          cropData:
                                            combinedProps.cropData || null,
                                          imageDataUrl: imageUrl,
                                          flipHorizontal:
                                            combinedProps.flipHorizontal ||
                                            false,
                                          flipVertical:
                                            combinedProps.flipVertical || false,
                                          name:
                                            combinedProps.name ||
                                            img.name ||
                                            "Picture 1",
                                          referenceUrl:
                                            combinedProps.referenceUrl ||
                                            img.referenceUrl ||
                                            img.image_properties
                                              ?.referenceUrl ||
                                            "",
                                          imageNumber:
                                            combinedProps.imageNumber ||
                                            img.imageNumber ||
                                            1,
                                          isOnTopLayer: resolvedIsOnTopLayer,
                                        },
                                        // Preserve original template data
                                        ...img,
                                        isOnTopLayer: resolvedIsOnTopLayer,
                                      };
                                    };

                                    // Process shapes if they exist in the template page
                                    let processedElements = {};

                                    // Check if we have shapes in the template page
                                    if (
                                      selectedPage.shapes &&
                                      selectedPage.shapes.length > 0
                                    ) {
                                      // Transform shapes to match expected format
                                      const transformShape = (shape) => {
                                        const props =
                                          shape.shape_properties || {};
                                        const typeMapping = {
                                          rectangle: "Rect",
                                          circle: "Circle",
                                          arrow: "Arrow",
                                          double_sided_arrow:
                                            "DoubleSidedArrow",
                                          lines: "Line",
                                          text: "Text",
                                          image: "Image",
                                          scribble: "Scribble",
                                        };
                                        const componentType =
                                          typeMapping[shape.type] || shape.type;

                                        // Convert stroke color
                                        let strokeColor =
                                          props.stroke ||
                                          props.strokeColor ||
                                          "#000000";
                                        if (typeof strokeColor === "number") {
                                          strokeColor =
                                            "#" +
                                            strokeColor
                                              .toString(16)
                                              .padStart(6, "0");
                                        }

                                        // Convert fill color
                                        let fillColor =
                                          props.fillColor || "#ffffff";
                                        if (typeof fillColor === "number") {
                                          fillColor =
                                            "#" +
                                            fillColor
                                              .toString(16)
                                              .padStart(6, "0");
                                        }

                                        return {
                                          id: shape.shape_id,
                                          type: componentType,
                                          fromCanvasTemplate: true, // Mark as from template
                                          isFromTemplate: true, // Also set isFromTemplate for compatibility
                                          shape_info: {
                                            ...props,
                                            x: props.x || 0,
                                            y: props.y || 0,
                                            width: props.width || 100,
                                            height: props.height || 100,
                                            fillColor: fillColor,
                                            strokeColor: strokeColor,
                                            strokeWidth: props.strokeWidth || 2,
                                            points: props.points || [],
                                            radius: props.radius || 50,
                                            stroke: strokeColor,
                                          },
                                          ...props,
                                        };
                                      };

                                      // Process each shape type using normalizeShapeType
                                      if (
                                        selectedPage.shapes.filter(
                                          (s) =>
                                            normalizeShapeType(s.type) ===
                                            "Rect",
                                        ).length > 0
                                      ) {
                                        processedElements.rectangles =
                                          selectedPage.shapes
                                            .filter(
                                              (s) =>
                                                normalizeShapeType(s.type) ===
                                                "Rect",
                                            )
                                            .map(transformShape);
                                      }

                                      if (
                                        selectedPage.shapes.filter(
                                          (s) =>
                                            normalizeShapeType(s.type) ===
                                            "Circle",
                                        ).length > 0
                                      ) {
                                        processedElements.circles =
                                          selectedPage.shapes
                                            .filter(
                                              (s) =>
                                                normalizeShapeType(s.type) ===
                                                "Circle",
                                            )
                                            .map(transformShape);
                                      }

                                      if (
                                        selectedPage.shapes.filter(
                                          (s) =>
                                            normalizeShapeType(s.type) ===
                                            "Arrow",
                                        ).length > 0
                                      ) {
                                        // Convert arrows to unified line format with hasEndArrow: true (1 pointer)
                                        processedElements.lines = (
                                          processedElements.lines || []
                                        ).concat(
                                          selectedPage.shapes
                                            .filter(
                                              (s) =>
                                                normalizeShapeType(s.type) ===
                                                "Arrow",
                                            )
                                            .map((shape) => {
                                              const transformed =
                                                transformShape(shape);
                                              // Set arrow flags for unified rendering
                                              transformed.hasStartArrow = false;
                                              transformed.hasEndArrow = true;
                                              return transformed;
                                            }),
                                        );
                                      }

                                      if (
                                        selectedPage.shapes.filter(
                                          (s) =>
                                            normalizeShapeType(s.type) ===
                                            "DoubleSidedArrow",
                                        ).length > 0
                                      ) {
                                        // Convert double-sided arrows to unified line format with both arrows (2 pointers)
                                        processedElements.lines = (
                                          processedElements.lines || []
                                        ).concat(
                                          selectedPage.shapes
                                            .filter(
                                              (s) =>
                                                normalizeShapeType(s.type) ===
                                                "DoubleSidedArrow",
                                            )
                                            .map((shape) => {
                                              const transformed =
                                                transformShape(shape);
                                              // Set arrow flags for unified rendering
                                              transformed.hasStartArrow = true;
                                              transformed.hasEndArrow = true;
                                              return transformed;
                                            }),
                                        );
                                      }

                                      if (
                                        selectedPage.shapes.filter(
                                          (s) =>
                                            normalizeShapeType(s.type) ===
                                            "Line",
                                        ).length > 0
                                      ) {
                                        processedElements.lines =
                                          selectedPage.shapes
                                            .filter(
                                              (s) =>
                                                normalizeShapeType(s.type) ===
                                                "Line",
                                            )
                                            .map(transformShape);
                                      }

                                      if (
                                        selectedPage.shapes.filter(
                                          (s) =>
                                            normalizeShapeType(s.type) ===
                                            "Text",
                                        ).length > 0
                                      ) {
                                        processedElements.texts =
                                          selectedPage.shapes
                                            .filter(
                                              (s) =>
                                                normalizeShapeType(s.type) ===
                                                "Text",
                                            )
                                            .map(transformShape);
                                      }

                                      if (
                                        selectedPage.shapes.filter(
                                          (s) =>
                                            normalizeShapeType(s.type) ===
                                            "Scribble",
                                        ).length > 0
                                      ) {
                                        processedElements.scribbles =
                                          selectedPage.shapes
                                            .filter(
                                              (s) =>
                                                normalizeShapeType(s.type) ===
                                                "Scribble",
                                            )
                                            .map(transformShape);
                                      }

                                      // Process images if they exist
                                      if (
                                        selectedPage.images &&
                                        selectedPage.images.length > 0
                                      ) {
                                        processedElements.images =
                                          selectedPage.images.map(
                                            transformImage,
                                          );
                                      }
                                    } else {
                                      // If no shapes, use elements directly
                                      processedElements = templateElements;

                                      // Still need to transform images if they exist in templateElements
                                      if (
                                        templateElements.images &&
                                        templateElements.images.length > 0
                                      ) {
                                        processedElements.images =
                                          templateElements.images.map(
                                            transformImage,
                                          );
                                      }
                                    }

                                    // Extract canvas height from template layout_config if available
                                    const templateCanvasHeight =
                                      selectedPage.layout_config?.layout
                                        ?.page_height_vh ||
                                      templateData.results?.layout_config
                                        ?.layout?.page_height_vh;
                                    const canvasHeight = templateCanvasHeight
                                      ? parseInt(templateCanvasHeight)
                                      : 100;

                                    const splitElementsByCanvasZone = (
                                      elements = {},
                                    ) => {
                                      const split = {};
                                      const offSplit = {};
                                      const elementKeys = [
                                        "texts",
                                        "rectangles",
                                        "circles",
                                        "arrows",
                                        "scribbles",
                                        "lines",
                                        "doubleSidedArrows",
                                        "images",
                                        "videos",
                                      ];

                                      elementKeys.forEach((key) => {
                                        const items = Array.isArray(elements[key])
                                          ? elements[key]
                                          : [];
                                        split[key] = items.filter(
                                          (item) => !isOffCanvasElement(item),
                                        );
                                        offSplit[key] = items.filter((item) =>
                                          isOffCanvasElement(item),
                                        );
                                      });

                                      return {
                                        mainElements: split,
                                        offCanvasElements: offSplit,
                                      };
                                    };

                                    const { mainElements, offCanvasElements } =
                                      splitElementsByCanvasZone(processedElements);

                                    // Save template data with the proper structure { id, data: {...} }
                                    // This matches the format expected by MyCanvas.js
                                    const templateDataToSave = {
                                      id: currentPageId,
                                      data: {
                                        snapshot: templateSnapshot,
                                        canvasHeight: canvasHeight,
                                        ...mainElements, // Spread main-canvas elements only
                                      },
                                      lastModified: new Date().toISOString(),
                                      templateName:
                                        templateData.results
                                          ?.canvas_page_template_name ||
                                        "Unknown Template",
                                      fromCanvasTemplate: true,
                                    };

                                    await store.put(templateDataToSave);
                                    await offCanvasStore.put({
                                      id: currentPageId,
                                      data: offCanvasElements,
                                      lastModified: new Date().toISOString(),
                                      fromCanvasTemplate: true,
                                    });

                                    // NOW create the page object and add to state
                                    // This ensures IndexedDB has the data before hydration runs
                                    const newPage = {
                                      id: currentPageId,
                                      // CRITICAL: Don't set canvas_page_id for newly added pages
                                      // This is how we identify them as newly added vs API pages
                                      // canvas_page_id: undefined, // Explicitly undefined for newly added pages
                                      snapshot: templateSnapshot,
                                      canva_snap_url:
                                        pageLevelSnapUrl ||
                                        templateLevelSnapUrl ||
                                        null,
                                      zoomLevel: 100,
                                      elements: {
                                        texts: mainElements.texts || [],
                                        rectangles:
                                          mainElements.rectangles || [],
                                        circles: mainElements.circles || [],
                                        arrows: mainElements.arrows || [],
                                        scribbles:
                                          mainElements.scribbles || [],
                                        lines: mainElements.lines || [],
                                        doubleSidedArrows:
                                          mainElements.doubleSidedArrows || [],
                                        images: mainElements.images || [],
                                      },
                                      canvasHeight: canvasHeight, // Include canvas height from template
                                      shortDescription: "",
                                      lastModified: new Date().toISOString(),
                                      templateApplied: new Date().toISOString(),
                                      templateName:
                                        templateData.results
                                          ?.canvas_page_template_name ||
                                        "Unknown Template",
                                      newlyAdded: true, // Mark as newly added for tracking
                                      text_boxes: [],
                                      text_box_content_data: [],
                                    };

                                    // Add page to state
                                    setCanvasPages((prev) => {
                                      const updated = [...prev, newPage];

                                      // Immediately save to localStorage to preserve newly added page
                                      // This is critical in edit mode to prevent page loss
                                      if (mode === "edit") {
                                        const lightweightPages = updated.map(
                                          (p) => ({
                                            id: p?.id || p?.pageId,
                                            canvas_page_id: p?.canvas_page_id, // CRITICAL: Include canvas_page_id for API pages
                                            zoomLevel:
                                              typeof p?.zoomLevel === "number"
                                                ? p.zoomLevel
                                                : 100,
                                            lastModified:
                                              p?.lastModified ||
                                              new Date().toISOString(),
                                            canvasHeight: p?.canvasHeight,
                                            newlyAdded: p?.newlyAdded,
                                            shortDescription:
                                              p?.shortDescription,
                                            templateName: p?.templateName,
                                            canva_snap_url: p?.canva_snap_url,
                                            templateApplied: p?.templateApplied,
                                            text_boxes: Array.isArray(
                                              p?.text_boxes,
                                            )
                                              ? p.text_boxes
                                              : [],
                                            text_box_content_data:
                                              Array.isArray(
                                                p?.text_box_content_data,
                                              )
                                                ? p.text_box_content_data
                                                : [],
                                          }),
                                        );
                                        localStorage.setItem(
                                          "canvasPages",
                                          JSON.stringify(lightweightPages),
                                        );
                                      }

                                      return updated;
                                    });

                                    // Success - template applied
                                    setHasUnsavedChanges(true);

                                    // CRITICAL: Set persistent unsaved changes flag immediately when new page is added
                                    // This ensures the flag persists across MyCanvas visits and navigation
                                    if (id) {
                                      const persistentFlagKey = `canvasHasUnsavedChanges_${id}`;
                                      localStorage.setItem(
                                        persistentFlagKey,
                                        "true",
                                      );
                                    }

                                    setPendingNewPageId(null);
                                    setTemplateProcessing(false);
                                    setTemplateModalOpen(false); // Ensure modal is closed after successful template application
                                    showSnackbar(
                                      "Template applied successfully!",
                                      "success",
                                    );
                                  }
                                } catch (error) {
                                  console.error(
                                    "Error saving template elements to IndexedDB:",
                                    error,
                                  );
                                  setPendingNewPageId(null);
                                  setTemplateProcessing(false);
                                  setTemplateModalOpen(false); // Close modal on error
                                  showSnackbar(
                                    "Failed to apply template. Please try again.",
                                    "error",
                                  );
                                }
                              } catch (error) {
                                console.error("Template error:", error);
                                setPendingNewPageId(null);
                                setTemplateProcessing(false);
                                setTemplateModalOpen(false); // Close modal on error
                                showSnackbar(
                                  "Failed to apply template. Please try again.",
                                  "error",
                                );
                              }
                            }}
                          >
                            <FontAwesomeIcon icon={faArrowRight} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan="4"
                    style={{ textAlign: "center", padding: "20px" }}
                  >
                    No templates found
                  </td>
                </tr>
              )}
            </AppTable>
          </Popup>
        )}

        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    </>
  );
};

export default CanvasForm;
